import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, DollarSign, TrendingUp, QrCode, Plus, LogOut, Edit, MapPin, Save, Minus, ChevronDown, Ticket, Upload, X, Image, Settings, Power, AlertTriangle, Loader2, Trash2, Star, MessageSquare, Phone, Mail, Clock, Gift, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api, { getImageUrl } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import StaffManagement from '@/pages/admin/StaffManagement';
import StockReconciliation from '@/pages/admin/StockReconciliation';
import CouponManagement from '@/pages/admin/CouponManagement';
import CategoryTagManagement from '@/pages/admin/CategoryTagManagement';
import StaffSwitcher from '@/components/StaffSwitcher';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout, isManager, isAdmin, getAllowedOutlets, staffData } = useAuth();
  
  // Get allowed outlets for manager restriction
  const allowedOutlets = getAllowedOutlets();
  const isLocationRestricted = isManager();
  const staffRole = staffData?.role || 'admin'; // Default to admin for master login
  
  // State
  const [selectedOutlet, setSelectedOutlet] = useState(() => {
    // For managers, default to their first allowed outlet
    if (isLocationRestricted && allowedOutlets.length > 0) {
      return allowedOutlets[0];
    }
    return 'all';
  });
  const [timePeriod, setTimePeriod] = useState('today');
  const [analytics, setAnalytics] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [stockValue, setStockValue] = useState(0);
  const [criticalStock, setCriticalStock] = useState([]);
  const [products, setProducts] = useState([]);
  const [outlets, setOutlets] = useState([]);
  
  // Dialogs
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [showAddOutlet, setShowAddOutlet] = useState(false);
  const [showEditOutlet, setShowEditOutlet] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingOutlet, setEditingOutlet] = useState(null);
  
  // Delivery Fee Ranges state
  const [showDeliveryFees, setShowDeliveryFees] = useState(false);
  const [selectedOutletForFees, setSelectedOutletForFees] = useState(null);
  const [deliveryFeeRanges, setDeliveryFeeRanges] = useState([]);
  const [newFeeRange, setNewFeeRange] = useState({ min_distance_km: '', max_distance_km: '', fee: '' });
  const [editingFeeRange, setEditingFeeRange] = useState(null);
  
  // Categories and Tags state
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  
  // Banner/Hero settings state
  const [bannerSettings, setBannerSettings] = useState({
    hero_headline: 'Where Heat Meets The Sea',
    hero_tagline: 'Premium seafood grilled to perfection. Fast, fresh, and on fire.',
    hero_image: ''
  });
  const [savingBanner, setSavingBanner] = useState(false);
  const bannerFileInputRef = useRef(null);
  
  // WebSocket for ticket notifications
  const wsRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('adminTicketSound');
    return saved !== 'false';
  });
  const audioRef = useRef(null);
  
  // Payment settings state
  const [paymentSettings, setPaymentSettings] = useState({
    cod_enabled: true,
    upi_enabled: true,
    razorpay_enabled: true
  });
  const [savingPayment, setSavingPayment] = useState(false);
  
  // Loyalty management state
  const [customersWithLoyalty, setCustomersWithLoyalty] = useState([]);
  const [loyaltySearchTerm, setLoyaltySearchTerm] = useState('');
  const [loadingLoyalty, setLoadingLoyalty] = useState(false);
  const [showAdjustPoints, setShowAdjustPoints] = useState(false);
  const [selectedCustomerForPoints, setSelectedCustomerForPoints] = useState(null);
  const [pointsAdjustment, setPointsAdjustment] = useState({ amount: '', reason: '', type: 'add' });
  
  // Loyalty settings state
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [loadingLoyaltySettings, setLoadingLoyaltySettings] = useState(false);
  const [savingLoyaltySettings, setSavingLoyaltySettings] = useState(false);
  const [loyaltySubTab, setLoyaltySubTab] = useState('customers');
  
  // Contact settings state
  const [contactSettings, setContactSettings] = useState({
    contact_phone: '',
    contact_whatsapp: '',
    contact_email: '',
    operating_hours: '10 AM - 12 AM'
  });
  const [savingContact, setSavingContact] = useState(false);
  
  // Feedback and Tickets state
  const [feedbackList, setFeedbackList] = useState([]);
  const [ticketsList, setTicketsList] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketResponse, setTicketResponse] = useState('');
  
  // Company Settings for Invoice
  const [companySettings, setCompanySettings] = useState({
    company_name: 'Fish on Fire',
    company_address: '',
    company_phone: '',
    company_email: '',
    gst_number: '',
    fssai_number: '',
    pan_number: '',
    logo_url: '',
    invoice_footer: 'Thank you for your order!',
    invoice_terms: ''
  });
  const [savingCompanySettings, setSavingCompanySettings] = useState(false);
  
  // Invoice Settings
  const [invoiceSettings, setInvoiceSettings] = useState({
    paper_width: '80mm',
    show_logo: true,
    show_gst: true,
    show_fssai: true,
    show_customer_details: true,
    show_item_wise_gst: false,
    print_copies: 1,
    auto_print_pos: true,
    auto_print_online: false
  });
  const [savingInvoiceSettings, setSavingInvoiceSettings] = useState(false);
  
  // Reports state
  const [reportDateRange, setReportDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [salesReport, setSalesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [addonReport, setAddonReport] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('month'); // today, week, month, quarter, custom
  const [reportOutlets, setReportOutlets] = useState([]); // Selected outlets for reports (multi-select)
  
  // Refund history state
  const [refundHistory, setRefundHistory] = useState([]);
  const [refundStats, setRefundStats] = useState({ total: 0, count: 0 });
  const [loadingRefunds, setLoadingRefunds] = useState(false);
  
  // Stock forecast state
  const [stockForecasts, setStockForecasts] = useState([]);
  const [loadingForecasts, setLoadingForecasts] = useState(false);
  const [forecastDays, setForecastDays] = useState(7);

  // Kitchen prep time analytics state
  const [prepTimeAnalytics, setPrepTimeAnalytics] = useState(null);
  const [loadingPrepTimes, setLoadingPrepTimes] = useState(false);
  const [prepTimeDays, setPrepTimeDays] = useState(30);
  
  // Kitchen load state (Auto Prep Time Adjustment)
  const [kitchenLoad, setKitchenLoad] = useState(null);
  const [loadingKitchenLoad, setLoadingKitchenLoad] = useState(false);

  // Global Add-ons Management state
  const [globalAddons, setGlobalAddons] = useState([]);
  const [categoryAddonMatrix, setCategoryAddonMatrix] = useState({ categories: [], global_addons: [], mappings: {} });
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [showAddAddon, setShowAddAddon] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [newAddon, setNewAddon] = useState({ name: '', price: '0', is_active: true, ingredient_id: '' });
  const [savingAddon, setSavingAddon] = useState(false);
  const [inventory, setInventory] = useState([]); // For ingredient linking dropdown

  // Global Customizations Management state
  const [globalCustomizations, setGlobalCustomizations] = useState([]);
  const [categoryCustomizationMatrix, setCategoryCustomizationMatrix] = useState({ categories: [], global_customizations: [], mappings: {} });
  const [showAddCustomization, setShowAddCustomization] = useState(false);
  const [editingCustomization, setEditingCustomization] = useState(null);
  const [newCustomization, setNewCustomization] = useState({ name: '', required: false, is_active: true, options: [] });
  const [savingCustomization, setSavingCustomization] = useState(false);

  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image: '',
    stock: '100',
    tags: [],
    tax_enabled: true,
    tax_percentage: '5',
    packaging_fee: '0',
    is_addon_only: false
  });

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [editImagePreview, setEditImagePreview] = useState('');
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const [newOutlet, setNewOutlet] = useState({
    name: '',
    address: '',
    phone: '',
    latitude: '',
    longitude: '',
    delivery_radius_km: '5',
    dine_in_radius_km: '0.5',
    pickup_radius_km: '15',
    ordering_start_time: '09:00',
    ordering_end_time: '22:00',
    is_accepting_orders: true
  });

  // Stock update state
  const [stockUpdates, setStockUpdates] = useState({});

  // Offers state
  const [offers, setOffers] = useState([]);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [offerForm, setOfferForm] = useState({
    name: '',
    description: '',
    offer_type: 'combo_deal',
    condition: {
      condition_type: 'any_item',
      min_cart_value: '',
      required_product_ids: [],
      required_category: '',
      min_quantity: 1
    },
    reward: {
      reward_type: 'special_price_product',
      product_id: '',
      product_name: '',
      special_price: '',
      discount_percentage: '',
      discount_amount: '',
      max_discount: ''
    },
    start_date: '',
    end_date: '',
    is_active: true,
    outlet_ids: [],
    usage_limit: ''
  });

  // Bulk Edit state
  const [bulkEditData, setBulkEditData] = useState([]);
  const [bulkEditChanged, setBulkEditChanged] = useState(new Set());
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkImageUploading, setBulkImageUploading] = useState({});
  const [bulkSearchTerm, setBulkSearchTerm] = useState('');
  const [bulkCategoryFilter, setBulkCategoryFilter] = useState('all');
  const bulkFileInputRefs = useRef({});
  const bulkInputRefs = useRef({}); // For keyboard navigation

  // Filter bulk edit data based on search and category
  const filteredBulkEditData = bulkEditData.filter(product => {
    const matchesSearch = bulkSearchTerm === '' || 
      product.name?.toLowerCase().includes(bulkSearchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(bulkSearchTerm.toLowerCase());
    const matchesCategory = bulkCategoryFilter === 'all' || product.category === bulkCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Keyboard navigation for bulk edit table
  const handleBulkKeyDown = (e, productId, fieldIndex, rowIndex) => {
    const fields = ['name', 'price', 'stock', 'tax_percentage', 'packaging_fee'];
    const totalRows = filteredBulkEditData.length;
    const totalFields = fields.length;
    
    if (e.key === 'Tab') {
      e.preventDefault();
      let nextFieldIndex = fieldIndex;
      let nextRowIndex = rowIndex;
      
      if (e.shiftKey) {
        // Shift+Tab: Move backward
        nextFieldIndex--;
        if (nextFieldIndex < 0) {
          nextFieldIndex = totalFields - 1;
          nextRowIndex--;
        }
      } else {
        // Tab: Move forward
        nextFieldIndex++;
        if (nextFieldIndex >= totalFields) {
          nextFieldIndex = 0;
          nextRowIndex++;
        }
      }
      
      // Wrap around
      if (nextRowIndex < 0) nextRowIndex = totalRows - 1;
      if (nextRowIndex >= totalRows) nextRowIndex = 0;
      
      const nextProductId = filteredBulkEditData[nextRowIndex]?.id;
      const nextField = fields[nextFieldIndex];
      const nextRef = bulkInputRefs.current[`${nextProductId}-${nextField}`];
      if (nextRef) {
        nextRef.focus();
        if (nextRef.select) nextRef.select();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Enter: Move to same field in next row
      const nextRowIndex = (rowIndex + 1) % totalRows;
      const nextProductId = filteredBulkEditData[nextRowIndex]?.id;
      const field = fields[fieldIndex];
      const nextRef = bulkInputRefs.current[`${nextProductId}-${field}`];
      if (nextRef) {
        nextRef.focus();
        if (nextRef.select) nextRef.select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextRowIndex = (rowIndex + 1) % totalRows;
      const nextProductId = filteredBulkEditData[nextRowIndex]?.id;
      const field = fields[fieldIndex];
      const nextRef = bulkInputRefs.current[`${nextProductId}-${field}`];
      if (nextRef) nextRef.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const nextRowIndex = (rowIndex - 1 + totalRows) % totalRows;
      const nextProductId = filteredBulkEditData[nextRowIndex]?.id;
      const field = fields[fieldIndex];
      const nextRef = bulkInputRefs.current[`${nextProductId}-${field}`];
      if (nextRef) nextRef.focus();
    }
  };

  useEffect(() => {
    fetchData();
    fetchCategoriesAndTags();
  }, [selectedOutlet, timePeriod]);

  // WebSocket connection for ticket notifications
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    
    // Determine which outlet to subscribe to
    const outletId = isLocationRestricted && allowedOutlets.length > 0 
      ? allowedOutlets[0] 
      : (selectedOutlet !== 'all' ? selectedOutlet : 'all');
    
    const wsUrl = `${wsProtocol}//${wsHost}/ws/stock/${outletId}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('Admin WebSocket connected for ticket notifications');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_ticket') {
            // Play sound if enabled
            if (soundEnabled && audioRef.current) {
              audioRef.current.play().catch(e => console.log('Audio play prevented:', e));
            }
            
            // Show toast notification
            toast.info(`🎫 New Support Ticket`, {
              description: `${data.subject} - From ${data.customer_name}`,
              duration: 10000,
              action: {
                label: 'View Tickets',
                onClick: () => {
                  // Switch to tickets tab
                  const ticketsTab = document.querySelector('[value="tickets"]');
                  if (ticketsTab) ticketsTab.click();
                }
              }
            });
            
            // Show browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('New Support Ticket', {
                body: `${data.subject}\nFrom: ${data.customer_name}`,
                icon: '/logo.png',
                tag: 'new-ticket'
              });
            }
            
            // Refresh tickets list
            fetchTickets();
          }
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };
      
      ws.onerror = (error) => {
        console.error('Admin WebSocket error:', error);
      };
      
      ws.onclose = () => {
        console.log('Admin WebSocket disconnected');
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to connect Admin WebSocket:', error);
    }
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [selectedOutlet, allowedOutlets, isLocationRestricted, soundEnabled]);

  const fetchData = async () => {
    await Promise.all([
      fetchAnalytics(),
      fetchSalesData(),
      fetchStockValue(),
      fetchCriticalStock(),
      fetchProducts(),
      fetchOutlets(),
      fetchBannerSettings(),
      fetchPaymentSettings(),
      fetchFeedback(),
      fetchTickets(),
      fetchCompanySettings(),
      fetchInvoiceSettings(),
      fetchGlobalAddons(),
      fetchGlobalCustomizations(),
      fetchOffers()
    ]);
  };

  const fetchOffers = async () => {
    try {
      const response = await api.get('/offers');
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  const resetOfferForm = () => {
    setOfferForm({
      name: '',
      description: '',
      offer_type: 'combo_deal',
      condition: {
        condition_type: 'any_item',
        min_cart_value: '',
        required_product_ids: [],
        required_category: '',
        min_quantity: 1
      },
      reward: {
        reward_type: 'special_price_product',
        product_id: '',
        product_name: '',
        special_price: '',
        discount_percentage: '',
        discount_amount: '',
        max_discount: ''
      },
      start_date: '',
      end_date: '',
      is_active: true,
      outlet_ids: [],
      usage_limit: ''
    });
    setEditingOffer(null);
  };

  const openOfferDialog = (offer = null) => {
    if (offer) {
      setEditingOffer(offer);
      setOfferForm({
        name: offer.name || '',
        description: offer.description || '',
        offer_type: offer.offer_type || 'combo_deal',
        condition: {
          condition_type: offer.condition?.condition_type || 'any_item',
          min_cart_value: offer.condition?.min_cart_value || '',
          required_product_ids: offer.condition?.required_product_ids || [],
          required_category: offer.condition?.required_category || '',
          min_quantity: offer.condition?.min_quantity || 1
        },
        reward: {
          reward_type: offer.reward?.reward_type || 'special_price_product',
          product_id: offer.reward?.product_id || '',
          product_name: offer.reward?.product_name || '',
          special_price: offer.reward?.special_price || '',
          discount_percentage: offer.reward?.discount_percentage || '',
          discount_amount: offer.reward?.discount_amount || '',
          max_discount: offer.reward?.max_discount || ''
        },
        start_date: offer.start_date ? offer.start_date.split('T')[0] : '',
        end_date: offer.end_date ? offer.end_date.split('T')[0] : '',
        is_active: offer.is_active !== false,
        outlet_ids: offer.outlet_ids || [],
        usage_limit: offer.usage_limit || ''
      });
    } else {
      resetOfferForm();
    }
    setShowOfferDialog(true);
  };

  const saveOffer = async () => {
    if (!offerForm.name) {
      toast.error('Offer name is required');
      return;
    }

    // Build offer data
    const offerData = {
      name: offerForm.name,
      description: offerForm.description,
      offer_type: offerForm.offer_type,
      condition: {
        condition_type: offerForm.condition.condition_type,
        min_cart_value: offerForm.condition.min_cart_value ? parseFloat(offerForm.condition.min_cart_value) : null,
        required_product_ids: offerForm.condition.required_product_ids,
        required_category: offerForm.condition.required_category || null,
        min_quantity: parseInt(offerForm.condition.min_quantity) || 1
      },
      reward: {
        reward_type: offerForm.reward.reward_type,
        product_id: offerForm.reward.product_id || null,
        product_name: offerForm.reward.product_name || null,
        special_price: offerForm.reward.special_price ? parseFloat(offerForm.reward.special_price) : null,
        discount_percentage: offerForm.reward.discount_percentage ? parseFloat(offerForm.reward.discount_percentage) : null,
        discount_amount: offerForm.reward.discount_amount ? parseFloat(offerForm.reward.discount_amount) : null,
        max_discount: offerForm.reward.max_discount ? parseFloat(offerForm.reward.max_discount) : null
      },
      start_date: offerForm.start_date ? new Date(offerForm.start_date).toISOString() : null,
      end_date: offerForm.end_date ? new Date(offerForm.end_date).toISOString() : null,
      is_active: offerForm.is_active,
      outlet_ids: offerForm.outlet_ids,
      usage_limit: offerForm.usage_limit ? parseInt(offerForm.usage_limit) : null
    };

    try {
      if (editingOffer) {
        await api.put(`/offers/${editingOffer.id}`, offerData);
        toast.success('Offer updated');
      } else {
        await api.post('/offers', offerData);
        toast.success('Offer created');
      }
      fetchOffers();
      setShowOfferDialog(false);
      resetOfferForm();
    } catch (error) {
      console.error('Error saving offer:', error);
      toast.error('Failed to save offer');
    }
  };

  const deleteOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;
    try {
      await api.delete(`/offers/${offerId}`);
      toast.success('Offer deleted');
      fetchOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Failed to delete offer');
    }
  };

  const toggleOfferStatus = async (offer) => {
    try {
      const updatedOffer = { ...offer, is_active: !offer.is_active };
      await api.put(`/offers/${offer.id}`, updatedOffer);
      toast.success(`Offer ${updatedOffer.is_active ? 'activated' : 'deactivated'}`);
      fetchOffers();
    } catch (error) {
      console.error('Error toggling offer:', error);
      toast.error('Failed to update offer');
    }
  };

  const fetchCategoriesAndTags = async () => {
    try {
      const [catRes, tagRes] = await Promise.all([
        api.get('/categories'),
        api.get('/tags')
      ]);
      setCategories(catRes.data);
      setTags(tagRes.data);
      // Set default category for new product if not set
      if (!newProduct.category && catRes.data.length > 0) {
        setNewProduct(prev => ({ ...prev, category: catRes.data[0].name }));
      }
    } catch (error) {
      console.error('Error fetching categories/tags:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedOutlet !== 'all') params.append('outlet_id', selectedOutlet);
      const response = await api.get(`/analytics/dashboard?${params}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchSalesData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedOutlet !== 'all') params.append('outlet_id', selectedOutlet);
      params.append('period', timePeriod);
      
      const response = await api.get(`/analytics/sales?${params}`);
      setSalesData(response.data);
    } catch (error) {
      console.error('Error fetching sales data:', error);
    }
  };

  const fetchStockValue = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedOutlet !== 'all') params.append('outlet_id', selectedOutlet);
      const response = await api.get(`/analytics/stock-value?${params}`);
      setStockValue(response.data.total_stock_value);
    } catch (error) {
      console.error('Error fetching stock value:', error);
    }
  };

  // Fetch customers with loyalty points
  const fetchCustomersWithLoyalty = async () => {
    try {
      const response = await api.get('/admin/customers-loyalty');
      setCustomersWithLoyalty(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  // Adjust loyalty points for a customer
  const handleAdjustPoints = async () => {
    if (!selectedCustomerForPoints || !pointsAdjustment.amount) {
      toast.error('Please enter points amount');
      return;
    }
    
    try {
      await api.post('/admin/adjust-loyalty-points', {
        customer_phone: selectedCustomerForPoints.phone,
        points: parseInt(pointsAdjustment.amount),
        type: pointsAdjustment.type,
        reason: pointsAdjustment.reason || (pointsAdjustment.type === 'add' ? 'Admin bonus' : 'Admin adjustment')
      });
      
      toast.success(`${pointsAdjustment.type === 'add' ? 'Added' : 'Deducted'} ${pointsAdjustment.amount} points`);
      setShowAdjustPoints(false);
      setPointsAdjustment({ amount: '', reason: '', type: 'add' });
      setSelectedCustomerForPoints(null);
      fetchCustomersWithLoyalty();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust points');
    }
  };

  // Fetch loyalty settings for current outlet
  const fetchLoyaltySettings = async () => {
    setLoadingLoyaltySettings(true);
    try {
      const outletParam = selectedOutlet === 'all' ? 'global' : selectedOutlet;
      const response = await api.get(`/admin/loyalty-settings?outlet_id=${outletParam}`);
      setLoyaltySettings(response.data);
    } catch (error) {
      console.error('Error fetching loyalty settings:', error);
      // Set defaults if fetch fails
      setLoyaltySettings({
        outlet_id: selectedOutlet === 'all' ? 'global' : selectedOutlet,
        points_per_rupee: 1,
        rupees_per_point: 0.25,
        min_redeem_points: 100,
        welcome_bonus: 50,
        referral_bonus_referrer: 100,
        referral_bonus_referee: 50,
        points_expiry_days: 180,
        bronze_min_points: 0,
        bronze_multiplier: 1.0,
        silver_min_points: 500,
        silver_multiplier: 1.25,
        gold_min_points: 2000,
        gold_multiplier: 1.5
      });
    } finally {
      setLoadingLoyaltySettings(false);
    }
  };

  // Save loyalty settings
  const saveLoyaltySettings = async () => {
    if (!loyaltySettings) return;
    
    setSavingLoyaltySettings(true);
    try {
      const outletParam = selectedOutlet === 'all' ? 'global' : selectedOutlet;
      await api.put(`/admin/loyalty-settings?outlet_id=${outletParam}`, loyaltySettings);
      toast.success(`Loyalty settings saved for ${selectedOutlet === 'all' ? 'Global (Default)' : outlets.find(o => o.id === selectedOutlet)?.name || 'this outlet'}`);
    } catch (error) {
      console.error('Error saving loyalty settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save loyalty settings');
    } finally {
      setSavingLoyaltySettings(false);
    }
  };

  // Reset outlet-specific settings to global defaults
  const resetToGlobalSettings = async () => {
    if (selectedOutlet === 'all') {
      toast.error('Cannot reset global settings');
      return;
    }
    
    try {
      await api.delete(`/admin/loyalty-settings/${selectedOutlet}`);
      toast.success('Outlet settings reset to global defaults');
      fetchLoyaltySettings();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info('This outlet is already using global defaults');
      } else {
        toast.error('Failed to reset settings');
      }
    }
  };

  const fetchCriticalStock = async () => {
    try {
      const params = new URLSearchParams();
      params.append('threshold', '20');
      if (selectedOutlet !== 'all') params.append('outlet_id', selectedOutlet);
      const response = await api.get(`/analytics/critical-stock?${params}`);
      setCriticalStock(response.data.critical_items);
    } catch (error) {
      console.error('Error fetching critical stock:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      if (selectedOutlet !== 'all') {
        // Fetch location-specific inventory with addon-only products included for admin
        const response = await api.get(`/outlets/${selectedOutlet}/inventory?include_addon_only=true`);
        // Map outlet_stock to stock for display
        const productsWithStock = response.data.map(p => ({
          ...p,
          stock: p.outlet_stock || p.stock || 0
        }));
        setProducts(productsWithStock);
      } else {
        // Fetch aggregated inventory across all locations with addon-only products
        const response = await api.get('/inventory/aggregated?include_addon_only=true');
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      // Fallback to regular products with addon-only included for admin
      const response = await api.get('/products?include_addon_only=true');
      setProducts(response.data);
    }
  };

  // Bulk Edit Functions
  const fetchBulkEditData = async () => {
    setBulkEditLoading(true);
    try {
      const response = await api.get('/products?include_addon_only=true');
      setBulkEditData(response.data.map(p => ({
        ...p,
        _original: { ...p } // Store original for comparison
      })));
      setBulkEditChanged(new Set());
    } catch (error) {
      console.error('Error fetching products for bulk edit:', error);
      toast.error('Failed to load products');
    } finally {
      setBulkEditLoading(false);
    }
  };

  const handleBulkCellChange = (productId, field, value) => {
    setBulkEditData(prev => prev.map(p => {
      if (p.id === productId) {
        return { ...p, [field]: value };
      }
      return p;
    }));
    setBulkEditChanged(prev => new Set([...prev, productId]));
  };

  const handleBulkImageUpload = async (productId, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, WebP or GIF');
      return;
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setBulkImageUploading(prev => ({ ...prev, [productId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      handleBulkCellChange(productId, 'image', response.data.url);
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setBulkImageUploading(prev => ({ ...prev, [productId]: false }));
    }
  };

  const saveBulkChanges = async () => {
    if (bulkEditChanged.size === 0) {
      toast.info('No changes to save');
      return;
    }
    
    setBulkSaving(true);
    const changedProducts = bulkEditData.filter(p => bulkEditChanged.has(p.id));
    
    // Parallel save with Promise.allSettled for better performance
    const updatePromises = changedProducts.map(product => {
      const original = product._original || {};
      const updateData = {
        name: product.name?.trim() || original.name,
        description: product.description || original.description || '',
        price: Math.max(0, parseFloat(product.price) || original.price || 0),
        category: product.category || original.category,
        image: product.image || original.image || '',
        stock: Math.max(0, parseInt(product.stock) || original.stock || 0),
        tags: product.tags || original.tags || [],
        tax_enabled: product.tax_enabled ?? original.tax_enabled ?? true,
        tax_percentage: Math.max(0, parseFloat(product.tax_percentage) ?? original.tax_percentage ?? 5),
        packaging_fee: Math.max(0, parseFloat(product.packaging_fee) ?? original.packaging_fee ?? 0),
        is_addon_only: product.is_addon_only ?? original.is_addon_only ?? false
      };
      return api.put(`/products/${product.id}`, updateData)
        .then(() => ({ status: 'success', id: product.id }))
        .catch(err => ({ status: 'error', id: product.id, error: err }));
    });
    
    const results = await Promise.all(updatePromises);
    const successCount = results.filter(r => r.status === 'success').length;
    const errorCount = results.filter(r => r.status === 'error').length;
    
    setBulkSaving(false);
    
    if (successCount > 0) {
      toast.success(`Updated ${successCount} product${successCount > 1 ? 's' : ''}`);
      fetchBulkEditData();
      fetchProducts(); // Refresh main products list too
    }
    if (errorCount > 0) {
      toast.error(`Failed to update ${errorCount} product${errorCount > 1 ? 's' : ''}`);
    }
  };

  const discardBulkChanges = () => {
    setBulkEditData(prev => prev.map(p => ({
      ...p._original,
      _original: { ...p._original }
    })));
    setBulkEditChanged(new Set());
    toast.info('Changes discarded');
  };

  const fetchOutlets = async () => {
    try {
      const response = await api.get('/outlets');
      setOutlets(response.data);
    } catch (error) {
      console.error('Error fetching outlets:', error);
    }
  };

  // Delivery Fee Range Functions
  const fetchDeliveryFeeRanges = async (outletId) => {
    try {
      const response = await api.get(`/delivery-fee-ranges?outlet_id=${outletId}`);
      setDeliveryFeeRanges(response.data);
    } catch (error) {
      console.error('Error fetching delivery fee ranges:', error);
      setDeliveryFeeRanges([]);
    }
  };

  const handleAddFeeRange = async () => {
    if (!newFeeRange.min_distance_km || !newFeeRange.max_distance_km || !newFeeRange.fee) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await api.post('/delivery-fee-ranges', {
        outlet_id: selectedOutletForFees.id,
        min_distance_km: parseFloat(newFeeRange.min_distance_km),
        max_distance_km: parseFloat(newFeeRange.max_distance_km),
        fee: parseFloat(newFeeRange.fee)
      });
      toast.success('Delivery fee range added');
      setNewFeeRange({ min_distance_km: '', max_distance_km: '', fee: '' });
      fetchDeliveryFeeRanges(selectedOutletForFees.id);
    } catch (error) {
      console.error('Error adding fee range:', error);
      toast.error('Failed to add fee range');
    }
  };

  const handleUpdateFeeRange = async () => {
    if (!editingFeeRange) return;
    try {
      await api.put(`/delivery-fee-ranges/${editingFeeRange.id}`, {
        min_distance_km: parseFloat(editingFeeRange.min_distance_km),
        max_distance_km: parseFloat(editingFeeRange.max_distance_km),
        fee: parseFloat(editingFeeRange.fee)
      });
      toast.success('Delivery fee range updated');
      setEditingFeeRange(null);
      fetchDeliveryFeeRanges(selectedOutletForFees.id);
    } catch (error) {
      console.error('Error updating fee range:', error);
      toast.error('Failed to update fee range');
    }
  };

  const handleDeleteFeeRange = async (rangeId) => {
    if (!window.confirm('Delete this fee range?')) return;
    try {
      await api.delete(`/delivery-fee-ranges/${rangeId}`);
      toast.success('Fee range deleted');
      fetchDeliveryFeeRanges(selectedOutletForFees.id);
    } catch (error) {
      console.error('Error deleting fee range:', error);
      toast.error('Failed to delete fee range');
    }
  };

  const fetchBannerSettings = async () => {
    try {
      const response = await api.get('/site-settings');
      setBannerSettings({
        hero_headline: response.data.hero_headline || 'Where Heat Meets The Sea',
        hero_tagline: response.data.hero_tagline || 'Premium seafood grilled to perfection. Fast, fresh, and on fire.',
        hero_image: response.data.hero_image || ''
      });
      setContactSettings({
        contact_phone: response.data.contact_phone || '',
        contact_whatsapp: response.data.contact_whatsapp || '',
        contact_email: response.data.contact_email || '',
        operating_hours: response.data.operating_hours || '10 AM - 12 AM'
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      await api.put('/site-settings', contactSettings);
      toast.success('Contact settings saved successfully');
    } catch (error) {
      console.error('Error saving contact settings:', error);
      toast.error('Failed to save contact settings');
    } finally {
      setSavingContact(false);
    }
  };

  // Company & Invoice Settings Functions
  const fetchCompanySettings = async () => {
    try {
      const response = await api.get('/settings/company');
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  const handleSaveCompanySettings = async () => {
    setSavingCompanySettings(true);
    try {
      await api.put('/settings/company', companySettings);
      toast.success('Company settings saved successfully');
    } catch (error) {
      console.error('Error saving company settings:', error);
      toast.error('Failed to save company settings');
    } finally {
      setSavingCompanySettings(false);
    }
  };

  const fetchInvoiceSettings = async () => {
    try {
      const response = await api.get('/settings/invoice');
      setInvoiceSettings(response.data);
    } catch (error) {
      console.error('Error fetching invoice settings:', error);
    }
  };

  const handleSaveInvoiceSettings = async () => {
    setSavingInvoiceSettings(true);
    try {
      await api.put('/settings/invoice', invoiceSettings);
      toast.success('Invoice settings saved successfully');
    } catch (error) {
      console.error('Error saving invoice settings:', error);
      toast.error('Failed to save invoice settings');
    } finally {
      setSavingInvoiceSettings(false);
    }
  };

  // Reports Functions
  const fetchSalesReport = async () => {
    setLoadingReports(true);
    try {
      const params = new URLSearchParams({
        start_date: reportDateRange.start,
        end_date: reportDateRange.end
      });
      // Support multi-outlet selection
      if (reportOutlets.length > 0) {
        params.append('outlet_ids', reportOutlets.join(','));
      } else if (selectedOutlet !== 'all') {
        params.append('outlet_id', selectedOutlet);
      }
      const response = await api.get(`/reports/sales?${params}`);
      setSalesReport(response.data);
    } catch (error) {
      console.error('Error fetching sales report:', error);
      toast.error('Failed to load sales report');
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchInventoryReport = async () => {
    try {
      const params = new URLSearchParams({
        start_date: reportDateRange.start,
        end_date: reportDateRange.end
      });
      // Support multi-outlet selection
      if (reportOutlets.length > 0) {
        params.append('outlet_ids', reportOutlets.join(','));
      } else if (selectedOutlet !== 'all') {
        params.append('outlet_id', selectedOutlet);
      }
      const response = await api.get(`/reports/inventory?${params}`);
      setInventoryReport(response.data);
    } catch (error) {
      console.error('Error fetching inventory report:', error);
    }
  };

  const fetchAddonReport = async () => {
    try {
      const params = new URLSearchParams({
        start_date: reportDateRange.start,
        end_date: reportDateRange.end
      });
      if (reportOutlets.length > 0) {
        params.append('outlet_id', reportOutlets[0]);
      } else if (selectedOutlet !== 'all') {
        params.append('outlet_id', selectedOutlet);
      }
      const response = await api.get(`/reports/addon-sales?${params}`);
      setAddonReport(response.data);
    } catch (error) {
      console.error('Error fetching addon report:', error);
    }
  };

  const handlePeriodChange = (period) => {
    setReportPeriod(period);
    const today = new Date();
    let start, end = today.toISOString().split('T')[0];
    
    switch (period) {
      case 'today':
        start = end;
        break;
      case 'week':
        start = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
        break;
      case 'month':
        start = new Date(today.setMonth(today.getMonth() - 1)).toISOString().split('T')[0];
        break;
      case 'quarter':
        start = new Date(today.setMonth(today.getMonth() - 3)).toISOString().split('T')[0];
        break;
      default:
        return;
    }
    setReportDateRange({ start, end: new Date().toISOString().split('T')[0] });
  };

  const downloadExcelReport = async (reportType) => {
    try {
      const params = new URLSearchParams({
        report_type: reportType,
        start_date: reportDateRange.start,
        end_date: reportDateRange.end
      });
      if (selectedOutlet !== 'all') {
        params.append('outlet_id', selectedOutlet);
      }
      
      const response = await api.get(`/reports/download-excel?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report_${reportDateRange.start}_to_${reportDateRange.end}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    }
  };

  const fetchFeedback = async () => {
    try {
      const response = await api.get('/feedback');
      setFeedbackList(response.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      // For non-admin staff, filter by their allowed outlets
      let url = '/tickets';
      if (isLocationRestricted && allowedOutlets.length > 0) {
        // Staff can only see tickets for their assigned outlets
        url = `/tickets?outlet_id=${allowedOutlets[0]}`;
      } else if (selectedOutlet !== 'all') {
        // Admin viewing specific outlet
        url = `/tickets?outlet_id=${selectedOutlet}`;
      }
      const response = await api.get(url);
      setTicketsList(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  // Fetch refund history
  const fetchRefundHistory = async () => {
    setLoadingRefunds(true);
    try {
      const outletParam = selectedOutlet !== 'all' ? `?outlet_id=${selectedOutlet}` : '';
      const response = await api.get(`/analytics/refund-history${outletParam}`);
      setRefundHistory(response.data.refunds || []);
      setRefundStats({
        total: response.data.total_refund_amount || 0,
        count: response.data.total_count || 0
      });
    } catch (error) {
      console.error('Error fetching refund history:', error);
    } finally {
      setLoadingRefunds(false);
    }
  };

  // Fetch stock forecasts
  const fetchStockForecasts = async () => {
    setLoadingForecasts(true);
    try {
      const outletParam = selectedOutlet !== 'all' ? `outlet_id=${selectedOutlet}&` : '';
      const response = await api.get(`/analytics/stock-forecast?${outletParam}days=${forecastDays}`);
      setStockForecasts(response.data.forecasts || []);
    } catch (error) {
      console.error('Error fetching stock forecasts:', error);
    } finally {
      setLoadingForecasts(false);
    }
  };

  // Fetch kitchen prep time analytics
  const fetchPrepTimeAnalytics = async () => {
    setLoadingPrepTimes(true);
    try {
      const outletParam = selectedOutlet !== 'all' ? `outlet_id=${selectedOutlet}&` : '';
      const response = await api.get(`/analytics/kitchen-prep-times?${outletParam}days=${prepTimeDays}`);
      setPrepTimeAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching prep time analytics:', error);
    } finally {
      setLoadingPrepTimes(false);
    }
  };

  // Fetch kitchen load data (Auto Prep Time Adjustment)
  const fetchKitchenLoad = async () => {
    setLoadingKitchenLoad(true);
    try {
      const outletParam = selectedOutlet !== 'all' ? `outlet_id=${selectedOutlet}` : '';
      const response = await api.get(`/analytics/kitchen-load?${outletParam}`);
      setKitchenLoad(response.data);
    } catch (error) {
      console.error('Error fetching kitchen load:', error);
    } finally {
      setLoadingKitchenLoad(false);
    }
  };

  // Global Add-ons Management functions
  const fetchGlobalAddons = async () => {
    setLoadingAddons(true);
    try {
      const [addonsRes, matrixRes, inventoryRes] = await Promise.all([
        api.get('/global-addons'),
        api.get('/category-addon-matrix'),
        api.get('/inventory')
      ]);
      setGlobalAddons(addonsRes.data);
      setCategoryAddonMatrix(matrixRes.data);
      setInventory(inventoryRes.data);
    } catch (error) {
      console.error('Error fetching global addons:', error);
    } finally {
      setLoadingAddons(false);
    }
  };

  const handleCreateAddon = async () => {
    if (!newAddon.name.trim()) {
      toast.error('Please enter add-on name');
      return;
    }
    setSavingAddon(true);
    try {
      await api.post('/global-addons', {
        name: newAddon.name.trim(),
        price: parseFloat(newAddon.price) || 0,
        is_active: newAddon.is_active,
        ingredient_id: newAddon.ingredient_id || null
      });
      toast.success('Add-on created successfully');
      setShowAddAddon(false);
      setNewAddon({ name: '', price: '0', is_active: true, ingredient_id: '' });
      fetchGlobalAddons();
    } catch (error) {
      console.error('Error creating add-on:', error);
      toast.error(error.response?.data?.detail || 'Failed to create add-on');
    } finally {
      setSavingAddon(false);
    }
  };

  const handleUpdateAddon = async () => {
    if (!editingAddon || !editingAddon.name.trim()) {
      toast.error('Please enter add-on name');
      return;
    }
    setSavingAddon(true);
    try {
      await api.put(`/global-addons/${editingAddon.id}`, {
        name: editingAddon.name.trim(),
        price: parseFloat(editingAddon.price) || 0,
        is_active: editingAddon.is_active,
        ingredient_id: editingAddon.ingredient_id || null
      });
      toast.success('Add-on updated successfully');
      setEditingAddon(null);
      fetchGlobalAddons();
    } catch (error) {
      console.error('Error updating add-on:', error);
      toast.error('Failed to update add-on');
    } finally {
      setSavingAddon(false);
    }
  };

  const handleToggleAddon = async (addonId) => {
    try {
      const response = await api.put(`/global-addons/${addonId}/toggle`);
      toast.success(response.data.message);
      fetchGlobalAddons();
    } catch (error) {
      console.error('Error toggling add-on:', error);
      toast.error('Failed to toggle add-on');
    }
  };

  const handleDeleteAddon = async (addonId) => {
    if (!window.confirm('Are you sure you want to delete this add-on? It will be removed from all categories and products.')) {
      return;
    }
    try {
      await api.delete(`/global-addons/${addonId}`);
      toast.success('Add-on deleted successfully');
      fetchGlobalAddons();
    } catch (error) {
      console.error('Error deleting add-on:', error);
      toast.error('Failed to delete add-on');
    }
  };

  const handleUpdateCategoryAddonMatrix = async (newMappings) => {
    try {
      await api.put('/category-addon-matrix', newMappings);
      toast.success('Category mappings updated');
      fetchGlobalAddons();
      fetchProducts(); // Refresh products to update effective_addons
    } catch (error) {
      console.error('Error updating category mappings:', error);
      toast.error('Failed to update category mappings');
    }
  };

  const handleToggleProductAddon = async (productId, addonId) => {
    try {
      const response = await api.put(`/products/${productId}/toggle-addon/${addonId}`);
      toast.success(`Add-on ${response.data.action} for this product`);
      fetchProducts();
    } catch (error) {
      console.error('Error toggling product add-on:', error);
      toast.error('Failed to toggle add-on');
    }
  };

  // Global Customizations Management functions
  const fetchGlobalCustomizations = async () => {
    try {
      const [customizationsRes, matrixRes] = await Promise.all([
        api.get('/global-customizations'),
        api.get('/category-customization-matrix')
      ]);
      setGlobalCustomizations(customizationsRes.data);
      setCategoryCustomizationMatrix(matrixRes.data);
    } catch (error) {
      console.error('Error fetching global customizations:', error);
    }
  };

  const handleCreateCustomization = async () => {
    if (!newCustomization.name.trim()) {
      toast.error('Please enter customization group name');
      return;
    }
    if (newCustomization.options.length === 0) {
      toast.error('Please add at least one option');
      return;
    }
    setSavingCustomization(true);
    try {
      await api.post('/global-customizations', {
        name: newCustomization.name.trim(),
        required: newCustomization.required,
        is_active: newCustomization.is_active,
        options: newCustomization.options
      });
      toast.success('Customization group created successfully');
      setShowAddCustomization(false);
      setNewCustomization({ name: '', required: false, is_active: true, options: [] });
      fetchGlobalCustomizations();
    } catch (error) {
      console.error('Error creating customization:', error);
      toast.error(error.response?.data?.detail || 'Failed to create customization');
    } finally {
      setSavingCustomization(false);
    }
  };

  const handleUpdateCustomization = async () => {
    if (!editingCustomization || !editingCustomization.name.trim()) {
      toast.error('Please enter customization group name');
      return;
    }
    setSavingCustomization(true);
    try {
      await api.put(`/global-customizations/${editingCustomization.id}`, {
        name: editingCustomization.name.trim(),
        required: editingCustomization.required,
        is_active: editingCustomization.is_active,
        options: editingCustomization.options
      });
      toast.success('Customization group updated successfully');
      setEditingCustomization(null);
      fetchGlobalCustomizations();
    } catch (error) {
      console.error('Error updating customization:', error);
      toast.error('Failed to update customization');
    } finally {
      setSavingCustomization(false);
    }
  };

  const handleToggleCustomization = async (groupId) => {
    try {
      const response = await api.put(`/global-customizations/${groupId}/toggle`);
      toast.success(response.data.message);
      fetchGlobalCustomizations();
    } catch (error) {
      console.error('Error toggling customization:', error);
      toast.error('Failed to toggle customization');
    }
  };

  const handleToggleCustomizationOption = async (groupId, optionId) => {
    try {
      await api.put(`/global-customizations/${groupId}/options/${optionId}/toggle`);
      toast.success('Option toggled');
      fetchGlobalCustomizations();
    } catch (error) {
      console.error('Error toggling option:', error);
      toast.error('Failed to toggle option');
    }
  };

  const handleDeleteCustomization = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this customization group?')) {
      return;
    }
    try {
      await api.delete(`/global-customizations/${groupId}`);
      toast.success('Customization group deleted');
      fetchGlobalCustomizations();
    } catch (error) {
      console.error('Error deleting customization:', error);
      toast.error('Failed to delete customization');
    }
  };

  const handleUpdateCategoryCustomizationMatrix = async (newMappings) => {
    try {
      await api.put('/category-customization-matrix', newMappings);
      toast.success('Category customization mappings updated');
      fetchGlobalCustomizations();
      fetchProducts();
    } catch (error) {
      console.error('Error updating category mappings:', error);
      toast.error('Failed to update category mappings');
    }
  };

  const handleUpdateTicket = async (ticketId, status, response) => {
    try {
      await api.put(`/tickets/${ticketId}?status=${status}&admin_response=${encodeURIComponent(response)}`);
      toast.success('Ticket updated successfully');
      fetchTickets();
      setSelectedTicket(null);
      setTicketResponse('');
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error('Failed to update ticket');
    }
  };

  const handleSaveBanner = async () => {
    setSavingBanner(true);
    try {
      await api.put('/site-settings', bannerSettings);
      toast.success('Banner settings saved successfully');
    } catch (error) {
      console.error('Error saving banner settings:', error);
      toast.error('Failed to save banner settings');
    } finally {
      setSavingBanner(false);
    }
  };

  const handleBannerImageUpload = async (file) => {
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WebP');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setBannerSettings(prev => ({ ...prev, hero_image: response.data.url }));
        toast.success('Banner image uploaded');
      }
    } catch (error) {
      console.error('Error uploading banner image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await api.get('/payment-settings');
      setPaymentSettings(response.data);
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const handleSavePaymentSettings = async () => {
    setSavingPayment(true);
    try {
      await api.put('/payment-settings', paymentSettings);
      toast.success('Payment settings saved successfully');
    } catch (error) {
      console.error('Error saving payment settings:', error);
      toast.error('Failed to save payment settings');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleAddProduct = async () => {
    try {
      await api.post('/products', {
        ...newProduct,
        price: parseFloat(newProduct.price),
        stock: parseInt(newProduct.stock),
        tags: newProduct.tags || [],
        addons: newProduct.addons || [],
        customization_groups: newProduct.customization_groups || [],
        tax_enabled: newProduct.tax_enabled,
        tax_percentage: parseFloat(newProduct.tax_percentage) || 5,
        packaging_fee: parseFloat(newProduct.packaging_fee) || 0,
        is_addon_only: newProduct.is_addon_only || false
      });
      toast.success('Product added successfully');
      setShowAddProduct(false);
      setNewProduct({
        name: '',
        description: '',
        price: '',
        category: categories.length > 0 ? categories[0].name : '',
        image: '',
        stock: '100',
        tags: [],
        addons: [],
        customization_groups: [],
        tax_enabled: true,
        tax_percentage: '5',
        packaging_fee: '0',
        is_addon_only: false
      });
      setImagePreview('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchProducts();
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error('Failed to add product');
    }
  };

  const handleEditProduct = async () => {
    try {
      const updates = {};
      if (editingProduct.name) updates.name = editingProduct.name;
      if (editingProduct.description) updates.description = editingProduct.description;
      if (editingProduct.price) updates.price = parseFloat(editingProduct.price);
      if (editingProduct.category) updates.category = editingProduct.category;
      if (editingProduct.image !== undefined) updates.image = editingProduct.image;
      if (editingProduct.stock !== undefined) updates.stock = parseInt(editingProduct.stock);
      if (editingProduct.available !== undefined) updates.available = editingProduct.available;
      if (editingProduct.tags !== undefined) updates.tags = editingProduct.tags;
      if (editingProduct.addons !== undefined) updates.addons = editingProduct.addons;
      if (editingProduct.customization_groups !== undefined) updates.customization_groups = editingProduct.customization_groups;
      if (editingProduct.tax_enabled !== undefined) updates.tax_enabled = editingProduct.tax_enabled;
      if (editingProduct.tax_percentage !== undefined) updates.tax_percentage = parseFloat(editingProduct.tax_percentage) || 5;
      if (editingProduct.packaging_fee !== undefined) updates.packaging_fee = parseFloat(editingProduct.packaging_fee) || 0;

      await api.put(`/products/${editingProduct.id}`, updates);
      toast.success('Product updated successfully');
      setShowEditProduct(false);
      setEditingProduct(null);
      setEditImagePreview('');
      if (editFileInputRef.current) {
        editFileInputRef.current.value = '';
      }
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product');
    }
  };

  const openEditProduct = (product) => {
    setEditingProduct({
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image: product.image,
      stock: product.stock.toString(),
      available: product.available,
      tags: product.tags || [],
      addons: product.addons || [],
      customization_groups: product.customization_groups || [],
      tax_enabled: product.tax_enabled !== false,
      tax_percentage: product.tax_percentage || 5,
      packaging_fee: product.packaging_fee || 0,
      // Inherited from category - for toggle controls
      category_addon_status: product.category_addon_status || [],
      category_customization_status: product.category_customization_status || []
    });
    setEditImagePreview(product.image || '');
    setShowEditProduct(true);
  };

  const handleAddOutlet = async () => {
    try {
      await api.post('/outlets', {
        ...newOutlet,
        latitude: newOutlet.latitude ? parseFloat(newOutlet.latitude) : null,
        longitude: newOutlet.longitude ? parseFloat(newOutlet.longitude) : null,
        delivery_radius_km: parseFloat(newOutlet.delivery_radius_km),
        dine_in_radius_km: parseFloat(newOutlet.dine_in_radius_km),
        pickup_radius_km: parseFloat(newOutlet.pickup_radius_km)
      });
      toast.success('Outlet added successfully');
      setShowAddOutlet(false);
      setNewOutlet({
        name: '',
        address: '',
        phone: '',
        latitude: '',
        longitude: '',
        delivery_radius_km: '5',
        dine_in_radius_km: '0.5',
        pickup_radius_km: '15'
      });
      fetchOutlets();
    } catch (error) {
      console.error('Error adding outlet:', error);
      toast.error('Failed to add outlet');
    }
  };

  const handleEditOutlet = async () => {
    try {
      const updates = {};
      if (editingOutlet.name) updates.name = editingOutlet.name;
      if (editingOutlet.address) updates.address = editingOutlet.address;
      if (editingOutlet.phone) updates.phone = editingOutlet.phone;
      if (editingOutlet.latitude) updates.latitude = parseFloat(editingOutlet.latitude);
      if (editingOutlet.longitude) updates.longitude = parseFloat(editingOutlet.longitude);
      if (editingOutlet.delivery_radius_km) updates.delivery_radius_km = parseFloat(editingOutlet.delivery_radius_km);
      if (editingOutlet.dine_in_radius_km) updates.dine_in_radius_km = parseFloat(editingOutlet.dine_in_radius_km);
      if (editingOutlet.pickup_radius_km) updates.pickup_radius_km = parseFloat(editingOutlet.pickup_radius_km);
      if (editingOutlet.active !== undefined) updates.active = editingOutlet.active;
      if (editingOutlet.ordering_start_time) updates.ordering_start_time = editingOutlet.ordering_start_time;
      if (editingOutlet.ordering_end_time) updates.ordering_end_time = editingOutlet.ordering_end_time;
      if (editingOutlet.branch_code) updates.branch_code = editingOutlet.branch_code.toUpperCase();
      if (editingOutlet.invoice_start_number) updates.invoice_start_number = parseInt(editingOutlet.invoice_start_number);

      await api.put(`/outlets/${editingOutlet.id}`, updates);
      toast.success('Outlet updated successfully');
      setShowEditOutlet(false);
      setEditingOutlet(null);
      fetchOutlets();
    } catch (error) {
      console.error('Error updating outlet:', error);
      toast.error('Failed to update outlet');
    }
  };

  const openEditOutlet = (outlet) => {
    setEditingOutlet({
      id: outlet.id,
      name: outlet.name,
      address: outlet.address,
      phone: outlet.phone,
      latitude: outlet.latitude?.toString() || '',
      longitude: outlet.longitude?.toString() || '',
      delivery_radius_km: outlet.delivery_radius_km?.toString() || '5',
      dine_in_radius_km: outlet.dine_in_radius_km?.toString() || '0.5',
      pickup_radius_km: outlet.pickup_radius_km?.toString() || '15',
      active: outlet.active,
      ordering_start_time: outlet.ordering_start_time || '09:00',
      ordering_end_time: outlet.ordering_end_time || '22:00',
      branch_code: outlet.branch_code || '',
      invoice_start_number: outlet.invoice_start_number?.toString() || '1'
    });
    setShowEditOutlet(true);
  };

  const initStockUpdate = (productId, currentStock) => {
    setStockUpdates({
      ...stockUpdates,
      [productId]: {
        value: '',
        mode: 'add',
        currentStock: currentStock
      }
    });
  };

  const updateStock = async (productId, newStock) => {
    try {
      // Use outlet-specific endpoint if outlet is selected
      if (selectedOutlet !== 'all') {
        await api.put(`/outlets/${selectedOutlet}/stock/${productId}`, { stock: newStock });
      } else {
        await api.put(`/products/${productId}`, { stock: newStock });
      }
      
      // Update local state immediately for responsive UI
      setProducts(prev => prev.map(p => 
        p.id === productId ? { ...p, stock: newStock, outlet_stock: newStock } : p
      ));
      
      toast.success('Stock updated');
      setStockUpdates({});
      
      // Refresh data in background (don't await to prevent blocking)
      fetchProducts().catch(console.error);
      fetchStockValue().catch(console.error);
      fetchCriticalStock().catch(console.error);
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error('Failed to update stock');
    }
  };

  const handleStockUpdate = (productId) => {
    const update = stockUpdates[productId];
    if (!update || !update.value) {
      toast.error('Please enter a quantity');
      return;
    }

    const quantity = parseInt(update.value);
    const newStock = update.mode === 'add' 
      ? update.currentStock + quantity 
      : Math.max(0, update.currentStock - quantity);

    updateStock(productId, newStock);
  };

  const quickStockUpdate = (productId, delta) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    // Use outlet_stock if available (for outlet-specific view), fallback to stock
    const currentStock = product.outlet_stock ?? product.stock ?? 0;
    const newStock = Math.max(0, currentStock + delta);
    updateStock(productId, newStock);
  };

  const generateQRCode = async (outletId = 'main') => {
    try {
      const response = await api.get(`/qr/generate/${outletId}`);
      setQrData(response.data);
      setShowQR(true);
    } catch (error) {
      console.error('Error generating QR:', error);
      toast.error('Failed to generate QR code');
    }
  };

  // Image upload handler
  const handleImageUpload = async (file, isEdit = false) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WebP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const imageUrl = response.data.url;
        
        if (isEdit) {
          setEditingProduct(prev => ({ ...prev, image: imageUrl }));
          setEditImagePreview(imageUrl);
        } else {
          setNewProduct(prev => ({ ...prev, image: imageUrl }));
          setImagePreview(imageUrl);
        }
        
        toast.success('Image uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileSelect = (e, isEdit = false) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file, isEdit);
    }
  };

  const clearImage = (isEdit = false) => {
    if (isEdit) {
      setEditingProduct(prev => ({ ...prev, image: '' }));
      setEditImagePreview('');
      if (editFileInputRef.current) {
        editFileInputRef.current.value = '';
      }
    } else {
      setNewProduct(prev => ({ ...prev, image: '' }));
      setImagePreview('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Toggle product enabled/disabled for specific location
  const toggleProductEnabled = async (productId, currentEnabled) => {
    if (selectedOutlet === 'all') {
      toast.error('Please select a specific location to toggle product availability');
      return;
    }
    
    try {
      await api.put(`/outlets/${selectedOutlet}/stock/${productId}`, {
        enabled: !currentEnabled
      });
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === productId ? { ...p, enabled: !currentEnabled } : p
      ));
      
      toast.success(`Product ${!currentEnabled ? 'enabled' : 'disabled'} for this location`);
    } catch (error) {
      console.error('Error toggling product:', error);
      toast.error('Failed to toggle product');
    }
  };

  // Update critical stock limit for a product at specific location
  const updateCriticalLimit = async (productId, newLimit) => {
    if (selectedOutlet === 'all') {
      toast.error('Please select a specific location');
      return;
    }
    
    try {
      await api.put(`/outlets/${selectedOutlet}/stock/${productId}`, {
        critical_limit: parseInt(newLimit)
      });
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === productId ? { ...p, critical_limit: parseInt(newLimit) } : p
      ));
      
      toast.success('Critical stock limit updated');
    } catch (error) {
      console.error('Error updating critical limit:', error);
      toast.error('Failed to update critical limit');
    }
  };

  // Update product price (global - affects all locations)
  const updateProductPrice = async (productId, newPrice) => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    try {
      await api.put(`/products/${productId}`, { price });
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === productId ? { ...p, price } : p
      ));
      
      toast.success('Price updated successfully');
    } catch (error) {
      console.error('Error updating price:', error);
      toast.error('Failed to update price');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Audio for ticket notifications */}
      <audio ref={audioRef} src="/notification.wav" preload="auto" />
      
      {/* Header - Mobile Responsive */}
      <header className="bg-[#121212] border-b border-white/10">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          {/* Top row - Logo and logout */}
          <div className="flex items-center justify-between mb-3 sm:mb-0">
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/">
                <Button variant="ghost" className="text-white px-2 sm:px-4 h-9 sm:h-10">
                  <span className="hidden sm:inline">← Back</span>
                  <span className="sm:hidden">←</span>
                </Button>
              </Link>
              <LayoutDashboard className="w-6 h-6 sm:w-8 sm:h-8 text-[#FFD600]" strokeWidth={1.5} />
              <h1 className="text-xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                Admin Dashboard
              </h1>
            </div>
            <Button
              onClick={() => {
                logout('admin');
                navigate('/');
              }}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-red-500/10 hover:border-red-500/50 h-9 sm:h-10"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
          
          {/* Bottom row - Filters and actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            {/* Outlet Selector - Filtered for managers */}
            <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
              <SelectTrigger className="w-full sm:w-48 bg-white/5 border-white/10 text-white h-10 sm:h-10">
                <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                {/* Only show "All Locations" for full admins */}
                {!isLocationRestricted && (
                  <SelectItem value="all">All Locations</SelectItem>
                )}
                {/* Filter outlets based on manager's allowed locations */}
                {outlets
                  .filter(outlet => !isLocationRestricted || allowedOutlets.includes(outlet.id))
                  .map(outlet => (
                    <SelectItem key={outlet.id} value={outlet.id}>{outlet.name}</SelectItem>
                  ))
                }
              </SelectContent>
            </Select>

            <Button 
              onClick={() => generateQRCode(selectedOutlet === 'all' ? 'main' : selectedOutlet)}
              className="bg-[#00E5FF] hover:bg-[#00B8D4] text-black h-10 sm:h-10"
              data-testid="generate-qr"
            >
              <QrCode className="w-4 h-4 mr-2" />
              QR Code
            </Button>
          </div>
          
          {/* Manager restriction notice */}
          {isLocationRestricted && (
            <div className="mt-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-xs text-blue-400">
                <MapPin className="w-3 h-3 inline mr-1" />
                Manager access: You can only view and manage your assigned location(s)
              </p>
            </div>
          )}
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Enhanced Stats Cards - Mobile Responsive Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-6 mb-6 sm:mb-8">
          {/* Sales Card with Graph - Full width on mobile */}
          <Card className="bg-[#121212] border-white/10 col-span-2 lg:col-span-2">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-xs sm:text-sm font-medium text-gray-400">Sales</CardTitle>
                  <div className="text-2xl sm:text-3xl font-bold text-white mt-1">
                    {salesData ? formatCurrency(salesData.total_sales) : '...'}
                  </div>
                </div>
                <Select value={timePeriod} onValueChange={setTimePeriod}>
                  <SelectTrigger className="w-full sm:w-32 bg-white/5 border-white/10 text-white text-xs h-8 sm:h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="last_week">Last Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="last_month">Last Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              {salesData && salesData.daily_breakdown && salesData.daily_breakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height={80}>
                  <BarChart data={salesData.daily_breakdown}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="_id" tick={{ fill: '#888', fontSize: 8 }} />
                    <YAxis tick={{ fill: '#888', fontSize: 8 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid #333' }}
                      labelStyle={{ color: '#FFF' }}
                    />
                    <Bar dataKey="sales" fill="#00C853" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-16 sm:h-24 flex items-center justify-center text-gray-500 text-xs sm:text-sm">
                  No sales data
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#121212] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-400">Orders</CardTitle>
              <TrendingUp className="w-4 h-4 text-[#00E5FF]" />
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="text-2xl sm:text-3xl font-bold text-white">
                {salesData ? salesData.total_orders : '...'}
              </div>
              <p className="text-xs text-gray-500 mt-1 truncate">{timePeriod.replace('_', ' ')}</p>
            </CardContent>
          </Card>

          <Card className="bg-[#121212] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-400">Stock Value</CardTitle>
              <Package className="w-4 h-4 text-[#FFD600]" />
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="text-xl sm:text-3xl font-bold text-white">
                {formatCurrency(stockValue)}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {selectedOutlet === 'all' ? 'All outlets' : 'This outlet'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#121212] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-400">Critical Stock</CardTitle>
              <AlertTriangle className="w-4 h-4 text-[#FF4500]" />
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="text-2xl sm:text-3xl font-bold text-[#FF4500]">
                {criticalStock.length}
              </div>
              <p className="text-xs text-gray-500 mt-1">Low stock items</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs - Mobile Responsive */}
        <Tabs defaultValue="products" className="w-full">
          <TabsList className="bg-[#121212] border border-white/10 mb-4 sm:mb-6 flex flex-wrap gap-1 h-auto p-1">
            <TabsTrigger value="products" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Products</TabsTrigger>
            <TabsTrigger value="bulk-edit" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Bulk Edit</TabsTrigger>
            <TabsTrigger value="addons" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Add-ons</TabsTrigger>
            <TabsTrigger value="outlets" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Outlets</TabsTrigger>
            <TabsTrigger value="inventory" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Inventory</TabsTrigger>
            <TabsTrigger value="stock-settings" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">
              <Settings className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="categories" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Categories</TabsTrigger>
            <TabsTrigger value="coupons" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Coupons</TabsTrigger>
            <TabsTrigger value="loyalty" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Loyalty</TabsTrigger>
            <TabsTrigger value="offers" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Offers</TabsTrigger>
            <TabsTrigger value="staff" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Staff</TabsTrigger>
            <TabsTrigger value="reconciliation" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Reconcile</TabsTrigger>
            <TabsTrigger value="top-items" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Top Items</TabsTrigger>
            <TabsTrigger value="banner" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Banner</TabsTrigger>
            <TabsTrigger value="payments" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Payments</TabsTrigger>
            <TabsTrigger value="refunds" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Refunds</TabsTrigger>
            <TabsTrigger value="forecast" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Stock Forecast</TabsTrigger>
            <TabsTrigger value="kitchen" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Kitchen Analytics</TabsTrigger>
            <TabsTrigger value="tickets" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Tickets</TabsTrigger>
            {staffRole === 'admin' && (
              <>
                <TabsTrigger value="reports" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Reports</TabsTrigger>
                <TabsTrigger value="invoice" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Invoice</TabsTrigger>
                <TabsTrigger value="contact" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Contact</TabsTrigger>
                <TabsTrigger value="feedback" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2">Feedback</TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>Menu Items</h2>
              <Button 
                onClick={() => setShowAddProduct(true)}
                className="bg-[#FF4500] hover:bg-[#FF5722] text-white w-full sm:w-auto h-10"
                data-testid="add-product"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </div>
            {selectedOutlet !== 'all' && (
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm text-blue-400">
                <Power className="w-4 h-4 inline mr-2" />
                Toggle the switch on each product to enable/disable it for this location
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {products.map(product => (
                <Card key={product.id} className={`bg-[#121212] border-white/10 group ${product.enabled === false ? 'opacity-50' : ''}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-black/20 relative">
                    <img src={getImageUrl(product.image)} alt={product.name} className="w-full h-full object-cover" />
                    <Button
                      onClick={() => openEditProduct(product)}
                      size="sm"
                      className="absolute top-2 right-2 bg-white/90 hover:bg-white text-black opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {product.enabled === false && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="text-white font-bold text-sm bg-red-500 px-3 py-1 rounded">DISABLED</span>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-base sm:text-lg font-bold text-white truncate">{product.name}</h3>
                          {product.is_addon_only && (
                            <Badge variant="outline" className="border-yellow-500/50 text-yellow-400 text-[10px] shrink-0">Add-on Only</Badge>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-gray-400 mb-1">{product.category}</p>
                        {/* Product Tags */}
                        {product.tags && product.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {product.tags.map(tagName => {
                              const tagData = tags.find(t => t.name === tagName);
                              return (
                                <span
                                  key={tagName}
                                  className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                                  style={{ 
                                    backgroundColor: tagData?.color || '#FF4500',
                                    color: '#000'
                                  }}
                                >
                                  {tagName}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {/* Show effective add-ons from category */}
                        {product.effective_addons && product.effective_addons.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {product.effective_addons.slice(0, 3).map((addon, idx) => (
                              <span key={idx} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-500/20 text-orange-400 border border-orange-500/30">
                                {addon.name}
                              </span>
                            ))}
                            {product.effective_addons.length > 3 && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-500/20 text-gray-400">
                                +{product.effective_addons.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                        {/* Show effective customization groups from category */}
                        {product.effective_customization_groups && product.effective_customization_groups.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {product.effective_customization_groups.slice(0, 2).map((group, idx) => (
                              <span key={idx} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                                {group.name} {group.required && <span className="text-red-400">*</span>}
                              </span>
                            ))}
                            {product.effective_customization_groups.length > 2 && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-500/20 text-gray-400">
                                +{product.effective_customization_groups.length - 2} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Enable/Disable Toggle - Only show when specific location selected */}
                      {selectedOutlet !== 'all' && (
                        <div className="flex flex-col items-center gap-1">
                          <Switch
                            checked={product.enabled !== false}
                            onCheckedChange={() => toggleProductEnabled(product.id, product.enabled !== false)}
                            className="data-[state=checked]:bg-green-500"
                          />
                          <span className="text-[10px] text-gray-500">
                            {product.enabled !== false ? 'ON' : 'OFF'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-lg sm:text-xl font-bold text-[#FF4500]">{formatCurrency(product.price)}</span>
                      <span className={`text-xs sm:text-sm ${
                        product.stock > 20 ? 'text-green-400' : 
                        product.stock > 10 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        Stock: {product.stock}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Bulk Edit Tab */}
          <TabsContent value="bulk-edit">
            <div className="space-y-4">
              {/* Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>Bulk Product Editor</h2>
                  <p className="text-gray-400 text-sm mt-1">Edit multiple products at once in a spreadsheet-style table</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={fetchBulkEditData}
                    variant="outline"
                    className="border-white/20 text-white"
                    disabled={bulkEditLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${bulkEditLoading ? 'animate-spin' : ''}`} />
                    {bulkEditData.length === 0 ? 'Load Products' : 'Refresh'}
                  </Button>
                  {bulkEditChanged.size > 0 && (
                    <>
                      <Button 
                        onClick={discardBulkChanges}
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Discard
                      </Button>
                      <Button 
                        onClick={saveBulkChanges}
                        className="bg-green-500 hover:bg-green-600 text-white"
                        disabled={bulkSaving}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {bulkSaving ? 'Saving...' : `Save ${bulkEditChanged.size} Change${bulkEditChanged.size > 1 ? 's' : ''}`}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Info Banner */}
              {bulkEditData.length === 0 && !bulkEditLoading && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                  <Package className="w-8 h-8 mx-auto text-blue-400 mb-2" />
                  <p className="text-white font-medium">Click "Load Products" to start editing</p>
                  <p className="text-gray-400 text-sm mt-1">Edit names, prices, descriptions, categories, stock, and images all in one place</p>
                </div>
              )}

              {/* Search & Filter Bar */}
              {bulkEditData.length > 0 && (
                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                  <div className="relative flex-1 max-w-md">
                    <input
                      type="text"
                      placeholder="Search by name or description..."
                      value={bulkSearchTerm}
                      onChange={(e) => setBulkSearchTerm(e.target.value)}
                      className="w-full h-10 pl-10 pr-4 bg-[#1A1A1A] border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#FF4500] focus:border-[#FF4500]"
                    />
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {bulkSearchTerm && (
                      <button
                        onClick={() => setBulkSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <Select
                    value={bulkCategoryFilter}
                    onValueChange={setBulkCategoryFilter}
                  >
                    <SelectTrigger className="w-full sm:w-[180px] bg-[#1A1A1A] border-white/10 text-white h-10">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      <SelectItem value="all" className="text-white hover:bg-white/10">All Categories</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id || cat.name} value={cat.name} className="text-white hover:bg-white/10">
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(bulkSearchTerm || bulkCategoryFilter !== 'all') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setBulkSearchTerm(''); setBulkCategoryFilter('all'); }}
                      className="text-gray-400 hover:text-white"
                    >
                      Clear filters
                    </Button>
                  )}
                  <span className="text-gray-500 text-sm whitespace-nowrap">
                    Showing {filteredBulkEditData.length} of {bulkEditData.length}
                  </span>
                </div>
              )}

              {/* Spreadsheet Table */}
              {bulkEditData.length > 0 && (
                <div className="bg-[#1A1A1A] border border-white/10 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1200px]">
                      <thead className="bg-[#121212] border-b border-white/10">
                        <tr>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider p-3 w-[50px]">#</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider p-3 w-[80px]">Image</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider p-3 min-w-[180px]">Name</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider p-3 w-[120px]">Category</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider p-3 w-[100px]">Price (₹)</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider p-3 min-w-[200px]">Description</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider p-3 w-[80px]">Stock</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider p-3 w-[80px]">Tax %</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider p-3 w-[80px]">Pkg Fee</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider p-3 w-[90px]">Add-on Only</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {filteredBulkEditData.map((product, idx) => (
                          <tr 
                            key={product.id} 
                            className={`hover:bg-white/5 transition-colors ${bulkEditChanged.has(product.id) ? 'bg-yellow-500/5 border-l-2 border-yellow-500' : ''}`}
                          >
                            <td className="p-3 text-gray-500 text-sm">{idx + 1}</td>
                            <td className="p-3">
                              <div className="relative group">
                                <div 
                                  className="w-12 h-12 rounded-lg bg-white/5 overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#FF4500] transition-all"
                                  onClick={() => {
                                    if (bulkFileInputRefs.current[product.id]) {
                                      bulkFileInputRefs.current[product.id].click();
                                    }
                                  }}
                                >
                                  {product.image ? (
                                    <img 
                                      src={product.image.startsWith('http') ? product.image : `${process.env.REACT_APP_BACKEND_URL}${product.image}`} 
                                      alt={product.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                                      <Image className="w-5 h-5" />
                                    </div>
                                  )}
                                  {bulkImageUploading[product.id] && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                      <Loader2 className="w-4 h-4 animate-spin text-white" />
                                    </div>
                                  )}
                                </div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  ref={el => bulkFileInputRefs.current[product.id] = el}
                                  onChange={(e) => handleBulkImageUpload(product.id, e.target.files[0])}
                                />
                              </div>
                            </td>
                            <td className="p-2">
                              <Input
                                ref={el => bulkInputRefs.current[`${product.id}-name`] = el}
                                value={product.name}
                                onChange={(e) => handleBulkCellChange(product.id, 'name', e.target.value)}
                                onKeyDown={(e) => handleBulkKeyDown(e, product.id, 0, idx)}
                                className="bg-transparent border-white/10 text-white h-9 text-sm focus:ring-1 focus:ring-[#FF4500] focus:border-[#FF4500]"
                              />
                            </td>
                            <td className="p-2">
                              <Select
                                value={product.category || ''}
                                onValueChange={(value) => handleBulkCellChange(product.id, 'category', value)}
                              >
                                <SelectTrigger className="bg-transparent border-white/10 text-white h-9 text-sm">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#1A1A1A] border-white/10">
                                  {categories.map(cat => (
                                    <SelectItem key={cat.id || cat.name} value={cat.name} className="text-white hover:bg-white/10">
                                      {cat.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-2">
                              <Input
                                ref={el => bulkInputRefs.current[`${product.id}-price`] = el}
                                type="number"
                                min="0"
                                value={product.price}
                                onChange={(e) => handleBulkCellChange(product.id, 'price', e.target.value)}
                                onKeyDown={(e) => handleBulkKeyDown(e, product.id, 1, idx)}
                                className="bg-transparent border-white/10 text-white h-9 text-sm w-full focus:ring-1 focus:ring-[#FF4500]"
                              />
                            </td>
                            <td className="p-2">
                              <Textarea
                                value={product.description || ''}
                                onChange={(e) => handleBulkCellChange(product.id, 'description', e.target.value)}
                                className="bg-transparent border-white/10 text-white text-sm min-h-[36px] h-9 resize-none focus:ring-1 focus:ring-[#FF4500]"
                                rows={1}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                ref={el => bulkInputRefs.current[`${product.id}-stock`] = el}
                                type="number"
                                min="0"
                                value={product.stock}
                                onChange={(e) => handleBulkCellChange(product.id, 'stock', e.target.value)}
                                onKeyDown={(e) => handleBulkKeyDown(e, product.id, 2, idx)}
                                className="bg-transparent border-white/10 text-white h-9 text-sm w-full focus:ring-1 focus:ring-[#FF4500]"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                ref={el => bulkInputRefs.current[`${product.id}-tax_percentage`] = el}
                                type="number"
                                min="0"
                                max="100"
                                value={product.tax_percentage ?? 5}
                                onChange={(e) => handleBulkCellChange(product.id, 'tax_percentage', e.target.value)}
                                onKeyDown={(e) => handleBulkKeyDown(e, product.id, 3, idx)}
                                className="bg-transparent border-white/10 text-white h-9 text-sm w-full focus:ring-1 focus:ring-[#FF4500]"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                ref={el => bulkInputRefs.current[`${product.id}-packaging_fee`] = el}
                                type="number"
                                min="0"
                                value={product.packaging_fee ?? 0}
                                onChange={(e) => handleBulkCellChange(product.id, 'packaging_fee', e.target.value)}
                                onKeyDown={(e) => handleBulkKeyDown(e, product.id, 4, idx)}
                                className="bg-transparent border-white/10 text-white h-9 text-sm w-full focus:ring-1 focus:ring-[#FF4500]"
                              />
                            </td>
                            <td className="p-2 text-center">
                              <Switch
                                checked={product.is_addon_only || false}
                                onCheckedChange={(checked) => handleBulkCellChange(product.id, 'is_addon_only', checked)}
                                className="data-[state=checked]:bg-yellow-500"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Footer Stats */}
                  <div className="bg-[#121212] border-t border-white/10 p-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-400 text-sm">
                        {filteredBulkEditData.length === bulkEditData.length 
                          ? `${bulkEditData.length} products` 
                          : `${filteredBulkEditData.length} of ${bulkEditData.length} products`}
                      </span>
                      <span className="text-gray-500 text-xs hidden sm:inline">
                        <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-gray-400 font-mono">Tab</kbd> next cell • 
                        <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-gray-400 font-mono ml-1">Enter</kbd> next row • 
                        <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-gray-400 font-mono ml-1">↑↓</kbd> navigate rows
                      </span>
                    </div>
                    {bulkEditChanged.size > 0 && (
                      <span className="text-yellow-400 text-sm font-medium">
                        {bulkEditChanged.size} unsaved change{bulkEditChanged.size > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Add-ons Management Tab */}
          <TabsContent value="addons">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>Global Add-ons Management</h2>
                  <p className="text-gray-400 text-sm mt-1">Create add-ons once, assign to categories, toggle availability globally</p>
                </div>
                <Button 
                  onClick={() => setShowAddAddon(true)}
                  className="bg-[#FF4500] hover:bg-[#FF5722] text-white"
                  data-testid="add-global-addon"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Add-on
                </Button>
              </div>

              {loadingAddons ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#FF4500]" />
                </div>
              ) : (
                <>
                  {/* Global Add-ons List */}
                  <Card className="bg-[#1a1a1a] border-white/10">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Package className="w-5 h-5 text-[#FF4500]" />
                        All Add-ons (Finishes)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {globalAddons.length === 0 ? (
                        <p className="text-gray-400 text-center py-4">No add-ons created yet. Click &quot;Create Add-on&quot; to get started.</p>
                      ) : (
                        <div className="space-y-3">
                          {globalAddons.map(addon => (
                            <div 
                              key={addon.id} 
                              className={`flex items-center justify-between p-3 rounded-lg border ${addon.is_active ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}
                            >
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => handleToggleAddon(addon.id)}
                                  className={`w-12 h-6 rounded-full transition-colors relative ${addon.is_active ? 'bg-green-500' : 'bg-red-500'}`}
                                  data-testid={`toggle-addon-${addon.id}`}
                                >
                                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${addon.is_active ? 'right-1' : 'left-1'}`} />
                                </button>
                                <div>
                                  <span className="text-white font-medium">{addon.name}</span>
                                  {addon.price > 0 && (
                                    <span className="text-[#FF4500] ml-2">+{formatCurrency(addon.price)}</span>
                                  )}
                                  {!addon.is_active && (
                                    <Badge variant="destructive" className="ml-2 text-xs">Stock Out</Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingAddon({ ...addon, price: addon.price.toString() })}
                                  className="text-gray-400 hover:text-white"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAddon(addon.id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Category × Add-ons Matrix */}
                  {globalAddons.length > 0 && categoryAddonMatrix.categories.length > 0 && (
                    <Card className="bg-[#1a1a1a] border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Settings className="w-5 h-5 text-[#FF4500]" />
                          Category Assignments
                        </CardTitle>
                        <p className="text-gray-400 text-sm">Check the boxes to enable add-ons for entire categories</p>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-white/10">
                                <th className="text-left text-gray-400 py-2 px-3 font-medium">Category</th>
                                {globalAddons.map(addon => (
                                  <th key={addon.id} className="text-center text-gray-400 py-2 px-3 font-medium min-w-[100px]">
                                    <div className="flex flex-col items-center gap-1">
                                      <span>{addon.name}</span>
                                      {!addon.is_active && <Badge variant="destructive" className="text-xs">OFF</Badge>}
                                      {/* Select All for this add-on column */}
                                      <button
                                        onClick={() => {
                                          const newMappings = { ...categoryAddonMatrix.mappings };
                                          const allEnabled = categoryAddonMatrix.categories.every(
                                            cat => (newMappings[cat.name] || []).includes(addon.id)
                                          );
                                          categoryAddonMatrix.categories.forEach(cat => {
                                            if (!newMappings[cat.name]) newMappings[cat.name] = [];
                                            if (allEnabled) {
                                              newMappings[cat.name] = newMappings[cat.name].filter(id => id !== addon.id);
                                            } else if (!newMappings[cat.name].includes(addon.id)) {
                                              newMappings[cat.name] = [...newMappings[cat.name], addon.id];
                                            }
                                          });
                                          handleUpdateCategoryAddonMatrix(newMappings);
                                        }}
                                        className="text-[10px] text-[#FF4500] hover:text-white underline"
                                      >
                                        {categoryAddonMatrix.categories.every(cat => (categoryAddonMatrix.mappings[cat.name] || []).includes(addon.id)) ? 'Deselect All' : 'Select All'}
                                      </button>
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {categoryAddonMatrix.categories.map(cat => {
                                const allAddonsEnabled = globalAddons.every(addon => (categoryAddonMatrix.mappings[cat.name] || []).includes(addon.id));
                                return (
                                  <tr key={cat.id} className="border-b border-white/5 hover:bg-white/5">
                                    <td className="text-white py-3 px-3 font-medium">
                                      <div className="flex items-center gap-2">
                                        <span>{cat.name}</span>
                                        {/* Select All for this category row */}
                                        <button
                                          onClick={() => {
                                            const newMappings = { ...categoryAddonMatrix.mappings };
                                            if (allAddonsEnabled) {
                                              newMappings[cat.name] = [];
                                            } else {
                                              newMappings[cat.name] = globalAddons.map(a => a.id);
                                            }
                                            handleUpdateCategoryAddonMatrix(newMappings);
                                          }}
                                          className="text-[10px] text-[#FF4500] hover:text-white underline"
                                        >
                                          {allAddonsEnabled ? 'Clear' : 'All'}
                                        </button>
                                      </div>
                                    </td>
                                    {globalAddons.map(addon => {
                                      const isEnabled = (categoryAddonMatrix.mappings[cat.name] || []).includes(addon.id);
                                      return (
                                        <td key={addon.id} className="text-center py-3 px-3">
                                          <input
                                            type="checkbox"
                                            checked={isEnabled}
                                            onChange={(e) => {
                                              const newMappings = { ...categoryAddonMatrix.mappings };
                                              if (!newMappings[cat.name]) newMappings[cat.name] = [];
                                              if (e.target.checked) {
                                                newMappings[cat.name] = [...newMappings[cat.name], addon.id];
                                              } else {
                                                newMappings[cat.name] = newMappings[cat.name].filter(id => id !== addon.id);
                                              }
                                              handleUpdateCategoryAddonMatrix(newMappings);
                                            }}
                                            className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-[#FF4500] focus:ring-[#FF4500] cursor-pointer"
                                            data-testid={`matrix-${cat.name}-${addon.id}`}
                                          />
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Per-Product Overrides Info */}
                  <Card className="bg-[#1a1a1a] border-white/10">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                        Per-Product Overrides
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-400">
                        To disable a specific add-on or customization for individual products within a category, go to the <strong className="text-white">Products</strong> tab and click on a product to see its inherited settings.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Divider */}
                  <div className="border-t border-white/20 my-8"></div>

                  {/* CUSTOMIZATIONS SECTION */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>Customization Groups</h2>
                      <p className="text-gray-400 text-sm mt-1">Radio-button style options (e.g., Choose Masala, Spice Level)</p>
                    </div>
                    <Button 
                      onClick={() => setShowAddCustomization(true)}
                      className="bg-[#FF4500] hover:bg-[#FF5722] text-white"
                      data-testid="add-global-customization"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Customization
                    </Button>
                  </div>

                  {/* Global Customizations List */}
                  <Card className="bg-[#1a1a1a] border-white/10">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Settings className="w-5 h-5 text-[#FF4500]" />
                        All Customization Groups
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {globalCustomizations.length === 0 ? (
                        <p className="text-gray-400 text-center py-4">No customization groups created yet.</p>
                      ) : (
                        <div className="space-y-4">
                          {globalCustomizations.map(group => (
                            <div 
                              key={group.id} 
                              className={`p-4 rounded-lg border ${group.is_active ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={() => handleToggleCustomization(group.id)}
                                    className={`w-12 h-6 rounded-full transition-colors relative ${group.is_active ? 'bg-green-500' : 'bg-red-500'}`}
                                    data-testid={`toggle-customization-${group.id}`}
                                  >
                                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${group.is_active ? 'right-1' : 'left-1'}`} />
                                  </button>
                                  <div>
                                    <span className="text-white font-medium">{group.name}</span>
                                    {group.required && (
                                      <Badge className="ml-2 bg-blue-500/20 text-blue-400 text-xs">Required</Badge>
                                    )}
                                    {!group.is_active && (
                                      <Badge variant="destructive" className="ml-2 text-xs">Disabled</Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingCustomization({ ...group })}
                                    className="text-gray-400 hover:text-white"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteCustomization(group.id)}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              {/* Options */}
                              <div className="pl-4 space-y-2">
                                <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Options:</p>
                                {group.options?.map(opt => (
                                  <div key={opt.id} className="flex items-center justify-between py-1 px-2 rounded bg-white/5">
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleToggleCustomizationOption(group.id, opt.id)}
                                        className={`w-8 h-4 rounded-full transition-colors relative ${opt.is_active ? 'bg-green-500' : 'bg-red-500'}`}
                                      >
                                        <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${opt.is_active ? 'right-0.5' : 'left-0.5'}`} />
                                      </button>
                                      <span className={`text-sm ${opt.is_active ? 'text-white' : 'text-gray-500 line-through'}`}>{opt.name}</span>
                                      {opt.price > 0 && <span className="text-[#FF4500] text-xs">+{formatCurrency(opt.price)}</span>}
                                    </div>
                                    {!opt.is_active && <span className="text-red-400 text-xs">Stock Out</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Category × Customizations Matrix */}
                  {globalCustomizations.length > 0 && categoryCustomizationMatrix.categories?.length > 0 && (
                    <Card className="bg-[#1a1a1a] border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Settings className="w-5 h-5 text-[#FF4500]" />
                          Customization Category Assignments
                        </CardTitle>
                        <p className="text-gray-400 text-sm">Assign customization groups to entire categories</p>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-white/10">
                                <th className="text-left text-gray-400 py-2 px-3 font-medium">Category</th>
                                {globalCustomizations.map(group => (
                                  <th key={group.id} className="text-center text-gray-400 py-2 px-3 font-medium min-w-[100px]">
                                    <div className="flex flex-col items-center gap-1">
                                      <span>{group.name}</span>
                                      {!group.is_active && <Badge variant="destructive" className="text-xs">OFF</Badge>}
                                      {/* Select All for this customization column */}
                                      <button
                                        onClick={() => {
                                          const newMappings = { ...categoryCustomizationMatrix.mappings };
                                          const allEnabled = categoryCustomizationMatrix.categories.every(
                                            cat => (newMappings[cat.name] || []).includes(group.id)
                                          );
                                          categoryCustomizationMatrix.categories.forEach(cat => {
                                            if (!newMappings[cat.name]) newMappings[cat.name] = [];
                                            if (allEnabled) {
                                              newMappings[cat.name] = newMappings[cat.name].filter(id => id !== group.id);
                                            } else if (!newMappings[cat.name].includes(group.id)) {
                                              newMappings[cat.name] = [...newMappings[cat.name], group.id];
                                            }
                                          });
                                          handleUpdateCategoryCustomizationMatrix(newMappings);
                                        }}
                                        className="text-[10px] text-[#FF4500] hover:text-white underline"
                                      >
                                        {categoryCustomizationMatrix.categories.every(cat => (categoryCustomizationMatrix.mappings?.[cat.name] || []).includes(group.id)) ? 'Deselect All' : 'Select All'}
                                      </button>
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {categoryCustomizationMatrix.categories?.map(cat => {
                                const allGroupsEnabled = globalCustomizations.every(group => (categoryCustomizationMatrix.mappings?.[cat.name] || []).includes(group.id));
                                return (
                                  <tr key={cat.id} className="border-b border-white/5 hover:bg-white/5">
                                    <td className="text-white py-3 px-3 font-medium">
                                      <div className="flex items-center gap-2">
                                        <span>{cat.name}</span>
                                        {/* Select All for this category row */}
                                        <button
                                          onClick={() => {
                                            const newMappings = { ...categoryCustomizationMatrix.mappings };
                                            if (allGroupsEnabled) {
                                              newMappings[cat.name] = [];
                                            } else {
                                              newMappings[cat.name] = globalCustomizations.map(g => g.id);
                                            }
                                            handleUpdateCategoryCustomizationMatrix(newMappings);
                                          }}
                                          className="text-[10px] text-[#FF4500] hover:text-white underline"
                                        >
                                          {allGroupsEnabled ? 'Clear' : 'All'}
                                        </button>
                                      </div>
                                    </td>
                                    {globalCustomizations.map(group => {
                                      const isEnabled = (categoryCustomizationMatrix.mappings?.[cat.name] || []).includes(group.id);
                                      return (
                                        <td key={group.id} className="text-center py-3 px-3">
                                          <input
                                            type="checkbox"
                                            checked={isEnabled}
                                            onChange={(e) => {
                                              const newMappings = { ...categoryCustomizationMatrix.mappings };
                                              if (!newMappings[cat.name]) newMappings[cat.name] = [];
                                              if (e.target.checked) {
                                                newMappings[cat.name] = [...newMappings[cat.name], group.id];
                                              } else {
                                                newMappings[cat.name] = newMappings[cat.name].filter(id => id !== group.id);
                                              }
                                              handleUpdateCategoryCustomizationMatrix(newMappings);
                                            }}
                                            className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-[#FF4500] focus:ring-[#FF4500] cursor-pointer"
                                            data-testid={`cust-matrix-${cat.name}-${group.id}`}
                                          />
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>

            {/* Create Add-on Dialog */}
            <Dialog open={showAddAddon} onOpenChange={setShowAddAddon}>
              <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Add-on</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white">Name *</Label>
                    <Input
                      value={newAddon.name}
                      onChange={(e) => setNewAddon(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Butter Garlic Finish"
                      className="bg-[#121212] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Price (₹)</Label>
                    <Input
                      type="number"
                      value={newAddon.price}
                      onChange={(e) => setNewAddon(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="0"
                      className="bg-[#121212] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Link to Inventory (for stock deduction)</Label>
                    <Select
                      value={newAddon.ingredient_id}
                      onValueChange={(value) => setNewAddon(prev => ({ ...prev, ingredient_id: value === 'none' ? '' : value }))}
                    >
                      <SelectTrigger className="bg-[#121212] border-white/10 text-white">
                        <SelectValue placeholder="None (no stock deduction)" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10">
                        <SelectItem value="none">None</SelectItem>
                        {inventory.map(item => (
                          <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={newAddon.is_active}
                      onCheckedChange={(checked) => setNewAddon(prev => ({ ...prev, is_active: checked }))}
                    />
                    <Label className="text-white">Available (in stock)</Label>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowAddAddon(false)}
                      className="flex-1 border-white/10 text-white hover:bg-white/10"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateAddon}
                      disabled={savingAddon}
                      className="flex-1 bg-[#FF4500] hover:bg-[#FF5722]"
                    >
                      {savingAddon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Add-on Dialog */}
            <Dialog open={!!editingAddon} onOpenChange={(open) => !open && setEditingAddon(null)}>
              <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle>Edit Add-on</DialogTitle>
                </DialogHeader>
                {editingAddon && (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white">Name *</Label>
                      <Input
                        value={editingAddon.name}
                        onChange={(e) => setEditingAddon(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-[#121212] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white">Price (₹)</Label>
                      <Input
                        type="number"
                        value={editingAddon.price}
                        onChange={(e) => setEditingAddon(prev => ({ ...prev, price: e.target.value }))}
                        className="bg-[#121212] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white">Link to Inventory</Label>
                      <Select
                        value={editingAddon.ingredient_id || ''}
                        onValueChange={(value) => setEditingAddon(prev => ({ ...prev, ingredient_id: value === 'none' ? '' : value }))}
                      >
                        <SelectTrigger className="bg-[#121212] border-white/10 text-white">
                          <SelectValue placeholder="None (no stock deduction)" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1a] border-white/10">
                          <SelectItem value="none">None</SelectItem>
                          {inventory.map(item => (
                            <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={editingAddon.is_active}
                        onCheckedChange={(checked) => setEditingAddon(prev => ({ ...prev, is_active: checked }))}
                      />
                      <Label className="text-white">Available (in stock)</Label>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setEditingAddon(null)}
                        className="flex-1 border-white/10 text-white hover:bg-white/10"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleUpdateAddon}
                        disabled={savingAddon}
                        className="flex-1 bg-[#FF4500] hover:bg-[#FF5722]"
                      >
                        {savingAddon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Create Customization Dialog */}
            <Dialog open={showAddCustomization} onOpenChange={setShowAddCustomization}>
              <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Customization Group</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white">Group Name *</Label>
                    <Input
                      value={newCustomization.name}
                      onChange={(e) => setNewCustomization(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Choose Masala, Spice Level"
                      className="bg-[#121212] border-white/10 text-white"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={newCustomization.required}
                      onCheckedChange={(checked) => setNewCustomization(prev => ({ ...prev, required: checked }))}
                    />
                    <Label className="text-white">Required (Customer must select one)</Label>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Options</Label>
                    <div className="space-y-2">
                      {newCustomization.options.map((opt, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white/5 p-2 rounded">
                          <Input
                            value={opt.name}
                            onChange={(e) => {
                              const opts = [...newCustomization.options];
                              opts[idx].name = e.target.value;
                              setNewCustomization(prev => ({ ...prev, options: opts }));
                            }}
                            placeholder="Option name"
                            className="bg-[#121212] border-white/10 text-white flex-1"
                          />
                          <Input
                            type="number"
                            value={opt.price}
                            onChange={(e) => {
                              const opts = [...newCustomization.options];
                              opts[idx].price = parseFloat(e.target.value) || 0;
                              setNewCustomization(prev => ({ ...prev, options: opts }));
                            }}
                            placeholder="₹"
                            className="bg-[#121212] border-white/10 text-white w-20"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const opts = newCustomization.options.filter((_, i) => i !== idx);
                              setNewCustomization(prev => ({ ...prev, options: opts }));
                            }}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setNewCustomization(prev => ({
                            ...prev,
                            options: [...prev.options, { name: '', price: 0, is_active: true }]
                          }));
                        }}
                        className="w-full border-dashed border-white/20 text-gray-400 hover:text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Option
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddCustomization(false);
                        setNewCustomization({ name: '', required: false, is_active: true, options: [] });
                      }}
                      className="flex-1 border-white/10 text-white hover:bg-white/10"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateCustomization}
                      disabled={savingCustomization}
                      className="flex-1 bg-[#FF4500] hover:bg-[#FF5722]"
                    >
                      {savingCustomization ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Customization Dialog */}
            <Dialog open={!!editingCustomization} onOpenChange={(open) => !open && setEditingCustomization(null)}>
              <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Customization Group</DialogTitle>
                </DialogHeader>
                {editingCustomization && (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white">Group Name *</Label>
                      <Input
                        value={editingCustomization.name}
                        onChange={(e) => setEditingCustomization(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-[#121212] border-white/10 text-white"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={editingCustomization.required}
                        onCheckedChange={(checked) => setEditingCustomization(prev => ({ ...prev, required: checked }))}
                      />
                      <Label className="text-white">Required</Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={editingCustomization.is_active}
                        onCheckedChange={(checked) => setEditingCustomization(prev => ({ ...prev, is_active: checked }))}
                      />
                      <Label className="text-white">Group Active</Label>
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Options</Label>
                      <div className="space-y-2">
                        {editingCustomization.options?.map((opt, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-white/5 p-2 rounded">
                            <Input
                              value={opt.name}
                              onChange={(e) => {
                                const opts = [...editingCustomization.options];
                                opts[idx].name = e.target.value;
                                setEditingCustomization(prev => ({ ...prev, options: opts }));
                              }}
                              placeholder="Option name"
                              className="bg-[#121212] border-white/10 text-white flex-1"
                            />
                            <Input
                              type="number"
                              value={opt.price || 0}
                              onChange={(e) => {
                                const opts = [...editingCustomization.options];
                                opts[idx].price = parseFloat(e.target.value) || 0;
                                setEditingCustomization(prev => ({ ...prev, options: opts }));
                              }}
                              placeholder="₹"
                              className="bg-[#121212] border-white/10 text-white w-20"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const opts = editingCustomization.options.filter((_, i) => i !== idx);
                                setEditingCustomization(prev => ({ ...prev, options: opts }));
                              }}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingCustomization(prev => ({
                              ...prev,
                              options: [...(prev.options || []), { name: '', price: 0, is_active: true }]
                            }));
                          }}
                          className="w-full border-dashed border-white/20 text-gray-400 hover:text-white"
                        >
                          <Plus className="w-4 h-4 mr-2" /> Add Option
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setEditingCustomization(null)}
                        className="flex-1 border-white/10 text-white hover:bg-white/10"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleUpdateCustomization}
                        disabled={savingCustomization}
                        className="flex-1 bg-[#FF4500] hover:bg-[#FF5722]"
                      >
                        {savingCustomization ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Outlets Tab */}
          <TabsContent value="outlets">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                Outlet Locations
              </h2>
              {/* Only full admins can add outlets */}
              {!isLocationRestricted && (
                <Button 
                  onClick={() => setShowAddOutlet(true)}
                  className="bg-[#FF4500] hover:bg-[#FF5722] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Outlet
                </Button>
              )}
            </div>
            
            {isLocationRestricted && (
              <div className="mb-4 px-4 py-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-400">
                  As a manager, you can only view your assigned outlet(s). Contact an admin to add or modify outlets.
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {outlets
                .filter(outlet => !isLocationRestricted || allowedOutlets.includes(outlet.id))
                .map(outlet => (
                <Card key={outlet.id} className="bg-[#121212] border-white/10">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2">{outlet.name}</h3>
                        <div className="space-y-1 text-sm text-gray-400">
                          <p className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {outlet.address}
                          </p>
                          <p>📞 {outlet.phone}</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => openEditOutlet(outlet)}
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/5"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10">
                      <div>
                        <p className="text-xs text-gray-500">Delivery</p>
                        <p className="text-lg font-bold text-[#00E5FF]">
                          {outlet.delivery_radius_km || 5} km
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Pickup</p>
                        <p className="text-lg font-bold text-green-400">
                          {outlet.pickup_radius_km || 15} km
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Dine-in</p>
                        <p className="text-lg font-bold text-orange-400">
                          {outlet.dine_in_radius_km || 0.5} km
                        </p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">Status</p>
                      <p className={`text-sm font-bold ${outlet.active ? 'text-green-400' : 'text-red-400'}`}>
                        {outlet.active ? 'Active' : 'Inactive'}
                      </p>
                    </div>

                    {(outlet.latitude && outlet.longitude) && (
                      <div className="mt-4 p-3 bg-white/5 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Shop Location (GPS)</p>
                        <p className="text-sm text-white font-mono">
                          {outlet.latitude.toFixed(6)}, {outlet.longitude.toFixed(6)}
                        </p>
                      </div>
                    )}
                    
                    {!outlet.latitude && (
                      <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <p className="text-xs text-yellow-400">
                          ⚠️ GPS location not set. Edit outlet to add coordinates for distance-based delivery.
                        </p>
                      </div>
                    )}

                    {/* Delivery Fee Ranges Button */}
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => {
                          setSelectedOutletForFees(outlet);
                          fetchDeliveryFeeRanges(outlet.id);
                          setShowDeliveryFees(true);
                        }}
                        variant="outline"
                        className="border-[#FF4500]/50 text-[#FF4500] hover:bg-[#FF4500]/10"
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        Delivery Fees
                      </Button>
                      <Button
                        onClick={() => generateQRCode(outlet.id)}
                        className="bg-[#00E5FF] hover:bg-[#00B8D4] text-black"
                      >
                        <QrCode className="w-4 h-4 mr-1" />
                        QR Code
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Enhanced Inventory Tab - Mobile Responsive */}
          <TabsContent value="inventory">
            <div className="mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue' }}>
                Stock Management
              </h2>
              <p className="text-gray-400 text-xs sm:text-sm">
                Location: {selectedOutlet === 'all' ? 'All Outlets (View Only)' : outlets.find(o => o.id === selectedOutlet)?.name || 'Unknown'}
              </p>
              {selectedOutlet === 'all' && (
                <div className="mt-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs sm:text-sm text-yellow-400">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Select a specific location to update stock
                </div>
              )}
            </div>
            <div className="space-y-3 sm:space-y-4">
              {products.map(product => {
                const updateState = stockUpdates[product.id];
                const isDisabled = selectedOutlet === 'all';
                return (
                  <Card key={product.id} className="bg-[#121212] border-white/10">
                    <CardContent className="p-3 sm:p-4">
                      {/* Mobile Layout */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg font-bold text-white truncate">{product.name}</h3>
                          <p className="text-xs sm:text-sm text-gray-400">{product.category}</p>
                        </div>
                        
                        {/* Stock Controls Row */}
                        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4">
                          {/* Current Stock with Quick +1/-1 */}
                          <div className="flex items-center gap-1 sm:gap-2">
                            <Button
                              size="sm"
                              onClick={() => quickStockUpdate(product.id, -1)}
                              className="w-8 h-8 sm:w-9 sm:h-9 p-0 bg-white/10 hover:bg-white/20"
                              disabled={product.stock === 0 || isDisabled}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <div className="text-center min-w-[60px] sm:min-w-[80px]">
                              <p className="text-[10px] sm:text-xs text-gray-500">Stock</p>
                              <span className={`text-2xl sm:text-3xl font-bold font-mono ${
                                product.stock > 20 ? 'text-green-400' : 
                                product.stock > 10 ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {product.stock}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => quickStockUpdate(product.id, 1)}
                              className="w-8 h-8 sm:w-9 sm:h-9 p-0 bg-white/10 hover:bg-white/20"
                              disabled={isDisabled}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>

                          {/* Bulk Update Section - Hidden on mobile when expanded */}
                          {!isDisabled && (
                            <>
                              {updateState ? (
                                <div className="hidden sm:flex items-center gap-2">
                                  <Input
                                    type="number"
                                    value={updateState.value}
                                    onChange={(e) => setStockUpdates({
                                      ...stockUpdates,
                                      [product.id]: { ...updateState, value: e.target.value }
                                    })}
                                    className="w-20 sm:w-24 bg-white/5 border-white/10 text-white h-9"
                                    placeholder="Qty"
                                  />
                                  <RadioGroup
                                    value={updateState.mode}
                                    onValueChange={(value) => setStockUpdates({
                                      ...stockUpdates,
                                      [product.id]: { ...updateState, mode: value }
                                    })}
                                    className="flex gap-2"
                                  >
                                    <div className="flex items-center">
                                      <RadioGroupItem value="add" id={`add-${product.id}`} />
                                      <Label htmlFor={`add-${product.id}`} className="ml-1 text-green-400 cursor-pointer">+</Label>
                                    </div>
                                    <div className="flex items-center">
                                      <RadioGroupItem value="subtract" id={`sub-${product.id}`} />
                                      <Label htmlFor={`sub-${product.id}`} className="ml-1 text-red-400 cursor-pointer">-</Label>
                                    </div>
                                  </RadioGroup>
                                  <Button
                                    size="sm"
                                    onClick={() => handleStockUpdate(product.id)}
                                    className="bg-[#00C853] hover:bg-[#00E676] text-white h-9"
                                  >
                                    <Save className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      const newUpdates = { ...stockUpdates };
                                      delete newUpdates[product.id];
                                      setStockUpdates(newUpdates);
                                    }}
                                    className="text-gray-400 hover:text-white h-9"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  onClick={() => initStockUpdate(product.id, product.stock)}
                                  variant="outline"
                                  size="sm"
                                  className="hidden sm:flex border-white/20 text-white hover:bg-white/5 h-9 text-xs"
                                >
                                  Bulk Update
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Mobile Bulk Update Row */}
                      {!isDisabled && updateState && (
                        <div className="flex sm:hidden items-center gap-2 mt-3 pt-3 border-t border-white/10">
                          <Input
                            type="number"
                            value={updateState.value}
                            onChange={(e) => setStockUpdates({
                              ...stockUpdates,
                              [product.id]: { ...updateState, value: e.target.value }
                            })}
                            className="flex-1 bg-white/5 border-white/10 text-white h-10"
                            placeholder="Qty"
                          />
                          <Button
                            size="sm"
                            onClick={() => setStockUpdates({
                              ...stockUpdates,
                              [product.id]: { ...updateState, mode: updateState.mode === 'add' ? 'subtract' : 'add' }
                            })}
                            variant="outline"
                            className={`h-10 px-3 ${updateState.mode === 'add' ? 'text-green-400 border-green-400/50' : 'text-red-400 border-red-400/50'}`}
                          >
                            {updateState.mode === 'add' ? '+' : '-'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleStockUpdate(product.id)}
                            className="bg-[#00C853] hover:bg-[#00E676] text-white h-10"
                          >
                            <Save className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newUpdates = { ...stockUpdates };
                              delete newUpdates[product.id];
                              setStockUpdates(newUpdates);
                            }}
                            className="text-gray-400 hover:text-white h-10"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {/* Mobile Bulk Update Button */}
                      {!isDisabled && !updateState && (
                        <Button
                          onClick={() => initStockUpdate(product.id, product.stock)}
                          variant="outline"
                          size="sm"
                          className="sm:hidden w-full mt-3 border-white/20 text-white hover:bg-white/5 h-10"
                        >
                          Bulk Update
                        </Button>
                      )}
                      
                      {product.stock < (product.critical_limit || 20) && (
                        <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs sm:text-sm text-yellow-400">
                          <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                          {product.stock < 10 ? 'Critical: ' : ''}Low stock alert
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Stock Settings Tab - NEW */}
          <TabsContent value="stock-settings">
            <div className="mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue' }}>
                Stock Settings
              </h2>
              <p className="text-gray-400 text-xs sm:text-sm">
                Configure critical stock limits per product for: {selectedOutlet === 'all' ? 'All Outlets' : outlets.find(o => o.id === selectedOutlet)?.name || 'Unknown'}
              </p>
              {selectedOutlet === 'all' && (
                <div className="mt-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs sm:text-sm text-yellow-400">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Select a specific location to configure critical stock limits
                </div>
              )}
            </div>
            
            {selectedOutlet !== 'all' ? (
              <div className="space-y-2">
                {/* Header Row */}
                <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-2 text-xs text-gray-500 font-medium">
                  <div className="col-span-4">Product</div>
                  <div className="col-span-2 text-center">Price</div>
                  <div className="col-span-3 text-center">Critical Limit</div>
                  <div className="col-span-3 text-center">Status</div>
                </div>
                
                {products.map(product => (
                  <Card key={product.id} className="bg-[#121212] border-white/10">
                    <CardContent className="p-3 sm:p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 items-center">
                        {/* Product Name */}
                        <div className="sm:col-span-4">
                          <h3 className="text-sm sm:text-base font-bold text-white">{product.name}</h3>
                          <p className="text-xs text-gray-500">{product.category}</p>
                        </div>
                        
                        {/* Price Input */}
                        <div className="sm:col-span-2 flex items-center gap-2">
                          <span className="text-xs text-gray-500 sm:hidden">Price:</span>
                          <div className="flex items-center">
                            <span className="text-gray-400 mr-1">₹</span>
                            <Input
                              type="number"
                              value={product.price}
                              onChange={(e) => updateProductPrice(product.id, e.target.value)}
                              className="w-20 sm:w-24 bg-white/5 border-white/10 text-white text-center h-9"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        
                        {/* Critical Limit Input */}
                        <div className="sm:col-span-3 flex items-center gap-2">
                          <span className="text-xs text-gray-500 sm:hidden">Critical Limit:</span>
                          <Input
                            type="number"
                            value={product.critical_limit || 10}
                            onChange={(e) => updateCriticalLimit(product.id, e.target.value)}
                            className="w-20 sm:w-24 bg-white/5 border-white/10 text-white text-center h-9 mx-auto"
                            min="0"
                          />
                        </div>
                        
                        {/* Active/Disabled Toggle */}
                        <div className="sm:col-span-3 flex items-center justify-start sm:justify-center gap-2">
                          <span className="text-xs text-gray-500 sm:hidden">Status:</span>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={product.enabled !== false}
                              onCheckedChange={() => toggleProductEnabled(product.id, product.enabled !== false)}
                              className="data-[state=checked]:bg-green-500"
                            />
                            <span className={`text-xs font-medium ${product.enabled !== false ? 'text-green-400' : 'text-red-400'}`}>
                              {product.enabled !== false ? 'Active' : 'Disabled'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a specific location from the dropdown above to configure stock settings</p>
              </div>
            )}
          </TabsContent>

          {/* Coupons Management Tab */}
          <TabsContent value="coupons">
            <CouponManagement />
          </TabsContent>

          {/* Categories & Tags Management Tab */}
          <TabsContent value="categories">
            <CategoryTagManagement 
              onCategoriesChange={setCategories}
              onTagsChange={setTags}
            />
          </TabsContent>

          {/* Staff Management Tab */}
          <TabsContent value="staff">
            {isLocationRestricted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Manager Access</h3>
                <p className="text-gray-400 max-w-md mx-auto">
                  Staff management is restricted to admins only. Contact your administrator to add or modify staff accounts.
                </p>
              </div>
            ) : (
              <StaffManagement outlets={outlets} selectedOutlet={selectedOutlet} />
            )}
          </TabsContent>

          {/* Loyalty Points Management Tab */}
          <TabsContent value="loyalty">
            <div className="space-y-6">
              {/* Sub-tabs for Loyalty */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                    Loyalty Program
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    {selectedOutlet === 'all' ? 'Global settings' : `Settings for ${outlets.find(o => o.id === selectedOutlet)?.name || 'this outlet'}`}
                  </p>
                </div>
                <div className="flex gap-2 bg-white/5 rounded-lg p-1">
                  <Button
                    variant={loyaltySubTab === 'customers' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setLoyaltySubTab('customers')}
                    className={loyaltySubTab === 'customers' ? 'bg-[#FFD600] text-black' : 'text-gray-400 hover:text-white'}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Customers
                  </Button>
                  <Button
                    variant={loyaltySubTab === 'settings' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setLoyaltySubTab('settings');
                      if (!loyaltySettings) fetchLoyaltySettings();
                    }}
                    className={loyaltySubTab === 'settings' ? 'bg-[#FFD600] text-black' : 'text-gray-400 hover:text-white'}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Rules & Settings
                  </Button>
                </div>
              </div>

              {/* Customers Sub-Tab */}
              {loyaltySubTab === 'customers' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <Input
                      placeholder="Search by phone number..."
                      value={loyaltySearchTerm}
                      onChange={(e) => setLoyaltySearchTerm(e.target.value)}
                      className="max-w-xs bg-white/5 border-white/10 text-white"
                    />
                    <Button
                      onClick={() => {
                        setLoadingLoyalty(true);
                        fetchCustomersWithLoyalty().finally(() => setLoadingLoyalty(false));
                      }}
                      className="bg-[#00E5FF] hover:bg-[#00B8D4] text-black"
                      disabled={loadingLoyalty}
                    >
                      {loadingLoyalty ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                      ) : (
                        <>Refresh List</>
                      )}
                    </Button>
                  </div>

                  {/* Customer List */}
                  {customersWithLoyalty.length === 0 ? (
                    <Card className="bg-[#121212] border-white/10">
                      <CardContent className="py-12 text-center">
                        <Star className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                        <h3 className="text-lg font-bold text-white mb-2">No Customers Found</h3>
                        <p className="text-gray-400 text-sm">
                          Click &quot;Refresh List&quot; to load customers with loyalty points
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid gap-4">
                      {customersWithLoyalty
                        .filter(c => !loyaltySearchTerm || c.phone.includes(loyaltySearchTerm))
                        .map(customer => (
                          <Card key={customer.id} className="bg-[#121212] border-white/10">
                            <CardContent className="p-4">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-[#FFD600]/20 flex items-center justify-center">
                                      <Star className="w-5 h-5 text-[#FFD600]" />
                                    </div>
                                    <div>
                                      <h3 className="text-white font-bold">{customer.name || 'Customer'}</h3>
                                      <p className="text-gray-400 text-sm">{customer.phone}</p>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-6">
                                  <div className="text-center">
                                    <p className="text-xs text-gray-500">Points Balance</p>
                                    <p className="text-2xl font-bold text-[#FFD600]">{customer.loyalty_points || 0}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-xs text-gray-500">Tier</p>
                                    <Badge className={`${
                                      customer.loyalty_tier === 'gold' ? 'bg-yellow-500' :
                                      customer.loyalty_tier === 'silver' ? 'bg-gray-400' : 'bg-amber-700'
                                    } text-black`}>
                                      {customer.loyalty_tier?.toUpperCase() || 'BRONZE'}
                                    </Badge>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-xs text-gray-500">Orders</p>
                                    <p className="text-lg font-bold text-white">{customer.total_orders || 0}</p>
                                  </div>
                                  <Button
                                    onClick={() => {
                                      setSelectedCustomerForPoints(customer);
                                      setPointsAdjustment({ amount: '', reason: '', type: 'add' });
                                      setShowAdjustPoints(true);
                                    }}
                                    variant="outline"
                                    size="sm"
                                    className="border-[#FFD600]/50 text-[#FFD600] hover:bg-[#FFD600]/10"
                                  >
                                    <Edit className="w-4 h-4 mr-1" />
                                    Adjust Points
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {/* Settings Sub-Tab */}
              {loyaltySubTab === 'settings' && (
                <div className="space-y-6">
                  {loadingLoyaltySettings ? (
                    <Card className="bg-[#121212] border-white/10">
                      <CardContent className="py-12 text-center">
                        <Loader2 className="w-8 h-8 mx-auto mb-4 text-[#FFD600] animate-spin" />
                        <p className="text-gray-400">Loading settings...</p>
                      </CardContent>
                    </Card>
                  ) : loyaltySettings ? (
                    <>
                      {/* Loyalty Program Toggle */}
                      <Card className="bg-[#121212] border-white/10">
                        <CardContent className="py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${loyaltySettings.loyalty_enabled !== false ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                                <Gift className={`w-5 h-5 ${loyaltySettings.loyalty_enabled !== false ? 'text-green-400' : 'text-red-400'}`} />
                              </div>
                              <div>
                                <h3 className="text-white font-semibold">Loyalty Points Program</h3>
                                <p className="text-gray-400 text-sm">
                                  {loyaltySettings.loyalty_enabled !== false 
                                    ? 'Customers can earn and redeem loyalty points' 
                                    : 'Loyalty points are disabled for customers'}
                                </p>
                              </div>
                            </div>
                            <Switch
                              checked={loyaltySettings.loyalty_enabled !== false}
                              onCheckedChange={(checked) => setLoyaltySettings({...loyaltySettings, loyalty_enabled: checked})}
                            />
                          </div>
                        </CardContent>
                      </Card>

                      {/* Location Notice */}
                      <div className="p-4 bg-[#FFD600]/10 border border-[#FFD600]/30 rounded-lg">
                        <p className="text-[#FFD600] text-sm">
                          <Settings className="w-4 h-4 inline mr-2" />
                          {selectedOutlet === 'all' 
                            ? 'Editing GLOBAL settings - these apply to all outlets without custom settings'
                            : `Editing settings for "${outlets.find(o => o.id === selectedOutlet)?.name}" - these override global settings`
                          }
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Earning Rules */}
                        <Card className="bg-[#121212] border-white/10">
                          <CardHeader>
                            <CardTitle className="text-white flex items-center gap-2">
                              <TrendingUp className="w-5 h-5 text-green-400" />
                              Earning Rules
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <Label className="text-gray-400 text-sm">Points earned per ₹1 spent</Label>
                              <Input
                                type="number"
                                step="0.1"
                                min="0"
                                value={loyaltySettings.points_per_rupee}
                                onChange={(e) => setLoyaltySettings({...loyaltySettings, points_per_rupee: parseFloat(e.target.value) || 0})}
                                className="bg-white/5 border-white/10 text-white mt-1"
                              />
                              <p className="text-xs text-gray-500 mt-1">e.g., 1 = customer earns 1 point per ₹1 spent</p>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Redemption Rules */}
                        <Card className="bg-[#121212] border-white/10">
                          <CardHeader>
                            <CardTitle className="text-white flex items-center gap-2">
                              <DollarSign className="w-5 h-5 text-[#00E5FF]" />
                              Redemption Rules
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <Label className="text-gray-400 text-sm">Each point worth (₹)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={loyaltySettings.rupees_per_point}
                                onChange={(e) => setLoyaltySettings({...loyaltySettings, rupees_per_point: parseFloat(e.target.value) || 0.25})}
                                className="bg-white/5 border-white/10 text-white mt-1"
                              />
                              <p className="text-xs text-gray-500 mt-1">e.g., 0.25 = 4 points = ₹1 discount</p>
                            </div>
                            <div>
                              <Label className="text-gray-400 text-sm">Minimum points to redeem</Label>
                              <Input
                                type="number"
                                min="0"
                                value={loyaltySettings.min_redeem_points}
                                onChange={(e) => setLoyaltySettings({...loyaltySettings, min_redeem_points: parseInt(e.target.value) || 0})}
                                className="bg-white/5 border-white/10 text-white mt-1"
                              />
                            </div>
                          </CardContent>
                        </Card>

                        {/* Bonus Rules */}
                        <Card className="bg-[#121212] border-white/10">
                          <CardHeader>
                            <CardTitle className="text-white flex items-center gap-2">
                              <Star className="w-5 h-5 text-[#FFD600]" />
                              Bonus Rules
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <Label className="text-gray-400 text-sm">Welcome bonus (new signup)</Label>
                              <Input
                                type="number"
                                min="0"
                                value={loyaltySettings.welcome_bonus}
                                onChange={(e) => setLoyaltySettings({...loyaltySettings, welcome_bonus: parseInt(e.target.value) || 0})}
                                className="bg-white/5 border-white/10 text-white mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-400 text-sm">Referral bonus (referrer gets)</Label>
                              <Input
                                type="number"
                                min="0"
                                value={loyaltySettings.referral_bonus_referrer}
                                onChange={(e) => setLoyaltySettings({...loyaltySettings, referral_bonus_referrer: parseInt(e.target.value) || 0})}
                                className="bg-white/5 border-white/10 text-white mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-400 text-sm">Referral bonus (new user gets)</Label>
                              <Input
                                type="number"
                                min="0"
                                value={loyaltySettings.referral_bonus_referee}
                                onChange={(e) => setLoyaltySettings({...loyaltySettings, referral_bonus_referee: parseInt(e.target.value) || 0})}
                                className="bg-white/5 border-white/10 text-white mt-1"
                              />
                            </div>
                          </CardContent>
                        </Card>

                        {/* Expiry Settings */}
                        <Card className="bg-[#121212] border-white/10">
                          <CardHeader>
                            <CardTitle className="text-white flex items-center gap-2">
                              <Clock className="w-5 h-5 text-orange-400" />
                              Expiry Settings
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <Label className="text-gray-400 text-sm">Points expire after (days)</Label>
                              <Input
                                type="number"
                                min="0"
                                value={loyaltySettings.points_expiry_days}
                                onChange={(e) => setLoyaltySettings({...loyaltySettings, points_expiry_days: parseInt(e.target.value) || 180})}
                                className="bg-white/5 border-white/10 text-white mt-1"
                              />
                              <p className="text-xs text-gray-500 mt-1">Set to 0 for no expiry</p>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Tier Thresholds */}
                        <Card className="bg-[#121212] border-white/10 lg:col-span-2">
                          <CardHeader>
                            <CardTitle className="text-white flex items-center gap-2">
                              <TrendingUp className="w-5 h-5 text-purple-400" />
                              Tier Thresholds & Multipliers
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              {/* Bronze */}
                              <div className="p-4 bg-amber-900/20 border border-amber-700/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-4">
                                  <div className="w-8 h-8 rounded-full bg-amber-700 flex items-center justify-center">
                                    <Star className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="text-amber-400 font-bold">BRONZE</span>
                                </div>
                                <div className="space-y-3">
                                  <div>
                                    <Label className="text-gray-400 text-xs">Min Points</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={loyaltySettings.bronze_min_points}
                                      onChange={(e) => setLoyaltySettings({...loyaltySettings, bronze_min_points: parseInt(e.target.value) || 0})}
                                      className="bg-white/5 border-white/10 text-white mt-1 h-9"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-gray-400 text-xs">Multiplier</Label>
                                    <Input
                                      type="number"
                                      step="0.05"
                                      min="1"
                                      value={loyaltySettings.bronze_multiplier}
                                      onChange={(e) => setLoyaltySettings({...loyaltySettings, bronze_multiplier: parseFloat(e.target.value) || 1})}
                                      className="bg-white/5 border-white/10 text-white mt-1 h-9"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Silver */}
                              <div className="p-4 bg-gray-500/20 border border-gray-400/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-4">
                                  <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center">
                                    <Star className="w-4 h-4 text-black" />
                                  </div>
                                  <span className="text-gray-300 font-bold">SILVER</span>
                                </div>
                                <div className="space-y-3">
                                  <div>
                                    <Label className="text-gray-400 text-xs">Min Points</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={loyaltySettings.silver_min_points}
                                      onChange={(e) => setLoyaltySettings({...loyaltySettings, silver_min_points: parseInt(e.target.value) || 500})}
                                      className="bg-white/5 border-white/10 text-white mt-1 h-9"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-gray-400 text-xs">Multiplier</Label>
                                    <Input
                                      type="number"
                                      step="0.05"
                                      min="1"
                                      value={loyaltySettings.silver_multiplier}
                                      onChange={(e) => setLoyaltySettings({...loyaltySettings, silver_multiplier: parseFloat(e.target.value) || 1.25})}
                                      className="bg-white/5 border-white/10 text-white mt-1 h-9"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Gold */}
                              <div className="p-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-4">
                                  <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center">
                                    <Star className="w-4 h-4 text-black" />
                                  </div>
                                  <span className="text-yellow-400 font-bold">GOLD</span>
                                </div>
                                <div className="space-y-3">
                                  <div>
                                    <Label className="text-gray-400 text-xs">Min Points</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={loyaltySettings.gold_min_points}
                                      onChange={(e) => setLoyaltySettings({...loyaltySettings, gold_min_points: parseInt(e.target.value) || 2000})}
                                      className="bg-white/5 border-white/10 text-white mt-1 h-9"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-gray-400 text-xs">Multiplier</Label>
                                    <Input
                                      type="number"
                                      step="0.05"
                                      min="1"
                                      value={loyaltySettings.gold_multiplier}
                                      onChange={(e) => setLoyaltySettings({...loyaltySettings, gold_multiplier: parseFloat(e.target.value) || 1.5})}
                                      className="bg-white/5 border-white/10 text-white mt-1 h-9"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-4">
                              Multiplier affects points earned per purchase. e.g., 1.5x multiplier means Gold members earn 50% more points.
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Save / Reset Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-white/10">
                        {selectedOutlet !== 'all' && (
                          <Button
                            variant="outline"
                            onClick={resetToGlobalSettings}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Reset to Global Defaults
                          </Button>
                        )}
                        <Button
                          onClick={saveLoyaltySettings}
                          className="bg-[#00C853] hover:bg-[#00E676] text-white"
                          disabled={savingLoyaltySettings}
                        >
                          {savingLoyaltySettings ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            <><Save className="w-4 h-4 mr-2" /> Save Settings</>
                          )}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Card className="bg-[#121212] border-white/10">
                      <CardContent className="py-12 text-center">
                        <Settings className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                        <h3 className="text-lg font-bold text-white mb-2">Failed to Load Settings</h3>
                        <Button onClick={fetchLoyaltySettings} className="mt-4">
                          Retry
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>Promotional Offers</h2>
                  <p className="text-gray-400 text-sm mt-1">Create combo deals, discounts, and special promotions</p>
                </div>
                <Button
                  onClick={() => openOfferDialog()}
                  className="bg-[#FF4500] hover:bg-[#FF4500]/80"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Offer
                </Button>
              </div>

              {/* Active Offers */}
              <div className="space-y-4">
                {offers.length === 0 ? (
                  <Card className="bg-[#1A1A1A] border-white/10">
                    <CardContent className="py-12 text-center">
                      <Gift className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                      <p className="text-gray-400">No offers yet</p>
                      <p className="text-gray-500 text-sm mt-2">Create your first promotional offer</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {offers.map(offer => (
                      <Card key={offer.id} className={`bg-[#1A1A1A] border-white/10 ${!offer.is_active ? 'opacity-60' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="text-white font-semibold">{offer.name}</h3>
                                {offer.is_active ? (
                                  <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                                ) : (
                                  <Badge className="bg-gray-500/20 text-gray-400">Inactive</Badge>
                                )}
                              </div>
                              {offer.description && (
                                <p className="text-gray-400 text-sm mt-1">{offer.description}</p>
                              )}
                            </div>
                          </div>

                          {/* Offer Type Badge */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            <Badge className={`${
                              offer.offer_type === 'combo_deal' ? 'bg-orange-500/20 text-orange-400' :
                              offer.offer_type === 'percentage' ? 'bg-blue-500/20 text-blue-400' :
                              offer.offer_type === 'flat_discount' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>
                              {offer.offer_type === 'combo_deal' ? 'Combo Deal' :
                               offer.offer_type === 'percentage' ? 'Percentage Off' :
                               offer.offer_type === 'flat_discount' ? 'Flat Discount' : 'BOGO'}
                            </Badge>
                            {offer.condition?.condition_type && (
                              <Badge className="bg-white/10 text-gray-300">
                                {offer.condition.condition_type === 'any_item' ? 'Any Item' :
                                 offer.condition.condition_type === 'min_cart_value' ? `Min ₹${offer.condition.min_cart_value}` :
                                 offer.condition.condition_type === 'specific_product' ? 'Specific Product' : 'Specific Category'}
                              </Badge>
                            )}
                          </div>

                          {/* Reward Info */}
                          {offer.offer_type === 'combo_deal' && offer.reward?.product_name && (
                            <div className="bg-white/5 rounded-lg p-3 mb-3">
                              <p className="text-gray-400 text-xs uppercase mb-1">Get this at</p>
                              <p className="text-white font-medium">{offer.reward.product_name}</p>
                              <p className="text-[#FF4500] font-bold text-lg">₹{offer.reward.special_price}</p>
                            </div>
                          )}

                          {(offer.offer_type === 'percentage' || offer.offer_type === 'flat_discount') && (
                            <div className="bg-white/5 rounded-lg p-3 mb-3">
                              <p className="text-[#FF4500] font-bold text-lg">
                                {offer.offer_type === 'percentage' 
                                  ? `${offer.reward?.discount_percentage}% OFF`
                                  : `₹${offer.reward?.discount_amount} OFF`}
                              </p>
                              {offer.reward?.max_discount && (
                                <p className="text-gray-400 text-xs">Max discount: ₹{offer.reward.max_discount}</p>
                              )}
                            </div>
                          )}

                          {/* Validity */}
                          {(offer.start_date || offer.end_date) && (
                            <div className="text-gray-500 text-xs mb-3">
                              {offer.start_date && <span>From {new Date(offer.start_date).toLocaleDateString()}</span>}
                              {offer.end_date && <span> to {new Date(offer.end_date).toLocaleDateString()}</span>}
                            </div>
                          )}

                          {/* Usage */}
                          {offer.usage_limit && (
                            <p className="text-gray-500 text-xs mb-3">
                              Used: {offer.usage_count || 0} / {offer.usage_limit}
                            </p>
                          )}

                          {/* Actions */}
                          <div className="flex gap-2 mt-3 pt-3 border-t border-white/10">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleOfferStatus(offer)}
                              className={offer.is_active ? 'border-red-500/50 text-red-400' : 'border-green-500/50 text-green-400'}
                            >
                              {offer.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openOfferDialog(offer)}
                              className="border-white/20 text-white"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteOffer(offer.id)}
                              className="border-red-500/50 text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Reconciliation Tab */}
          <TabsContent value="reconciliation">
            <StockReconciliation outlets={outlets} products={products} selectedOutlet={selectedOutlet} />
          </TabsContent>

          {/* Top Selling Tab */}
          <TabsContent value="top-items">
            <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue' }}>
              Top Selling Items
            </h2>
            <div className="space-y-4">
              {analytics?.top_selling_items?.map((item, idx) => (
                <Card key={idx} className="bg-[#121212] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#FF4500] flex items-center justify-center text-white font-bold text-lg">
                            #{idx + 1}
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-white">{item._id}</h3>
                            <p className="text-sm text-gray-400">Sold: {item.total_quantity} units</p>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[#00C853]">
                          {formatCurrency(item.total_revenue)}
                        </p>
                        <p className="text-xs text-gray-500">Total revenue</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Banner Settings Tab */}
          <TabsContent value="banner">
            <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue' }}>
              Hero Banner Settings
            </h2>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-6 space-y-6">
                {/* Preview */}
                <div className="relative rounded-lg overflow-hidden bg-black/40 p-8 text-center">
                  {bannerSettings.hero_image && (
                    <div className="absolute inset-0">
                      <img 
                        src={getImageUrl(bannerSettings.hero_image)} 
                        alt="Banner preview"
                        className="w-full h-full object-cover opacity-50"
                      />
                    </div>
                  )}
                  <div className="relative">
                    <h3 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue' }}>
                      {bannerSettings.hero_headline || 'Your Headline Here'}
                    </h3>
                    <p className="text-gray-300">
                      {bannerSettings.hero_tagline || 'Your tagline here'}
                    </p>
                  </div>
                </div>

                {/* Headline Input */}
                <div>
                  <Label className="text-white mb-2 block">Headline</Label>
                  <Input
                    value={bannerSettings.hero_headline}
                    onChange={(e) => setBannerSettings(prev => ({ ...prev, hero_headline: e.target.value }))}
                    placeholder="Where Heat Meets The Sea"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="banner-headline"
                  />
                </div>

                {/* Tagline Input */}
                <div>
                  <Label className="text-white mb-2 block">Tagline</Label>
                  <Input
                    value={bannerSettings.hero_tagline}
                    onChange={(e) => setBannerSettings(prev => ({ ...prev, hero_tagline: e.target.value }))}
                    placeholder="Premium seafood grilled to perfection..."
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="banner-tagline"
                  />
                </div>

                {/* Hero Image */}
                <div>
                  <Label className="text-white mb-2 block">Background Image</Label>
                  <input
                    ref={bannerFileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={(e) => e.target.files?.[0] && handleBannerImageUpload(e.target.files[0])}
                    className="hidden"
                    data-testid="banner-image-input"
                  />
                  
                  {bannerSettings.hero_image ? (
                    <div className="relative rounded-lg overflow-hidden bg-black/20">
                      <img 
                        src={getImageUrl(bannerSettings.hero_image)} 
                        alt="Banner background" 
                        className="w-full h-32 object-cover"
                      />
                      <div className="absolute top-2 right-2 flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => bannerFileInputRef.current?.click()}
                          className="bg-white/90 hover:bg-white text-black"
                          disabled={uploadingImage}
                        >
                          <Upload className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setBannerSettings(prev => ({ ...prev, hero_image: '' }))}
                          className="bg-red-500/90 hover:bg-red-500 text-white"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div
                        onClick={() => !uploadingImage && bannerFileInputRef.current?.click()}
                        className={`border-2 border-dashed border-white/20 rounded-lg p-4 text-center cursor-pointer hover:border-[#FF4500]/50 hover:bg-white/5 transition-all ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {uploadingImage ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-[#FF4500] border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-gray-400 text-sm">Uploading...</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2">
                            <Upload className="w-5 h-5 text-gray-500" />
                            <span className="text-gray-400 text-sm">Upload image</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-px bg-white/10"></div>
                        <span className="text-gray-500 text-xs">OR</span>
                        <div className="flex-1 h-px bg-white/10"></div>
                      </div>
                      
                      <Input
                        placeholder="Paste image URL (https://...)"
                        value={bannerSettings.hero_image}
                        onChange={(e) => setBannerSettings(prev => ({ ...prev, hero_image: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white text-sm"
                        data-testid="banner-image-url"
                      />
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <Button
                  onClick={handleSaveBanner}
                  disabled={savingBanner}
                  className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white py-6 text-lg font-bold"
                  data-testid="save-banner"
                >
                  {savingBanner ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Banner Settings
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Settings Tab */}
          <TabsContent value="payments">
            <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue' }}>
              Payment Settings
            </h2>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-6 space-y-6">
                <p className="text-gray-400 text-sm">
                  Enable or disable payment methods available to customers during checkout.
                </p>

                {/* Cash on Delivery */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <h3 className="text-white font-medium">Cash on Delivery (COD)</h3>
                    <p className="text-gray-500 text-sm">Allow customers to pay when order is delivered</p>
                  </div>
                  <button
                    onClick={() => setPaymentSettings(prev => ({ ...prev, cod_enabled: !prev.cod_enabled }))}
                    className={`relative w-14 h-7 rounded-full transition-colors ${paymentSettings.cod_enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                  >
                    <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${paymentSettings.cod_enabled ? 'translate-x-7' : ''}`} />
                  </button>
                </div>

                {/* UPI Payment */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <h3 className="text-white font-medium">UPI Payment</h3>
                    <p className="text-gray-500 text-sm">Allow payments via UPI (Razorpay)</p>
                  </div>
                  <button
                    onClick={() => setPaymentSettings(prev => ({ ...prev, upi_enabled: !prev.upi_enabled }))}
                    className={`relative w-14 h-7 rounded-full transition-colors ${paymentSettings.upi_enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                  >
                    <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${paymentSettings.upi_enabled ? 'translate-x-7' : ''}`} />
                  </button>
                </div>

                {/* Razorpay Online Payment */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <h3 className="text-white font-medium">Online Payment (Razorpay)</h3>
                    <p className="text-gray-500 text-sm">Accept cards, netbanking, wallets via Razorpay</p>
                  </div>
                  <button
                    onClick={() => setPaymentSettings(prev => ({ ...prev, razorpay_enabled: !prev.razorpay_enabled }))}
                    className={`relative w-14 h-7 rounded-full transition-colors ${paymentSettings.razorpay_enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                  >
                    <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${paymentSettings.razorpay_enabled ? 'translate-x-7' : ''}`} />
                  </button>
                </div>

                {/* Save Button */}
                <Button
                  onClick={handleSavePaymentSettings}
                  disabled={savingPayment}
                  className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white py-6 text-lg font-bold"
                  data-testid="save-payment-settings"
                >
                  {savingPayment ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Payment Settings
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Refund History Tab */}
          <TabsContent value="refunds">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                Refund History
              </h2>
              <Button
                onClick={fetchRefundHistory}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingRefunds ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {/* Refund Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-4">
                  <p className="text-gray-400 text-sm">Total Refunds</p>
                  <p className="text-2xl font-bold text-white">{refundStats.count}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-4">
                  <p className="text-gray-400 text-sm">Total Refund Amount</p>
                  <p className="text-2xl font-bold text-orange-400">{formatCurrency(refundStats.total)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Refund List */}
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                {loadingRefunds ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                  </div>
                ) : refundHistory.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No refunds found</p>
                ) : (
                  <div className="space-y-3">
                    {refundHistory.map((refund, idx) => (
                      <div key={idx} className="bg-white/5 p-4 rounded-lg">
                        <div className="flex flex-col sm:flex-row justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-[#00E5FF] font-mono">{refund.order_number}</span>
                              <Badge className={refund.refund_type === 'full' ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'}>
                                {refund.refund_type === 'full' ? 'Full Refund' : 'Partial Refund'}
                              </Badge>
                            </div>
                            <p className="text-gray-400 text-sm mt-1">{refund.customer_name} • {refund.customer_phone}</p>
                            {refund.refund_reason && (
                              <p className="text-gray-500 text-xs mt-1">Reason: {refund.refund_reason}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-orange-400 font-bold">{formatCurrency(refund.refund_amount)}</p>
                            <p className="text-gray-500 text-xs">of {formatCurrency(refund.order_total)}</p>
                            <p className="text-gray-500 text-xs mt-1">
                              {new Date(refund.refunded_at || refund.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        {refund.rejected_items && refund.rejected_items.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-white/5">
                            <p className="text-xs text-gray-400 mb-1">Rejected items:</p>
                            <div className="flex flex-wrap gap-1">
                              {refund.rejected_items.map((item, i) => (
                                <Badge key={i} variant="outline" className="text-xs border-white/10 text-gray-300">
                                  {item.quantity}x {item.product_name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stock Forecast Tab */}
          <TabsContent value="forecast">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                Stock Forecast
              </h2>
              <div className="flex items-center gap-2">
                <select
                  value={forecastDays}
                  onChange={(e) => setForecastDays(Number(e.target.value))}
                  className="bg-[#121212] border border-white/10 text-white rounded-lg px-3 py-2"
                >
                  <option value={3}>Last 3 days</option>
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                </select>
                <Button
                  onClick={fetchStockForecasts}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingForecasts ? 'animate-spin' : ''}`} />
                  Analyze
                </Button>
              </div>
            </div>

            <p className="text-gray-400 text-sm mb-4">
              Based on order velocity from the last {forecastDays} days, here&apos;s when each product may run out of stock.
            </p>

            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                {loadingForecasts ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                  </div>
                ) : stockForecasts.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No forecast data available. Make sure there are orders in the system.</p>
                ) : (
                  <div className="space-y-3">
                    {stockForecasts.map((forecast, idx) => (
                      <div 
                        key={idx} 
                        className={`p-4 rounded-lg border ${
                          forecast.is_critical 
                            ? 'bg-red-500/10 border-red-500/30' 
                            : forecast.days_until_stockout && forecast.days_until_stockout <= 7 
                              ? 'bg-yellow-500/10 border-yellow-500/30' 
                              : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{forecast.product_name}</span>
                              {forecast.is_critical && (
                                <Badge className="bg-red-500 text-white">CRITICAL</Badge>
                              )}
                            </div>
                            <p className="text-gray-400 text-sm">
                              Current stock: <span className="text-white">{forecast.current_stock}</span>
                              {' • '}
                              Daily avg: <span className="text-white">{forecast.daily_avg_consumption}</span>/day
                            </p>
                          </div>
                          <div className="text-right">
                            {forecast.days_until_stockout !== null ? (
                              <>
                                <p className={`font-bold ${forecast.is_critical ? 'text-red-400' : forecast.days_until_stockout <= 7 ? 'text-yellow-400' : 'text-green-400'}`}>
                                  {forecast.days_until_stockout} days
                                </p>
                                <p className="text-gray-500 text-xs">until stockout</p>
                              </>
                            ) : (
                              <p className="text-green-400 font-medium">No immediate risk</p>
                            )}
                          </div>
                        </div>
                        {forecast.estimated_stockout_date && (
                          <p className="text-gray-500 text-xs mt-2">
                            Est. stockout: {new Date(forecast.estimated_stockout_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Kitchen Analytics Tab */}
          <TabsContent value="kitchen">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                Kitchen Prep Time Analytics
              </h2>
              <div className="flex items-center gap-2">
                <select
                  value={prepTimeDays}
                  onChange={(e) => setPrepTimeDays(Number(e.target.value))}
                  className="bg-[#121212] border border-white/10 text-white rounded-lg px-3 py-2"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={60}>Last 60 days</option>
                </select>
                <Button
                  onClick={fetchPrepTimeAnalytics}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingPrepTimes ? 'animate-spin' : ''}`} />
                  Analyze
                </Button>
              </div>
            </div>

            <p className="text-gray-400 text-sm mb-4">
              Track average kitchen preparation times per product to optimize efficiency and identify bottlenecks.
            </p>

            {/* Summary Cards */}
            {prepTimeAnalytics && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-gray-400 text-sm">Avg Prep Time</p>
                    <p className="text-2xl font-bold text-white">{prepTimeAnalytics.overall_avg_prep_time} min</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-gray-400 text-sm">Orders Analyzed</p>
                    <p className="text-2xl font-bold text-white">{prepTimeAnalytics.total_orders_analyzed}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-gray-400 text-sm">Products Tracked</p>
                    <p className="text-2xl font-bold text-white">{prepTimeAnalytics.unique_products}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-gray-400 text-sm">Slow Items</p>
                    <p className="text-2xl font-bold text-yellow-400">{prepTimeAnalytics.slow_items_count}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                {loadingPrepTimes ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                  </div>
                ) : !prepTimeAnalytics || prepTimeAnalytics.prep_analytics.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 mx-auto text-gray-500 mb-2" />
                    <p className="text-gray-400">No prep time data available yet.</p>
                    <p className="text-gray-500 text-sm">Data is collected from completed orders.</p>
                    <Button
                      onClick={fetchPrepTimeAnalytics}
                      className="mt-4 bg-[#FF4500] hover:bg-[#FF5722]"
                    >
                      Load Analytics
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {prepTimeAnalytics.prep_analytics.map((item, idx) => (
                      <div 
                        key={idx} 
                        className={`p-4 rounded-lg border ${
                          item.is_slow 
                            ? 'bg-yellow-500/10 border-yellow-500/30' 
                            : item.efficiency_score === 'excellent' 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-medium">{item.product_name}</span>
                              <Badge variant="outline" className="border-white/20 text-gray-400 text-xs">
                                {item.category}
                              </Badge>
                              {item.is_slow && (
                                <Badge className="bg-yellow-500 text-black text-xs">SLOW</Badge>
                              )}
                              {item.efficiency_score === 'excellent' && (
                                <Badge className="bg-green-500 text-white text-xs">EXCELLENT</Badge>
                              )}
                              {item.efficiency_score === 'needs_improvement' && (
                                <Badge className="bg-red-500 text-white text-xs">NEEDS ATTENTION</Badge>
                              )}
                            </div>
                            <p className="text-gray-400 text-sm mt-1">
                              Orders: <span className="text-white">{item.order_count}</span>
                              {' • '}
                              Range: <span className="text-white">{item.min_prep_time_minutes} - {item.max_prep_time_minutes} min</span>
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`text-2xl font-bold ${
                              item.is_slow ? 'text-yellow-400' : 
                              item.efficiency_score === 'excellent' ? 'text-green-400' : 'text-white'
                            }`}>
                              {item.avg_prep_time_minutes} min
                            </p>
                            <p className="text-gray-500 text-xs">avg prep time</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Kitchen Load Section - Auto Prep Time Adjustment */}
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                  🔥 Live Kitchen Load
                </h3>
                <Button
                  onClick={fetchKitchenLoad}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/5"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingKitchenLoad ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              
              <p className="text-gray-400 text-sm mb-4">
                Real-time kitchen load affects prep time estimates. Higher load = longer estimated times for customers.
              </p>

              {kitchenLoad ? (
                <div className="space-y-4">
                  {/* Current Load Status */}
                  <Card className={`border-2 ${
                    kitchenLoad.current_load.load_level === 'light' ? 'bg-green-500/10 border-green-500/30' :
                    kitchenLoad.current_load.load_level === 'normal' ? 'bg-blue-500/10 border-blue-500/30' :
                    kitchenLoad.current_load.load_level === 'busy' ? 'bg-yellow-500/10 border-yellow-500/30' :
                    'bg-red-500/10 border-red-500/30'
                  }`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                          <div className="flex items-center gap-3">
                            <span className={`text-4xl font-bold ${
                              kitchenLoad.current_load.load_level === 'light' ? 'text-green-400' :
                              kitchenLoad.current_load.load_level === 'normal' ? 'text-blue-400' :
                              kitchenLoad.current_load.load_level === 'busy' ? 'text-yellow-400' :
                              'text-red-400'
                            }`}>
                              {kitchenLoad.current_load.active_orders}
                            </span>
                            <div>
                              <p className="text-white font-medium">Active Orders</p>
                              <Badge className={`${
                                kitchenLoad.current_load.load_level === 'light' ? 'bg-green-500' :
                                kitchenLoad.current_load.load_level === 'normal' ? 'bg-blue-500' :
                                kitchenLoad.current_load.load_level === 'busy' ? 'bg-yellow-500 text-black' :
                                'bg-red-500'
                              }`}>
                                {kitchenLoad.current_load.load_level.toUpperCase()} LOAD
                              </Badge>
                            </div>
                          </div>
                          <p className="text-gray-400 text-sm mt-2">
                            Prep time multiplier: <span className="text-white font-medium">{kitchenLoad.current_load.load_multiplier}x</span>
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div className="bg-white/5 rounded-lg p-3">
                            <p className="text-2xl font-bold text-orange-400">{kitchenLoad.current_load.breakdown.placed}</p>
                            <p className="text-gray-400 text-xs">Placed</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-3">
                            <p className="text-2xl font-bold text-blue-400">{kitchenLoad.current_load.breakdown.accepted}</p>
                            <p className="text-gray-400 text-xs">Accepted</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-3">
                            <p className="text-2xl font-bold text-yellow-400">{kitchenLoad.current_load.breakdown.preparing}</p>
                            <p className="text-gray-400 text-xs">Cooking</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Load Multiplier Explanation */}
                  <Card className="bg-[#121212] border-white/10">
                    <CardContent className="p-4">
                      <h4 className="text-white font-medium mb-3">📊 How Auto Prep Time Works</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/30">
                          <p className="text-green-400 font-medium">Light (0-3)</p>
                          <p className="text-gray-400">1.0x multiplier</p>
                          <p className="text-xs text-gray-500">Base prep time</p>
                        </div>
                        <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/30">
                          <p className="text-blue-400 font-medium">Normal (4-7)</p>
                          <p className="text-gray-400">1.15x multiplier</p>
                          <p className="text-xs text-gray-500">+15% prep time</p>
                        </div>
                        <div className="bg-yellow-500/10 rounded-lg p-3 border border-yellow-500/30">
                          <p className="text-yellow-400 font-medium">Busy (8-12)</p>
                          <p className="text-gray-400">1.35x multiplier</p>
                          <p className="text-xs text-gray-500">+35% prep time</p>
                        </div>
                        <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                          <p className="text-red-400 font-medium">Rush (13+)</p>
                          <p className="text-gray-400">1.6x multiplier</p>
                          <p className="text-xs text-gray-500">+60% prep time</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* 24-Hour Load History */}
                  <Card className="bg-[#121212] border-white/10">
                    <CardContent className="p-4">
                      <h4 className="text-white font-medium mb-3">📈 24-Hour Load History</h4>
                      <div className="flex gap-1 overflow-x-auto pb-2">
                        {kitchenLoad.load_history.map((hour, idx) => (
                          <div key={idx} className="flex flex-col items-center min-w-[40px]">
                            <div 
                              className={`w-8 rounded-t transition-all ${
                                hour.load_level === 'light' ? 'bg-green-500' :
                                hour.load_level === 'normal' ? 'bg-blue-500' :
                                hour.load_level === 'busy' ? 'bg-yellow-500' :
                                'bg-red-500'
                              }`}
                              style={{ height: `${Math.max(8, hour.orders * 8)}px` }}
                            />
                            <p className="text-gray-500 text-xs mt-1">{hour.hour}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-8 text-center">
                    <Clock className="w-12 h-12 mx-auto text-gray-500 mb-2" />
                    <p className="text-gray-400">Click &quot;Refresh&quot; to load kitchen data</p>
                    <Button
                      onClick={fetchKitchenLoad}
                      className="mt-4 bg-[#FF4500] hover:bg-[#FF5722]"
                    >
                      Load Kitchen Data
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Contact Settings Tab - Admin Only */}
          {staffRole === 'admin' && (
            <TabsContent value="contact">
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-6 space-y-6">
                  <h3 className="text-xl font-bold text-white mb-4">Contact Settings</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-400 text-sm mb-1 block">Phone Number</Label>
                      <Input
                        value={contactSettings.contact_phone}
                        onChange={(e) => setContactSettings({...contactSettings, contact_phone: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="+91 9876543210"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm mb-1 block">WhatsApp Number</Label>
                      <Input
                        value={contactSettings.contact_whatsapp}
                        onChange={(e) => setContactSettings({...contactSettings, contact_whatsapp: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="+91 9876543210"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm mb-1 block">Email</Label>
                      <Input
                        value={contactSettings.contact_email}
                        onChange={(e) => setContactSettings({...contactSettings, contact_email: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="support@fishonfire.com"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm mb-1 block">Operating Hours</Label>
                      <Input
                        value={contactSettings.operating_hours}
                        onChange={(e) => setContactSettings({...contactSettings, operating_hours: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="10 AM - 12 AM"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleSaveContact}
                    disabled={savingContact}
                    className="bg-[#FF4500] hover:bg-[#FF5722] text-white"
                  >
                    {savingContact ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Contact Settings
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Feedback Tab - Admin Only */}
          {staffRole === 'admin' && (
            <TabsContent value="feedback">
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">Customer Feedback ({feedbackList.length})</h3>
                  
                  {feedbackList.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No feedback received yet</p>
                  ) : (
                    <div className="space-y-4 max-h-[500px] overflow-y-auto">
                      {feedbackList.map((fb) => (
                        <div key={fb.id} className="bg-white/5 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">{fb.customer_name || 'Anonymous'}</span>
                            <div className="flex items-center gap-1">
                              {[1,2,3,4,5].map(star => (
                                <Star 
                                  key={star} 
                                  className={`w-4 h-4 ${star <= fb.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`} 
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-gray-300 text-sm">{fb.feedback_text}</p>
                          <p className="text-gray-500 text-xs mt-2">
                            {new Date(fb.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Support Tickets Tab - All Staff Can View */}
          <TabsContent value="tickets">
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">Support Tickets ({ticketsList.length})</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">🔔 Notifications</span>
                    <Switch 
                      checked={soundEnabled}
                      onCheckedChange={(checked) => {
                        setSoundEnabled(checked);
                        localStorage.setItem('adminTicketSound', checked.toString());
                        toast.success(checked ? 'Ticket notifications enabled' : 'Ticket notifications disabled');
                      }}
                    />
                  </div>
                </div>
                  
                  {ticketsList.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No support tickets</p>
                  ) : (
                    <div className="space-y-4 max-h-[500px] overflow-y-auto">
                      {ticketsList.map((ticket) => (
                        <div key={ticket.id} className="bg-white/5 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">{ticket.subject}</span>
                            <Badge className={
                              ticket.status === 'open' ? 'bg-yellow-500/20 text-yellow-500' :
                              ticket.status === 'in_progress' ? 'bg-blue-500/20 text-blue-500' :
                              ticket.status === 'resolved' ? 'bg-green-500/20 text-green-500' :
                              'bg-gray-500/20 text-gray-500'
                            }>
                              {ticket.status}
                            </Badge>
                          </div>
                          <p className="text-gray-300 text-sm mb-2">{ticket.description}</p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-1">
                            <span className="bg-white/5 px-2 py-0.5 rounded">Order: {ticket.order_id?.slice(0, 8)}...</span>
                            <span className="bg-white/5 px-2 py-0.5 rounded">
                              Created: {ticket.created_at ? new Date(ticket.created_at).toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'N/A'}
                            </span>
                            {ticket.outlet_id && (
                              <span className="bg-[#FF4500]/10 text-[#FF4500] px-2 py-0.5 rounded">
                                📍 {outlets.find(o => o.id === ticket.outlet_id)?.name || 'Unknown Outlet'}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>Ticket ID: {ticket.id?.slice(0, 8)}...</span>
                            <span>{ticket.customer_name} - {ticket.customer_phone}</span>
                          </div>
                          {ticket.admin_response && (
                            <div className="mt-2 p-2 bg-green-500/10 rounded text-sm text-green-400">
                              <strong>Response:</strong> {ticket.admin_response}
                            </div>
                          )}
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-white border-white/20"
                              onClick={() => setSelectedTicket(ticket)}
                            >
                              Respond
                            </Button>
                            {ticket.status !== 'resolved' && (
                              <Button 
                                size="sm"
                                className="bg-green-500 hover:bg-green-600"
                                onClick={() => handleUpdateTicket(ticket.id, 'resolved', ticket.admin_response || 'Issue resolved')}
                              >
                                Mark Resolved
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

          {/* Reports Tab */}
          {staffRole === 'admin' && (
            <TabsContent value="reports">
              <div className="space-y-6">
                {/* Report Controls */}
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={reportPeriod === 'today' ? 'default' : 'outline'}
                          onClick={() => handlePeriodChange('today')}
                          className={reportPeriod === 'today' ? 'bg-[#FF4500]' : 'border-white/20 text-white'}
                        >
                          Today
                        </Button>
                        <Button
                          size="sm"
                          variant={reportPeriod === 'week' ? 'default' : 'outline'}
                          onClick={() => handlePeriodChange('week')}
                          className={reportPeriod === 'week' ? 'bg-[#FF4500]' : 'border-white/20 text-white'}
                        >
                          Week
                        </Button>
                        <Button
                          size="sm"
                          variant={reportPeriod === 'month' ? 'default' : 'outline'}
                          onClick={() => handlePeriodChange('month')}
                          className={reportPeriod === 'month' ? 'bg-[#FF4500]' : 'border-white/20 text-white'}
                        >
                          Month
                        </Button>
                        <Button
                          size="sm"
                          variant={reportPeriod === 'quarter' ? 'default' : 'outline'}
                          onClick={() => handlePeriodChange('quarter')}
                          className={reportPeriod === 'quarter' ? 'bg-[#FF4500]' : 'border-white/20 text-white'}
                        >
                          Quarter
                        </Button>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Input
                          type="date"
                          value={reportDateRange.start}
                          onChange={(e) => {
                            setReportDateRange({...reportDateRange, start: e.target.value});
                            setReportPeriod('custom');
                          }}
                          className="bg-white/5 border-white/10 text-white w-[140px]"
                        />
                        <span className="text-gray-400">to</span>
                        <Input
                          type="date"
                          value={reportDateRange.end}
                          onChange={(e) => {
                            setReportDateRange({...reportDateRange, end: e.target.value});
                            setReportPeriod('custom');
                          }}
                          className="bg-white/5 border-white/10 text-white w-[140px]"
                        />
                      </div>
                    </div>
                    
                    {/* Outlet Selection for Reports */}
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <span className="text-gray-400 text-sm">Select Outlets:</span>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant={reportOutlets.length === 0 ? 'default' : 'outline'}
                          onClick={() => setReportOutlets([])}
                          className={reportOutlets.length === 0 ? 'bg-[#FF4500]' : 'border-white/20 text-white'}
                        >
                          All Outlets
                        </Button>
                        {outlets.map(outlet => (
                          <Button
                            key={outlet.id}
                            size="sm"
                            variant={reportOutlets.includes(outlet.id) ? 'default' : 'outline'}
                            onClick={() => {
                              if (reportOutlets.includes(outlet.id)) {
                                setReportOutlets(reportOutlets.filter(id => id !== outlet.id));
                              } else {
                                setReportOutlets([...reportOutlets, outlet.id]);
                              }
                            }}
                            className={reportOutlets.includes(outlet.id) ? 'bg-[#00E5FF] text-black' : 'border-white/20 text-white'}
                          >
                            {outlet.name}
                          </Button>
                        ))}
                      </div>
                      {reportOutlets.length > 0 && (
                        <span className="text-xs text-gray-500">
                          ({reportOutlets.length} outlet{reportOutlets.length > 1 ? 's' : ''} selected)
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4">
                      <Button
                        onClick={() => { fetchSalesReport(); fetchInventoryReport(); fetchAddonReport(); }}
                        className="bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-black"
                        disabled={loadingReports}
                      >
                        {loadingReports ? 'Loading...' : 'Generate Report'}
                      </Button>
                      
                      <div className="ml-auto flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadExcelReport('sales')}
                          className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                        >
                          📥 Sales Excel
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadExcelReport('inventory')}
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        >
                          📥 Inventory Excel
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadExcelReport('addons')}
                          className="border-[#FF4500]/50 text-[#FF4500] hover:bg-[#FF4500]/10"
                        >
                          📥 Add-ons Excel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {salesReport && (
                  <>
                    {/* Outlet-wise Columnar Sales Report Table */}
                    <Card className="bg-[#121212] border-white/10 overflow-hidden">
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-[#1a1a1a] border-b border-white/10">
                                <th className="text-left p-4 text-gray-400 font-medium sticky left-0 bg-[#1a1a1a] min-w-[180px]">
                                  Metric
                                </th>
                                {/* Individual Outlet Columns */}
                                {salesReport.outlet_breakdown && Object.entries(salesReport.outlet_breakdown).map(([outletId, data]) => (
                                  <th key={outletId} className="text-center p-4 min-w-[140px] border-l border-white/10">
                                    <span className="text-[#00E5FF] font-bold block">{data.outlet_name}</span>
                                    <span className="text-gray-500 text-xs">{outletId.slice(0, 8)}...</span>
                                  </th>
                                ))}
                                {/* If no outlet breakdown, show all outlets from state */}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map(outlet => (
                                  <th key={outlet.id} className="text-center p-4 min-w-[140px] border-l border-white/10">
                                    <span className="text-[#00E5FF] font-bold block">{outlet.name}</span>
                                  </th>
                                ))}
                                {/* Consolidated Column */}
                                <th className="text-center p-4 min-w-[160px] border-l-2 border-[#FF4500]/50 bg-[#FF4500]/10">
                                  <span className="text-[#FF4500] font-bold block">CONSOLIDATED</span>
                                  <span className="text-gray-400 text-xs">All Outlets</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Sales Section Header */}
                              <tr className="bg-green-500/10">
                                <td colSpan={100} className="p-2 text-green-400 font-bold text-xs uppercase tracking-wider">
                                  💰 Sales Metrics
                                </td>
                              </tr>
                              
                              {/* Total Sales */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">Total Sales</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-green-400 font-mono border-l border-white/5">
                                    ₹{data.summary.total_sales.toLocaleString()}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-green-400 font-bold font-mono border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  ₹{salesReport.summary.total_sales.toLocaleString()}
                                </td>
                              </tr>
                              
                              {/* Billable Orders */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">Billable Orders</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-blue-400 border-l border-white/5">
                                    {data.summary.billable_orders}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-blue-400 font-bold border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  {salesReport.summary.billable_orders}
                                </td>
                              </tr>
                              
                              {/* Total Orders */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">Total Orders</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-white border-l border-white/5">
                                    {data.summary.total_orders}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-white font-bold border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  {salesReport.summary.total_orders}
                                </td>
                              </tr>
                              
                              {/* Average Order Value */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">Avg Order Value</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-purple-400 font-mono border-l border-white/5">
                                    ₹{data.summary.average_order_value.toLocaleString()}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-purple-400 font-bold font-mono border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  ₹{salesReport.summary.average_order_value.toLocaleString()}
                                </td>
                              </tr>
                              
                              {/* Order Status Section Header */}
                              <tr className="bg-blue-500/10">
                                <td colSpan={100} className="p-2 text-blue-400 font-bold text-xs uppercase tracking-wider">
                                  📊 Order Status
                                </td>
                              </tr>
                              
                              {/* Completed Orders */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">Completed</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-green-400 border-l border-white/5">
                                    {data.summary.completed_orders}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-green-400 font-bold border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  {salesReport.summary.completed_orders}
                                </td>
                              </tr>
                              
                              {/* Pending Orders */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">Pending</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-yellow-400 border-l border-white/5">
                                    {data.summary.pending_orders}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-yellow-400 font-bold border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  {salesReport.summary.pending_orders}
                                </td>
                              </tr>
                              
                              {/* Cancelled Orders */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">Cancelled</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-red-400 border-l border-white/5">
                                    {data.summary.cancelled_orders}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-red-400 font-bold border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  {salesReport.summary.cancelled_orders}
                                </td>
                              </tr>
                              
                              {/* Payment Section Header */}
                              <tr className="bg-purple-500/10">
                                <td colSpan={100} className="p-2 text-purple-400 font-bold text-xs uppercase tracking-wider">
                                  💳 Payment Breakdown
                                </td>
                              </tr>
                              
                              {/* Cash */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">Cash</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-green-400 font-mono border-l border-white/5">
                                    ₹{data.payment_breakdown.cash.toLocaleString()}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-green-400 font-bold font-mono border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  ₹{salesReport.payment_breakdown.cash.toLocaleString()}
                                </td>
                              </tr>
                              
                              {/* UPI */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">UPI</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-blue-400 font-mono border-l border-white/5">
                                    ₹{data.payment_breakdown.upi.toLocaleString()}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-blue-400 font-bold font-mono border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  ₹{salesReport.payment_breakdown.upi.toLocaleString()}
                                </td>
                              </tr>
                              
                              {/* Card */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">Card</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-purple-400 font-mono border-l border-white/5">
                                    ₹{data.payment_breakdown.card.toLocaleString()}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-purple-400 font-bold font-mono border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  ₹{salesReport.payment_breakdown.card.toLocaleString()}
                                </td>
                              </tr>
                              
                              {/* COD */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">COD</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-yellow-400 font-mono border-l border-white/5">
                                    ₹{data.payment_breakdown.cod.toLocaleString()}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-yellow-400 font-bold font-mono border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  ₹{salesReport.payment_breakdown.cod.toLocaleString()}
                                </td>
                              </tr>
                              
                              {/* Order Types Section Header */}
                              <tr className="bg-orange-500/10">
                                <td colSpan={100} className="p-2 text-orange-400 font-bold text-xs uppercase tracking-wider">
                                  📦 Order Types
                                </td>
                              </tr>
                              
                              {/* Delivery */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">🚚 Delivery</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-white border-l border-white/5">
                                    {data.order_types.delivery}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-white font-bold border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  {salesReport.order_types.delivery}
                                </td>
                              </tr>
                              
                              {/* Pickup */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">🏪 Pickup</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-white border-l border-white/5">
                                    {data.order_types.pickup}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-white font-bold border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  {salesReport.order_types.pickup}
                                </td>
                              </tr>
                              
                              {/* Dine-in */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">🍽️ Dine-in</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-white border-l border-white/5">
                                    {data.order_types.dine_in}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-white font-bold border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  {salesReport.order_types.dine_in}
                                </td>
                              </tr>
                              
                              {/* POS */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">💻 POS</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-white border-l border-white/5">
                                    {data.order_types.pos}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-white font-bold border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  {salesReport.order_types.pos}
                                </td>
                              </tr>
                              
                              {/* Customer Section Header */}
                              <tr className="bg-cyan-500/10">
                                <td colSpan={100} className="p-2 text-cyan-400 font-bold text-xs uppercase tracking-wider">
                                  👥 Customer Analytics
                                </td>
                              </tr>
                              
                              {/* Unique Customers */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">Unique Customers</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-white border-l border-white/5">
                                    {data.customers.total_unique}
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-white font-bold border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  {salesReport.customers.total_unique}
                                </td>
                              </tr>
                              
                              {/* Repeat Rate */}
                              <tr className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-3 text-white font-medium sticky left-0 bg-[#121212]">Repeat Rate</td>
                                {salesReport.outlet_breakdown && Object.values(salesReport.outlet_breakdown).map((data, idx) => (
                                  <td key={idx} className="p-3 text-center text-yellow-400 border-l border-white/5">
                                    {data.customers.repeat_percentage}%
                                  </td>
                                ))}
                                {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && outlets.map((outlet, idx) => (
                                  <td key={idx} className="p-3 text-center text-gray-500 border-l border-white/5">-</td>
                                ))}
                                <td className="p-3 text-center text-yellow-400 font-bold border-l-2 border-[#FF4500]/50 bg-[#FF4500]/5">
                                  {salesReport.customers.repeat_percentage}%
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Note about outlet selection */}
                    {(!salesReport.outlet_breakdown || Object.keys(salesReport.outlet_breakdown).length === 0) && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                        <p className="text-blue-400 text-sm">
                          💡 Select specific outlets above to see individual outlet data in separate columns
                        </p>
                      </div>
                    )}

                    {/* Daily Sales Chart */}
                    <Card className="bg-[#121212] border-white/10">
                      <CardContent className="p-4">
                        <h4 className="text-white font-semibold mb-3">📈 Daily Sales Trend</h4>
                        <div className="overflow-x-auto">
                          <div className="flex items-end gap-1 h-[150px] min-w-[600px]">
                            {salesReport.daily_breakdown.map((day, idx) => {
                              const maxSales = Math.max(...salesReport.daily_breakdown.map(d => d.sales));
                              const height = maxSales > 0 ? (day.sales / maxSales) * 100 : 0;
                              return (
                                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                                  <span className="text-xs text-gray-400">₹{(day.sales/1000).toFixed(1)}k</span>
                                  <div 
                                    className="w-full bg-gradient-to-t from-[#FF4500] to-[#FF6B3D] rounded-t"
                                    style={{ height: `${Math.max(height, 5)}%` }}
                                    title={`${day.date}: ₹${day.sales.toLocaleString()} (${day.orders} orders)`}
                                  />
                                  <span className="text-[10px] text-gray-500 rotate-45 origin-left">{day.date.slice(5)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}

                {/* Inventory Report */}
                {inventoryReport && (
                  <Card className="bg-[#121212] border-white/10">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
                        <div>
                          <h4 className="text-white font-semibold">📦 Inventory Report (Cost-Based Valuation)</h4>
                          {inventoryReport.period?.is_sunday && (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mt-1">
                              ✓ Reconciliation Data Included (Sunday)
                            </Badge>
                          )}
                          {!inventoryReport.period?.is_sunday && (
                            <span className="text-gray-500 text-xs">System stock only - reconciliation done on Sundays</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm">
                          <span className="text-gray-400">Stock Value: <span className="text-green-400 font-bold">₹{inventoryReport.summary.total_stock_value.toLocaleString()}</span></span>
                          <span className="text-gray-400">Sold: <span className="text-blue-400 font-bold">₹{inventoryReport.summary.total_sold_value.toLocaleString()}</span></span>
                          <span className="text-gray-400">Wastage: <span className="text-red-400 font-bold">₹{inventoryReport.summary.total_wastage_value.toLocaleString()}</span></span>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-gray-400 border-b border-white/10">
                              <th className="text-left py-2">Product</th>
                              <th className="text-right py-2">Cost Price</th>
                              <th className="text-right py-2">Opening</th>
                              <th className="text-right py-2">Sold</th>
                              <th className="text-right py-2">Wastage</th>
                              <th className="text-right py-2">System Stock</th>
                              {inventoryReport.period?.is_sunday && (
                                <th className="text-right py-2 text-green-400">Reconciled</th>
                              )}
                              <th className="text-right py-2">Closing</th>
                              <th className="text-right py-2">Value</th>
                              <th className="text-right py-2">Diff</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inventoryReport.items.slice(0, 30).map((item, idx) => (
                              <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                                <td className="py-2 text-white">{item.product_name}</td>
                                <td className="py-2 text-right text-purple-400 font-mono">₹{(item.cost_price || item.price || 0).toLocaleString()}</td>
                                <td className="py-2 text-right text-gray-400">{item.opening_stock}</td>
                                <td className="py-2 text-right text-blue-400">{item.sold_qty}</td>
                                <td className="py-2 text-right text-red-400">{item.wastage_qty}</td>
                                <td className="py-2 text-right text-gray-300 font-mono">{item.system_closing_stock || item.closing_stock}</td>
                                {inventoryReport.period?.is_sunday && (
                                  <td className={`py-2 text-right font-mono ${item.reconciled_stock !== null ? 'text-green-400' : 'text-gray-500'}`}>
                                    {item.reconciled_stock !== null && item.reconciled_stock !== undefined ? item.reconciled_stock : '-'}
                                  </td>
                                )}
                                <td className="py-2 text-right text-white font-mono font-bold">{item.closing_stock}</td>
                                <td className="py-2 text-right text-green-400">₹{item.stock_value.toLocaleString()}</td>
                                <td className={`py-2 text-right ${item.difference !== 0 ? 'text-yellow-400 font-bold' : 'text-gray-500'}`}>
                                  {item.difference}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Outlet-wise Breakdown */}
                {salesReport?.outlet_breakdown && Object.keys(salesReport.outlet_breakdown).length > 0 && (
                  <Card className="bg-[#121212] border-white/10">
                    <CardContent className="p-4">
                      <h4 className="text-white font-semibold mb-4">🏪 Outlet-wise Breakdown</h4>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {Object.entries(salesReport.outlet_breakdown).map(([outletId, data]) => (
                          <Card key={outletId} className="bg-white/5 border-white/10">
                            <CardContent className="p-4">
                              <h5 className="text-[#00E5FF] font-semibold mb-3 text-lg">{data.outlet_name}</h5>
                              
                              {/* Summary Stats */}
                              <div className="grid grid-cols-3 gap-2 mb-4">
                                <div className="bg-black/30 rounded-lg p-2 text-center">
                                  <p className="text-xs text-gray-400">Total Sales</p>
                                  <p className="text-lg font-bold text-green-400">₹{data.summary.total_sales.toLocaleString()}</p>
                                </div>
                                <div className="bg-black/30 rounded-lg p-2 text-center">
                                  <p className="text-xs text-gray-400">Orders</p>
                                  <p className="text-lg font-bold text-white">{data.summary.billable_orders}</p>
                                </div>
                                <div className="bg-black/30 rounded-lg p-2 text-center">
                                  <p className="text-xs text-gray-400">Avg Order</p>
                                  <p className="text-lg font-bold text-white">₹{data.summary.average_order_value.toLocaleString()}</p>
                                </div>
                              </div>
                              
                              {/* Payment Breakdown */}
                              <div className="mb-3">
                                <p className="text-xs text-gray-400 mb-1">Payment Methods</p>
                                <div className="flex flex-wrap gap-2 text-xs">
                                  <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded">Cash: ₹{data.payment_breakdown.cash}</span>
                                  <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded">UPI: ₹{data.payment_breakdown.upi}</span>
                                  <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded">Card: ₹{data.payment_breakdown.card}</span>
                                  <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">COD: ₹{data.payment_breakdown.cod}</span>
                                </div>
                              </div>
                              
                              {/* Order Types */}
                              <div className="mb-3">
                                <p className="text-xs text-gray-400 mb-1">Order Types</p>
                                <div className="flex flex-wrap gap-2 text-xs">
                                  <span className="bg-white/10 text-white px-2 py-1 rounded">Delivery: {data.order_types.delivery}</span>
                                  <span className="bg-white/10 text-white px-2 py-1 rounded">Pickup: {data.order_types.pickup}</span>
                                  <span className="bg-white/10 text-white px-2 py-1 rounded">Dine-in: {data.order_types.dine_in}</span>
                                  <span className="bg-white/10 text-white px-2 py-1 rounded">POS: {data.order_types.pos}</span>
                                </div>
                              </div>
                              
                              {/* Order Status */}
                              <div className="flex flex-wrap gap-2 text-xs">
                                <span className="text-gray-400">Completed: <span className="text-green-400">{data.summary.completed_orders}</span></span>
                                <span className="text-gray-400">Pending: <span className="text-yellow-400">{data.summary.pending_orders}</span></span>
                                <span className="text-gray-400">Cancelled: <span className="text-red-400">{data.summary.cancelled_orders}</span></span>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Add-on Sales Analytics */}
                {addonReport && (
                  <Card className="bg-[#121212] border-white/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-bold text-[#FF4500] flex items-center gap-2">
                        🔥 Add-on & Customization Analytics
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {/* Summary Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white/5 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-[#FF4500]">₹{addonReport.summary?.total_addon_revenue?.toLocaleString() || 0}</p>
                          <p className="text-xs text-gray-400">Add-on Revenue</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-[#00E5FF]">{addonReport.summary?.total_addon_orders || 0}</p>
                          <p className="text-xs text-gray-400">Orders with Add-ons</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-white">{addonReport.summary?.unique_addons || 0}</p>
                          <p className="text-xs text-gray-400">Unique Add-ons Used</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-green-400">₹{addonReport.summary?.avg_addon_value?.toFixed(2) || 0}</p>
                          <p className="text-xs text-gray-400">Avg Add-on Value</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Top Add-ons */}
                        <div>
                          <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                            <span className="text-[#FF4500]">🔥</span> Top Finishes/Add-ons
                          </h4>
                          {addonReport.top_addons?.length > 0 ? (
                            <div className="space-y-2">
                              {addonReport.top_addons.map((addon, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[#FF4500] font-bold">#{idx + 1}</span>
                                    <span className="text-white">{addon.name}</span>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-[#00E5FF] font-bold">{addon.count}x</span>
                                    <span className="text-gray-400 text-xs ml-2">₹{addon.revenue}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No add-on data yet</p>
                          )}
                        </div>
                        
                        {/* Top Customizations */}
                        <div>
                          <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                            <span className="text-[#00E5FF]">⚡</span> Top Customizations
                          </h4>
                          {addonReport.top_customizations?.length > 0 ? (
                            <div className="space-y-2">
                              {addonReport.top_customizations.map((cust, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[#00E5FF] font-bold">#{idx + 1}</span>
                                    <span className="text-white text-sm">{cust.name}</span>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-[#00E5FF] font-bold">{cust.count}x</span>
                                    {cust.revenue > 0 && <span className="text-gray-400 text-xs ml-2">₹{cust.revenue}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No customization data yet</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Products with Most Add-ons */}
                      {addonReport.by_product?.length > 0 && (
                        <div className="mt-6">
                          <h4 className="text-white font-semibold mb-3">📦 Products with Most Add-ons</h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-gray-400 border-b border-white/10">
                                  <th className="text-left py-2">Product</th>
                                  <th className="text-center py-2">Add-ons</th>
                                  <th className="text-center py-2">Customizations</th>
                                  <th className="text-right py-2">Revenue</th>
                                </tr>
                              </thead>
                              <tbody>
                                {addonReport.by_product.map((prod, idx) => (
                                  <tr key={idx} className="border-b border-white/5">
                                    <td className="py-2 text-white">{prod.product}</td>
                                    <td className="py-2 text-center text-[#FF4500]">{prod.addons}</td>
                                    <td className="py-2 text-center text-[#00E5FF]">{prod.customizations}</td>
                                    <td className="py-2 text-right text-green-400">₹{prod.revenue}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {!salesReport && !loadingReports && (
                  <Card className="bg-[#121212] border-white/10">
                    <CardContent className="p-8 text-center">
                      <p className="text-gray-400">Select a date range and click &quot;Generate Report&quot; to view analytics</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}

          {/* Invoice Settings Tab */}
          {staffRole === 'admin' && (
            <TabsContent value="invoice">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Company Details Card */}
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-4">Company Details</h3>
                    <p className="text-gray-400 text-sm mb-4">These details will appear on all invoices</p>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-white">Company Name</Label>
                        <Input
                          value={companySettings.company_name}
                          onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="Fish on Fire"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-white">Company Address</Label>
                        <Input
                          value={companySettings.company_address}
                          onChange={(e) => setCompanySettings({...companySettings, company_address: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="123 Street, City, State - PIN"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-white">Phone</Label>
                          <Input
                            value={companySettings.company_phone}
                            onChange={(e) => setCompanySettings({...companySettings, company_phone: e.target.value})}
                            className="bg-white/5 border-white/10 text-white"
                            placeholder="+91 98765 43210"
                          />
                        </div>
                        <div>
                          <Label className="text-white">Email</Label>
                          <Input
                            value={companySettings.company_email}
                            onChange={(e) => setCompanySettings({...companySettings, company_email: e.target.value})}
                            className="bg-white/5 border-white/10 text-white"
                            placeholder="contact@fishonfire.com"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-white">GST Number</Label>
                          <Input
                            value={companySettings.gst_number}
                            onChange={(e) => setCompanySettings({...companySettings, gst_number: e.target.value})}
                            className="bg-white/5 border-white/10 text-white"
                            placeholder="29ABCDE1234F1Z5"
                          />
                        </div>
                        <div>
                          <Label className="text-white">PAN Number</Label>
                          <Input
                            value={companySettings.pan_number}
                            onChange={(e) => setCompanySettings({...companySettings, pan_number: e.target.value})}
                            className="bg-white/5 border-white/10 text-white"
                            placeholder="ABCDE1234F"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-white">FSSAI Number</Label>
                        <Input
                          value={companySettings.fssai_number}
                          onChange={(e) => setCompanySettings({...companySettings, fssai_number: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="12345678901234"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-white">Invoice Footer Message</Label>
                        <Input
                          value={companySettings.invoice_footer}
                          onChange={(e) => setCompanySettings({...companySettings, invoice_footer: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="Thank you for your order!"
                        />
                      </div>
                      
                      <Button
                        onClick={handleSaveCompanySettings}
                        disabled={savingCompanySettings}
                        className="w-full bg-[#FF4500] hover:bg-[#FF4500]/80"
                      >
                        {savingCompanySettings ? 'Saving...' : 'Save Company Details'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Invoice Settings Card */}
                <Card className="bg-[#121212] border-white/10">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-4">Invoice Settings</h3>
                    <p className="text-gray-400 text-sm mb-4">Configure invoice layout and printing options</p>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-white">Paper Width</Label>
                        <Select 
                          value={invoiceSettings.paper_width} 
                          onValueChange={(value) => setInvoiceSettings({...invoiceSettings, paper_width: value})}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#121212] border-white/10">
                            <SelectItem value="58mm">58mm (2-inch) - Small Thermal</SelectItem>
                            <SelectItem value="80mm">80mm (3-inch) - Standard Thermal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label className="text-white">Print Copies</Label>
                        <Select 
                          value={invoiceSettings.print_copies.toString()} 
                          onValueChange={(value) => setInvoiceSettings({...invoiceSettings, print_copies: parseInt(value)})}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#121212] border-white/10">
                            <SelectItem value="1">1 Copy</SelectItem>
                            <SelectItem value="2">2 Copies (Customer + Kitchen)</SelectItem>
                            <SelectItem value="3">3 Copies</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-3">
                        <Label className="text-white block">Display Options</Label>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Show GST Details</span>
                          <Switch
                            checked={invoiceSettings.show_gst}
                            onCheckedChange={(checked) => setInvoiceSettings({...invoiceSettings, show_gst: checked})}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Show FSSAI Number</span>
                          <Switch
                            checked={invoiceSettings.show_fssai}
                            onCheckedChange={(checked) => setInvoiceSettings({...invoiceSettings, show_fssai: checked})}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Show Customer Details</span>
                          <Switch
                            checked={invoiceSettings.show_customer_details}
                            onCheckedChange={(checked) => setInvoiceSettings({...invoiceSettings, show_customer_details: checked})}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Auto Print POS Orders</span>
                          <Switch
                            checked={invoiceSettings.auto_print_pos}
                            onCheckedChange={(checked) => setInvoiceSettings({...invoiceSettings, auto_print_pos: checked})}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Auto Print Online Orders</span>
                          <Switch
                            checked={invoiceSettings.auto_print_online}
                            onCheckedChange={(checked) => setInvoiceSettings({...invoiceSettings, auto_print_online: checked})}
                          />
                        </div>
                      </div>
                      
                      <Button
                        onClick={handleSaveInvoiceSettings}
                        disabled={savingInvoiceSettings}
                        className="w-full bg-[#FF4500] hover:bg-[#FF4500]/80"
                      >
                        {savingInvoiceSettings ? 'Saving...' : 'Save Invoice Settings'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Invoice Numbering Info */}
                <Card className="bg-[#121212] border-white/10 lg:col-span-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-4">Invoice Numbering</h3>
                    <div className="bg-white/5 rounded-lg p-4">
                      <p className="text-gray-300 mb-3">
                        Invoice numbers are generated automatically based on outlet branch code:
                      </p>
                      <div className="bg-[#050505] rounded p-3 font-mono text-sm">
                        <span className="text-[#FF4500]">[BranchCode]</span>
                        <span className="text-[#00E5FF]">[Month]</span>
                        <span className="text-green-400">[Date]</span>
                        <span className="text-yellow-400">[Sequence]</span>
                      </div>
                      <p className="text-gray-400 text-sm mt-3">
                        Example: <span className="text-white font-mono">QK101001</span> = 
                        <span className="text-[#FF4500]"> QK1</span> (branch) +
                        <span className="text-[#00E5FF]"> 1</span> (Jan) +
                        <span className="text-green-400"> 01</span> (day 1) +
                        <span className="text-yellow-400"> 001</span> (first order)
                      </p>
                      <p className="text-gray-500 text-xs mt-2">
                        Set branch codes in Outlets tab. Sequence resets daily.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

        </Tabs>
      </div>

      {/* Dialogs remain the same - Add Product, Edit Product, Add Outlet, Edit Outlet, QR Code */}
      {/* (Keeping the existing dialog code from previous version) */}
      
      {/* Add Product Dialog */}
      <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Add New Product
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-white mb-2 block">Product Name *</Label>
              <Input
                id="name"
                value={newProduct.name}
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Grilled Fish Burger"
              />
            </div>
            <div>
              <Label htmlFor="description" className="text-white mb-2 block">Description *</Label>
              <Input
                id="description"
                value={newProduct.description}
                onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Flame-grilled fish with spicy mayo"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price" className="text-white mb-2 block">Price *</Label>
                <Input
                  id="price"
                  type="number"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="199"
                />
              </div>
              <div>
                <Label htmlFor="stock" className="text-white mb-2 block">Stock *</Label>
                <Input
                  id="stock"
                  type="number"
                  value={newProduct.stock}
                  onChange={(e) => setNewProduct({...newProduct, stock: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="100"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="category" className="text-white mb-2 block">Category *</Label>
              <Select 
                value={newProduct.category} 
                onValueChange={(value) => setNewProduct({...newProduct, category: value})}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color || '#FF4500' }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white mb-2 block">Tags</Label>
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => {
                      const newTags = newProduct.tags?.includes(tag.name)
                        ? newProduct.tags.filter(t => t !== tag.name)
                        : [...(newProduct.tags || []), tag.name];
                      setNewProduct({...newProduct, tags: newTags});
                    }}
                    className={`px-3 py-1 rounded-full text-sm transition-all ${
                      newProduct.tags?.includes(tag.name)
                        ? 'ring-2 ring-white'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    style={{ backgroundColor: tag.color, color: '#000' }}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Add-on Only Checkbox */}
            <div className="flex items-center gap-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <input
                type="checkbox"
                id="is_addon_only"
                checked={newProduct.is_addon_only}
                onChange={(e) => setNewProduct({...newProduct, is_addon_only: e.target.checked})}
                className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-[#FF4500] focus:ring-[#FF4500]"
              />
              <div>
                <Label htmlFor="is_addon_only" className="text-white cursor-pointer">Add-on Only Product</Label>
                <p className="text-yellow-400 text-xs mt-0.5">Hidden from customer menu, only available as an add-on selection (visible in POS)</p>
              </div>
            </div>
            
            {/* Add-ons Section */}
            <div className="bg-white/5 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-white">Add-ons (Finishes)</Label>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    const addons = newProduct.addons || [];
                    setNewProduct({...newProduct, addons: [...addons, {name: '', price: ''}]});
                  }}
                  className="bg-[#FF4500]/20 text-[#FF4500] hover:bg-[#FF4500]/30 text-xs"
                >
                  + Add
                </Button>
              </div>
              <p className="text-gray-500 text-xs">E.g., Peri-Peri (+₹20), Butter Garlic (+₹30)</p>
              {(newProduct.addons || []).map((addon, idx) => (
                <div key={idx} className="flex flex-col gap-2 bg-white/5 rounded-lg p-2">
                  <div className="flex gap-2 items-center">
                    <Input
                      placeholder="Name (e.g., Peri-Peri)"
                      value={addon.name}
                      onChange={(e) => {
                        const addons = [...(newProduct.addons || [])];
                        addons[idx] = {...addons[idx], name: e.target.value};
                        setNewProduct({...newProduct, addons});
                      }}
                      className="bg-white/5 border-white/10 text-white text-sm flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="₹"
                      value={addon.price}
                      onChange={(e) => {
                        const addons = [...(newProduct.addons || [])];
                        addons[idx] = {...addons[idx], price: parseFloat(e.target.value) || 0};
                        setNewProduct({...newProduct, addons});
                      }}
                      className="bg-white/5 border-white/10 text-white text-sm w-20"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const addons = (newProduct.addons || []).filter((_, i) => i !== idx);
                        setNewProduct({...newProduct, addons});
                      }}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20 p-1"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Ingredient Deduction (Optional) */}
                  <div className="flex items-center gap-2 pl-2">
                    <span className="text-gray-500 text-xs">📦 Deduct from:</span>
                    <select
                      value={addon.ingredient_deductions?.[0]?.product_id || ''}
                      onChange={(e) => {
                        const addons = [...(newProduct.addons || [])];
                        if (e.target.value) {
                          addons[idx] = {...addons[idx], ingredient_deductions: [{product_id: e.target.value, quantity: 1}]};
                        } else {
                          addons[idx] = {...addons[idx], ingredient_deductions: []};
                        }
                        setNewProduct({...newProduct, addons});
                      }}
                      className="bg-[#1A1A1A] border border-white/10 text-white text-xs rounded px-2 py-1 flex-1"
                    >
                      <option value="">None (no stock deduction)</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Customization Groups Section */}
            <div className="bg-white/5 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-white">Customization Options</Label>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    const groups = newProduct.customization_groups || [];
                    setNewProduct({...newProduct, customization_groups: [...groups, {name: '', required: false, options: [{name: '', price: 0}]}]});
                  }}
                  className="bg-[#00E5FF]/20 text-[#00E5FF] hover:bg-[#00E5FF]/30 text-xs"
                >
                  + Add Group
                </Button>
              </div>
              <p className="text-gray-500 text-xs">E.g., &quot;Choose Masala&quot; with options like Red Masala, Green Masala</p>
              {(newProduct.customization_groups || []).map((group, gIdx) => (
                <div key={gIdx} className="border border-white/10 rounded-lg p-3 space-y-2">
                  <div className="flex gap-2 items-center">
                    <Input
                      placeholder="Group name (e.g., Choose Masala)"
                      value={group.name}
                      onChange={(e) => {
                        const groups = [...(newProduct.customization_groups || [])];
                        groups[gIdx] = {...groups[gIdx], name: e.target.value};
                        setNewProduct({...newProduct, customization_groups: groups});
                      }}
                      className="bg-white/5 border-white/10 text-white text-sm flex-1"
                    />
                    <label className="flex items-center gap-1 text-xs text-gray-400">
                      <input
                        type="checkbox"
                        checked={group.required}
                        onChange={(e) => {
                          const groups = [...(newProduct.customization_groups || [])];
                          groups[gIdx] = {...groups[gIdx], required: e.target.checked};
                          setNewProduct({...newProduct, customization_groups: groups});
                        }}
                        className="w-3 h-3"
                      />
                      Required
                    </label>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const groups = (newProduct.customization_groups || []).filter((_, i) => i !== gIdx);
                        setNewProduct({...newProduct, customization_groups: groups});
                      }}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20 p-1"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="pl-3 space-y-1">
                    {(group.options || []).map((opt, oIdx) => (
                      <div key={oIdx} className="flex gap-2 items-center">
                        <span className="text-gray-500 text-xs">↳</span>
                        <Input
                          placeholder="Option (e.g., Red Masala)"
                          value={opt.name}
                          onChange={(e) => {
                            const groups = [...(newProduct.customization_groups || [])];
                            groups[gIdx].options[oIdx] = {...groups[gIdx].options[oIdx], name: e.target.value};
                            setNewProduct({...newProduct, customization_groups: groups});
                          }}
                          className="bg-white/5 border-white/10 text-white text-xs flex-1"
                        />
                        <Input
                          type="number"
                          placeholder="₹0"
                          value={opt.price || 0}
                          onChange={(e) => {
                            const groups = [...(newProduct.customization_groups || [])];
                            groups[gIdx].options[oIdx] = {...groups[gIdx].options[oIdx], price: parseFloat(e.target.value) || 0};
                            setNewProduct({...newProduct, customization_groups: groups});
                          }}
                          className="bg-white/5 border-white/10 text-white text-xs w-16"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const groups = [...(newProduct.customization_groups || [])];
                            groups[gIdx].options = groups[gIdx].options.filter((_, i) => i !== oIdx);
                            setNewProduct({...newProduct, customization_groups: groups});
                          }}
                          className="text-red-400 hover:text-red-300 p-0"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const groups = [...(newProduct.customization_groups || [])];
                        groups[gIdx].options = [...(groups[gIdx].options || []), {name: '', price: 0}];
                        setNewProduct({...newProduct, customization_groups: groups});
                      }}
                      className="text-[#00E5FF] hover:text-[#00E5FF]/80 text-xs pl-5"
                    >
                      + Add Option
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Tax Settings */}
            <div className="bg-white/5 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-white">GST (Govt. Tax)</Label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setNewProduct({...newProduct, tax_enabled: !newProduct.tax_enabled})}
                    className={`relative w-12 h-6 rounded-full transition-colors ${newProduct.tax_enabled ? 'bg-[#FF4500]' : 'bg-gray-600'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${newProduct.tax_enabled ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>
              </div>
              {newProduct.tax_enabled && (
                <div>
                  <Label className="text-gray-400 text-sm mb-1 block">Tax Percentage (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.tax_percentage}
                    onChange={(e) => setNewProduct({...newProduct, tax_percentage: e.target.value})}
                    className="bg-white/5 border-white/10 text-white w-24"
                    placeholder="5"
                  />
                </div>
              )}
              
              <div>
                <Label className="text-gray-400 text-sm mb-1 block">Packaging Fee (₹)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={newProduct.packaging_fee}
                  onChange={(e) => setNewProduct({...newProduct, packaging_fee: e.target.value})}
                  className="bg-white/5 border-white/10 text-white w-24"
                  placeholder="0"
                />
                <p className="text-gray-500 text-xs mt-1">Charged per item in cart</p>
              </div>
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Product Image *</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={(e) => handleFileSelect(e, false)}
                className="hidden"
                data-testid="product-image-input"
              />
              
              {/* Image Preview or Upload/URL Options */}
              {newProduct.image || imagePreview ? (
                <div className="relative rounded-lg overflow-hidden bg-black/20">
                  <img 
                    src={getImageUrl(newProduct.image || imagePreview)} 
                    alt="Product preview" 
                    className="w-full h-40 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden w-full h-40 items-center justify-center bg-red-900/20 text-red-400 text-sm">
                    Image failed to load - check URL
                  </div>
                  <div className="absolute top-2 right-2 flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-white/90 hover:bg-white text-black"
                      disabled={uploadingImage}
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => clearImage(false)}
                      className="bg-red-500/90 hover:bg-red-500 text-white"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Upload Button */}
                  <div
                    onClick={() => !uploadingImage && fileInputRef.current?.click()}
                    className={`border-2 border-dashed border-white/20 rounded-lg p-4 text-center cursor-pointer hover:border-[#FF4500]/50 hover:bg-white/5 transition-all ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {uploadingImage ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-6 h-6 border-2 border-[#FF4500] border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-gray-400 text-sm">Uploading...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <Upload className="w-5 h-5 text-gray-500" />
                        <span className="text-gray-400 text-sm">Upload image (max 5MB)</span>
                      </div>
                    )}
                  </div>
                  
                  {/* OR Divider */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-white/10"></div>
                    <span className="text-gray-500 text-xs">OR</span>
                    <div className="flex-1 h-px bg-white/10"></div>
                  </div>
                  
                  {/* URL Input */}
                  <Input
                    placeholder="Paste image URL (https://...)"
                    value={newProduct.image || ''}
                    onChange={(e) => setNewProduct({...newProduct, image: e.target.value})}
                    className="bg-white/5 border-white/10 text-white text-sm"
                    data-testid="product-image-url"
                  />
                </div>
              )}
            </div>
            <Button
              onClick={handleAddProduct}
              className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white py-6 text-lg font-bold"
              data-testid="submit-product"
            >
              Add Product
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={showEditProduct} onOpenChange={setShowEditProduct}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Edit Product
            </DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name" className="text-white mb-2 block">Product Name</Label>
                <Input
                  id="edit-name"
                  value={editingProduct.name}
                  onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label htmlFor="edit-description" className="text-white mb-2 block">Description</Label>
                <Input
                  id="edit-description"
                  value={editingProduct.description}
                  onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-price" className="text-white mb-2 block">Price</Label>
                  <Input
                    id="edit-price"
                    type="number"
                    value={editingProduct.price}
                    onChange={(e) => setEditingProduct({...editingProduct, price: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-stock" className="text-white mb-2 block">Stock</Label>
                  <Input
                    id="edit-stock"
                    type="number"
                    value={editingProduct.stock}
                    onChange={(e) => setEditingProduct({...editingProduct, stock: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-category" className="text-white mb-2 block">Category</Label>
                <Select 
                  value={editingProduct.category} 
                  onValueChange={(value) => setEditingProduct({...editingProduct, category: value})}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.name}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color || '#FF4500' }} />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white mb-2 block">Tags</Label>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => {
                        const newTags = editingProduct.tags?.includes(tag.name)
                          ? editingProduct.tags.filter(t => t !== tag.name)
                          : [...(editingProduct.tags || []), tag.name];
                        setEditingProduct({...editingProduct, tags: newTags});
                      }}
                      className={`px-3 py-1 rounded-full text-sm transition-all ${
                        editingProduct.tags?.includes(tag.name)
                          ? 'ring-2 ring-white'
                          : 'opacity-60 hover:opacity-100'
                      }`}
                      style={{ backgroundColor: tag.color, color: '#000' }}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Add-ons Section in Edit */}
              <div className="bg-white/5 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-white">Add-ons (Finishes)</Label>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      const addons = editingProduct.addons || [];
                      setEditingProduct({...editingProduct, addons: [...addons, {name: '', price: ''}]});
                    }}
                    className="bg-[#FF4500]/20 text-[#FF4500] hover:bg-[#FF4500]/30 text-xs"
                  >
                    + Add
                  </Button>
                </div>
                {(editingProduct.addons || []).map((addon, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      placeholder="Name (e.g., Peri-Peri)"
                      value={addon.name}
                      onChange={(e) => {
                        const addons = [...(editingProduct.addons || [])];
                        addons[idx] = {...addons[idx], name: e.target.value};
                        setEditingProduct({...editingProduct, addons});
                      }}
                      className="bg-white/5 border-white/10 text-white text-sm flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="₹"
                      value={addon.price}
                      onChange={(e) => {
                        const addons = [...(editingProduct.addons || [])];
                        addons[idx] = {...addons[idx], price: parseFloat(e.target.value) || 0};
                        setEditingProduct({...editingProduct, addons});
                      }}
                      className="bg-white/5 border-white/10 text-white text-sm w-20"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const addons = (editingProduct.addons || []).filter((_, i) => i !== idx);
                        setEditingProduct({...editingProduct, addons});
                      }}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20 p-1"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              
              {/* Inherited Add-ons from Category - Toggle Section */}
              {editingProduct.category_addon_status && editingProduct.category_addon_status.length > 0 && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-orange-400">Inherited Add-ons (Finishes)</Label>
                      <p className="text-gray-500 text-xs mt-1">From category: {editingProduct.category}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {editingProduct.category_addon_status.map(addon => (
                      <div 
                        key={addon.id} 
                        className={`flex items-center justify-between p-2 rounded ${
                          addon.is_disabled_for_product 
                            ? 'bg-red-500/10' 
                            : addon.is_active_globally 
                              ? 'bg-green-500/10' 
                              : 'bg-gray-500/10'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${addon.is_disabled_for_product ? 'text-red-400 line-through' : 'text-white'}`}>
                            {addon.name}
                          </span>
                          {addon.price > 0 && (
                            <span className="text-[#FF4500] text-xs">+₹{addon.price}</span>
                          )}
                          {!addon.is_active_globally && (
                            <Badge variant="destructive" className="text-[10px]">Stock Out</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 text-xs">
                            {addon.is_disabled_for_product ? 'Hidden' : 'Visible'}
                          </span>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await api.put(`/products/${editingProduct.id}/toggle-addon/${addon.id}`);
                                // Update local state
                                const updated = editingProduct.category_addon_status.map(a => 
                                  a.id === addon.id ? { ...a, is_disabled_for_product: !a.is_disabled_for_product } : a
                                );
                                setEditingProduct({ ...editingProduct, category_addon_status: updated });
                                toast.success(`Add-on "${addon.name}" ${addon.is_disabled_for_product ? 'enabled' : 'disabled'} for this product`);
                                fetchProducts();
                              } catch (error) {
                                toast.error('Failed to toggle add-on');
                              }
                            }}
                            className={`w-10 h-5 rounded-full transition-colors relative ${
                              addon.is_disabled_for_product ? 'bg-red-500' : 'bg-green-500'
                            }`}
                            disabled={!addon.is_active_globally}
                            title={!addon.is_active_globally ? 'Enable this add-on globally first' : ''}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                              addon.is_disabled_for_product ? 'left-0.5' : 'right-0.5'
                            }`} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-gray-500 text-xs">
                    Toggle OFF to hide an add-on for this product only. Stock out add-ons are globally disabled.
                  </p>
                </div>
              )}
              
              {/* Customization Groups Section in Edit */}
              <div className="bg-white/5 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-white">Customization Options</Label>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      const groups = editingProduct.customization_groups || [];
                      setEditingProduct({...editingProduct, customization_groups: [...groups, {name: '', required: false, options: [{name: '', price: 0}]}]});
                    }}
                    className="bg-[#00E5FF]/20 text-[#00E5FF] hover:bg-[#00E5FF]/30 text-xs"
                  >
                    + Add Group
                  </Button>
                </div>
                {(editingProduct.customization_groups || []).map((group, gIdx) => (
                  <div key={gIdx} className="border border-white/10 rounded-lg p-3 space-y-2">
                    <div className="flex gap-2 items-center">
                      <Input
                        placeholder="Group name (e.g., Choose Masala)"
                        value={group.name}
                        onChange={(e) => {
                          const groups = [...(editingProduct.customization_groups || [])];
                          groups[gIdx] = {...groups[gIdx], name: e.target.value};
                          setEditingProduct({...editingProduct, customization_groups: groups});
                        }}
                        className="bg-white/5 border-white/10 text-white text-sm flex-1"
                      />
                      <label className="flex items-center gap-1 text-xs text-gray-400">
                        <input
                          type="checkbox"
                          checked={group.required}
                          onChange={(e) => {
                            const groups = [...(editingProduct.customization_groups || [])];
                            groups[gIdx] = {...groups[gIdx], required: e.target.checked};
                            setEditingProduct({...editingProduct, customization_groups: groups});
                          }}
                          className="w-3 h-3"
                        />
                        Required
                      </label>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const groups = (editingProduct.customization_groups || []).filter((_, i) => i !== gIdx);
                          setEditingProduct({...editingProduct, customization_groups: groups});
                        }}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20 p-1"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="pl-3 space-y-1">
                      {(group.options || []).map((opt, oIdx) => (
                        <div key={oIdx} className="flex gap-2 items-center">
                          <span className="text-gray-500 text-xs">↳</span>
                          <Input
                            placeholder="Option (e.g., Red Masala)"
                            value={opt.name}
                            onChange={(e) => {
                              const groups = [...(editingProduct.customization_groups || [])];
                              groups[gIdx].options[oIdx] = {...groups[gIdx].options[oIdx], name: e.target.value};
                              setEditingProduct({...editingProduct, customization_groups: groups});
                            }}
                            className="bg-white/5 border-white/10 text-white text-xs flex-1"
                          />
                          <Input
                            type="number"
                            placeholder="₹0"
                            value={opt.price || 0}
                            onChange={(e) => {
                              const groups = [...(editingProduct.customization_groups || [])];
                              groups[gIdx].options[oIdx] = {...groups[gIdx].options[oIdx], price: parseFloat(e.target.value) || 0};
                              setEditingProduct({...editingProduct, customization_groups: groups});
                            }}
                            className="bg-white/5 border-white/10 text-white text-xs w-16"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const groups = [...(editingProduct.customization_groups || [])];
                              groups[gIdx].options = groups[gIdx].options.filter((_, i) => i !== oIdx);
                              setEditingProduct({...editingProduct, customization_groups: groups});
                            }}
                            className="text-red-400 hover:text-red-300 p-0"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const groups = [...(editingProduct.customization_groups || [])];
                          groups[gIdx].options = [...(groups[gIdx].options || []), {name: '', price: 0}];
                          setEditingProduct({...editingProduct, customization_groups: groups});
                        }}
                        className="text-[#00E5FF] hover:text-[#00E5FF]/80 text-xs pl-5"
                      >
                        + Add Option
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Inherited Customizations from Category - Toggle Section */}
              {editingProduct?.category_customization_status?.length > 0 && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-cyan-400">Inherited Customizations</Label>
                      <p className="text-gray-500 text-xs mt-1">From category: {editingProduct.category}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {editingProduct.category_customization_status.map(group => (
                      <div 
                        key={group.id} 
                        className={`p-3 rounded-lg border ${
                          group.is_disabled_for_product 
                            ? 'bg-red-500/10 border-red-500/30' 
                            : group.is_active_globally 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-gray-500/10 border-gray-500/30'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${group.is_disabled_for_product ? 'text-red-400 line-through' : 'text-white'}`}>
                              {group.name}
                            </span>
                            {group.required && (
                              <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">Required</Badge>
                            )}
                            {!group.is_active_globally && (
                              <Badge variant="destructive" className="text-[10px]">Global Off</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs">
                              {group.is_disabled_for_product ? 'Hidden' : 'Visible'}
                            </span>
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await api.put(`/products/${editingProduct.id}/toggle-customization/${group.id}`);
                                  // Update local state
                                  const updated = editingProduct.category_customization_status.map(g => 
                                    g.id === group.id ? { ...g, is_disabled_for_product: !g.is_disabled_for_product } : g
                                  );
                                  setEditingProduct({ ...editingProduct, category_customization_status: updated });
                                  toast.success(`Customization "${group.name}" ${group.is_disabled_for_product ? 'enabled' : 'disabled'} for this product`);
                                  fetchProducts();
                                } catch (error) {
                                  toast.error('Failed to toggle customization');
                                }
                              }}
                              className={`w-10 h-5 rounded-full transition-colors relative ${
                                group.is_disabled_for_product ? 'bg-red-500' : 'bg-green-500'
                              }`}
                              disabled={!group.is_active_globally}
                              title={!group.is_active_globally ? 'Enable this group globally first' : ''}
                            >
                              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                                group.is_disabled_for_product ? 'left-0.5' : 'right-0.5'
                              }`} />
                            </button>
                          </div>
                        </div>
                        {/* Show options */}
                        {group.options && group.options.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {group.options.map((opt, idx) => (
                              <span 
                                key={idx} 
                                className={`text-[10px] px-2 py-0.5 rounded ${
                                  opt.is_active 
                                    ? 'bg-white/10 text-gray-300' 
                                    : 'bg-red-500/10 text-red-400 line-through'
                                }`}
                              >
                                {opt.name} {opt.price > 0 && `+₹${opt.price}`}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-gray-500 text-xs">
                    Toggle OFF to hide a customization group for this product only. 
                    Options grayed out are globally disabled (stock out).
                  </p>
                </div>
              )}
              
              {/* Tax Settings in Edit */}
              <div className="bg-white/5 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-white">GST (Govt. Tax)</Label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingProduct({...editingProduct, tax_enabled: !editingProduct.tax_enabled})}
                      className={`relative w-12 h-6 rounded-full transition-colors ${editingProduct.tax_enabled ? 'bg-[#FF4500]' : 'bg-gray-600'}`}
                    >
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${editingProduct.tax_enabled ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>
                </div>
                {editingProduct.tax_enabled && (
                  <div>
                    <Label className="text-gray-400 text-sm mb-1 block">Tax Percentage (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editingProduct.tax_percentage || 5}
                      onChange={(e) => setEditingProduct({...editingProduct, tax_percentage: e.target.value})}
                      className="bg-white/5 border-white/10 text-white w-24"
                      placeholder="5"
                    />
                  </div>
                )}
                
                <div>
                  <Label className="text-gray-400 text-sm mb-1 block">Packaging Fee (₹)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editingProduct.packaging_fee || 0}
                    onChange={(e) => setEditingProduct({...editingProduct, packaging_fee: e.target.value})}
                    className="bg-white/5 border-white/10 text-white w-24"
                    placeholder="0"
                  />
                  <p className="text-gray-500 text-xs mt-1">Charged per item in cart</p>
                </div>
              </div>
              
              <div>
                <Label className="text-white mb-2 block">Product Image</Label>
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={(e) => handleFileSelect(e, true)}
                  className="hidden"
                  data-testid="edit-product-image-input"
                />
                
                {/* Image Preview or Upload/URL Options */}
                {editingProduct.image || editImagePreview ? (
                  <div className="space-y-3">
                    <div className="relative rounded-lg overflow-hidden bg-black/20">
                      <img 
                        src={getImageUrl(editingProduct.image || editImagePreview)} 
                        alt="Product preview" 
                        className="w-full h-40 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-full h-40 items-center justify-center bg-red-900/20 text-red-400 text-sm">
                        Image failed to load - check URL
                      </div>
                      <div className="absolute top-2 right-2 flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => editFileInputRef.current?.click()}
                          className="bg-white/90 hover:bg-white text-black"
                          disabled={uploadingImage}
                        >
                          <Upload className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => clearImage(true)}
                          className="bg-red-500/90 hover:bg-red-500 text-white"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {/* URL Input - always visible for easy URL changes */}
                    <Input
                      placeholder="Or paste new image URL (https://...)"
                      value={editingProduct.image?.startsWith('/api/') ? '' : (editingProduct.image || '')}
                      onChange={(e) => setEditingProduct({...editingProduct, image: e.target.value})}
                      className="bg-white/5 border-white/10 text-white text-sm"
                      data-testid="edit-product-image-url-inline"
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Upload Button */}
                    <div
                      onClick={() => !uploadingImage && editFileInputRef.current?.click()}
                      className={`border-2 border-dashed border-white/20 rounded-lg p-4 text-center cursor-pointer hover:border-[#00C853]/50 hover:bg-white/5 transition-all ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {uploadingImage ? (
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-6 h-6 border-2 border-[#00C853] border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-gray-400 text-sm">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <Upload className="w-5 h-5 text-gray-500" />
                          <span className="text-gray-400 text-sm">Upload image (max 5MB)</span>
                        </div>
                      )}
                    </div>
                    
                    {/* OR Divider */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-white/10"></div>
                      <span className="text-gray-500 text-xs">OR</span>
                      <div className="flex-1 h-px bg-white/10"></div>
                    </div>
                    
                    {/* URL Input */}
                    <Input
                      placeholder="Paste image URL (https://...)"
                      value={editingProduct.image || ''}
                      onChange={(e) => setEditingProduct({...editingProduct, image: e.target.value})}
                      className="bg-white/5 border-white/10 text-white text-sm"
                      data-testid="edit-product-image-url"
                    />
                  </div>
                )}
              </div>
              <Button
                onClick={handleEditProduct}
                className="w-full bg-[#00C853] hover:bg-[#00E676] text-white py-6 text-lg font-bold"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Outlet Dialog */}
      <Dialog open={showAddOutlet} onOpenChange={setShowAddOutlet}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Add New Outlet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="outlet-name" className="text-white mb-2 block">Outlet Name *</Label>
              <Input
                id="outlet-name"
                value={newOutlet.name}
                onChange={(e) => setNewOutlet({...newOutlet, name: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Fish on Fire - Downtown"
              />
            </div>
            <div>
              <Label htmlFor="outlet-address" className="text-white mb-2 block">Address *</Label>
              <Input
                id="outlet-address"
                value={newOutlet.address}
                onChange={(e) => setNewOutlet({...newOutlet, address: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="123 Main Street, City"
              />
            </div>
            <div>
              <Label htmlFor="outlet-phone" className="text-white mb-2 block">Phone *</Label>
              <Input
                id="outlet-phone"
                value={newOutlet.phone}
                onChange={(e) => setNewOutlet({...newOutlet, phone: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="+91 9876543210"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="outlet-lat" className="text-white mb-2 block">Latitude</Label>
                <Input
                  id="outlet-lat"
                  type="number"
                  step="0.000001"
                  value={newOutlet.latitude}
                  onChange={(e) => setNewOutlet({...newOutlet, latitude: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="12.9716"
                />
              </div>
              <div>
                <Label htmlFor="outlet-lng" className="text-white mb-2 block">Longitude</Label>
                <Input
                  id="outlet-lng"
                  type="number"
                  step="0.000001"
                  value={newOutlet.longitude}
                  onChange={(e) => setNewOutlet({...newOutlet, longitude: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="77.5946"
                />
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF]/10"
              onClick={() => {
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      setNewOutlet({
                        ...newOutlet,
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                      });
                      toast.success('Location captured!');
                    },
                    (error) => {
                      toast.error('Could not get location. Please enter manually.');
                    }
                  );
                } else {
                  toast.error('Geolocation not supported');
                }
              }}
            >
              <MapPin className="w-4 h-4 mr-2" />
              Get Current Location (GPS)
            </Button>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-white mb-2 block text-sm">Delivery (km)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={newOutlet.delivery_radius_km}
                  onChange={(e) => setNewOutlet({...newOutlet, delivery_radius_km: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="5"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block text-sm">Pickup (km)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={newOutlet.pickup_radius_km}
                  onChange={(e) => setNewOutlet({...newOutlet, pickup_radius_km: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="15"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block text-sm">Dine-in (km)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newOutlet.dine_in_radius_km}
                  onChange={(e) => setNewOutlet({...newOutlet, dine_in_radius_km: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="0.5"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Delivery: Max distance for home delivery • Pickup: Max distance for order & collect • Dine-in: Must be at outlet
            </p>
            {/* Ordering Hours */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Opening Time</Label>
                <Input
                  type="time"
                  value={newOutlet.ordering_start_time}
                  onChange={(e) => setNewOutlet({...newOutlet, ordering_start_time: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Closing Time</Label>
                <Input
                  type="time"
                  value={newOutlet.ordering_end_time}
                  onChange={(e) => setNewOutlet({...newOutlet, ordering_end_time: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
            <Button
              onClick={handleAddOutlet}
              className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white py-6 text-lg font-bold"
            >
              Add Outlet
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Outlet Dialog */}
      <Dialog open={showEditOutlet} onOpenChange={setShowEditOutlet}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Edit Outlet
            </DialogTitle>
          </DialogHeader>
          {editingOutlet && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-outlet-name" className="text-white mb-2 block">Outlet Name</Label>
                <Input
                  id="edit-outlet-name"
                  value={editingOutlet.name}
                  onChange={(e) => setEditingOutlet({...editingOutlet, name: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label htmlFor="edit-outlet-address" className="text-white mb-2 block">Address</Label>
                <Input
                  id="edit-outlet-address"
                  value={editingOutlet.address}
                  onChange={(e) => setEditingOutlet({...editingOutlet, address: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label htmlFor="edit-outlet-phone" className="text-white mb-2 block">Phone</Label>
                <Input
                  id="edit-outlet-phone"
                  value={editingOutlet.phone}
                  onChange={(e) => setEditingOutlet({...editingOutlet, phone: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-outlet-lat" className="text-white mb-2 block">Latitude</Label>
                  <Input
                    id="edit-outlet-lat"
                    type="number"
                    step="0.000001"
                    value={editingOutlet.latitude || ''}
                    onChange={(e) => setEditingOutlet({...editingOutlet, latitude: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="12.9716"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-outlet-lng" className="text-white mb-2 block">Longitude</Label>
                  <Input
                    id="edit-outlet-lng"
                    type="number"
                    step="0.000001"
                    value={editingOutlet.longitude || ''}
                    onChange={(e) => setEditingOutlet({...editingOutlet, longitude: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="77.5946"
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                onClick={() => {
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (position) => {
                        setEditingOutlet({
                          ...editingOutlet,
                          latitude: position.coords.latitude,
                          longitude: position.coords.longitude
                        });
                        toast.success('Location captured!');
                      },
                      (error) => {
                        toast.error('Could not get location. Please enter manually.');
                      }
                    );
                  } else {
                    toast.error('Geolocation not supported');
                  }
                }}
              >
                <MapPin className="w-4 h-4 mr-2" />
                Get Current Location (GPS)
              </Button>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-white mb-2 block text-sm">Delivery (km)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={editingOutlet.delivery_radius_km}
                    onChange={(e) => setEditingOutlet({...editingOutlet, delivery_radius_km: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block text-sm">Pickup (km)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={editingOutlet.pickup_radius_km}
                    onChange={(e) => setEditingOutlet({...editingOutlet, pickup_radius_km: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block text-sm">Dine-in (km)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editingOutlet.dine_in_radius_km}
                    onChange={(e) => setEditingOutlet({...editingOutlet, dine_in_radius_km: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Delivery: Home delivery radius • Pickup: Order & collect radius • Dine-in: Must be at outlet
              </p>
              {/* Ordering Hours */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Opening Time</Label>
                  <Input
                    type="time"
                    value={editingOutlet.ordering_start_time || '09:00'}
                    onChange={(e) => setEditingOutlet({...editingOutlet, ordering_start_time: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block">Closing Time</Label>
                  <Input
                    type="time"
                    value={editingOutlet.ordering_end_time || '22:00'}
                    onChange={(e) => setEditingOutlet({...editingOutlet, ordering_end_time: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
              {/* Invoice Settings */}
              <div className="bg-white/5 rounded-lg p-3">
                <Label className="text-white mb-2 block text-sm font-semibold">📄 Invoice Numbering</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-400 mb-1 block text-xs">Branch Code (3 letters)</Label>
                    <Input
                      value={editingOutlet.branch_code || ''}
                      onChange={(e) => setEditingOutlet({...editingOutlet, branch_code: e.target.value.toUpperCase().slice(0, 3)})}
                      className="bg-white/10 border-white/10 text-white font-mono"
                      placeholder="QK1"
                      maxLength={3}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 mb-1 block text-xs">Start Number</Label>
                    <Input
                      type="number"
                      value={editingOutlet.invoice_start_number || 1}
                      onChange={(e) => setEditingOutlet({...editingOutlet, invoice_start_number: e.target.value})}
                      className="bg-white/10 border-white/10 text-white"
                      min={1}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Invoice format: <span className="font-mono text-[#00E5FF]">{editingOutlet.branch_code || 'XXX'}101001</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-outlet-active"
                  checked={editingOutlet.active}
                  onChange={(e) => setEditingOutlet({...editingOutlet, active: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="edit-outlet-active" className="text-white">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-outlet-accepting"
                  checked={editingOutlet.is_accepting_orders !== false}
                  onChange={(e) => setEditingOutlet({...editingOutlet, is_accepting_orders: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="edit-outlet-accepting" className="text-white">Accepting Orders</Label>
              </div>
              <Button
                onClick={handleEditOutlet}
                className="w-full bg-[#00C853] hover:bg-[#00E676] text-white py-6 text-lg font-bold"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              QR Code for Orders
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-400">
              Customers can scan this QR code to order from{' '}
              {qrData?.outlet_id === 'main' ? 'Main Outlet' : 
               outlets.find(o => o.id === qrData?.outlet_id)?.name || 'this outlet'}
            </p>
            {qrData && (
              <div className="bg-white p-8 rounded-lg">
                <QRCode value={qrData.url} size={256} className="w-full h-auto" />
              </div>
            )}
            <Button
              onClick={() => {
                toast.success('QR Code ready to print');
              }}
              className="w-full bg-[#00E5FF] hover:bg-[#00B8D4] text-black"
            >
              Print QR Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delivery Fee Ranges Dialog */}
      <Dialog open={showDeliveryFees} onOpenChange={setShowDeliveryFees}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Delivery Fee Ranges
            </DialogTitle>
            {selectedOutletForFees && (
              <p className="text-gray-400 text-sm">
                {selectedOutletForFees.name}
              </p>
            )}
          </DialogHeader>
          
          {/* Outlet Location Info */}
          {selectedOutletForFees && (
            <div className="bg-white/5 rounded-lg p-4 mb-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Shop Location (Pickup Point)</p>
                  {selectedOutletForFees.latitude && selectedOutletForFees.longitude ? (
                    <p className="text-white font-mono text-sm">
                      {selectedOutletForFees.latitude.toFixed(6)}, {selectedOutletForFees.longitude.toFixed(6)}
                    </p>
                  ) : (
                    <p className="text-yellow-400 text-sm">
                      ⚠️ GPS location not set. Edit outlet to add coordinates.
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowDeliveryFees(false);
                    openEditOutlet(selectedOutletForFees);
                  }}
                  className="border-white/20 text-white hover:bg-white/5"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit Location
                </Button>
              </div>
            </div>
          )}
          
          {/* Add New Fee Range */}
          <div className="bg-white/5 rounded-lg p-4 mb-4">
            <h4 className="text-white font-semibold mb-3">Add Distance Range</h4>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">From (km)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={newFeeRange.min_distance_km}
                  onChange={(e) => setNewFeeRange({...newFeeRange, min_distance_km: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">To (km)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={newFeeRange.max_distance_km}
                  onChange={(e) => setNewFeeRange({...newFeeRange, max_distance_km: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="3"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Fee (₹)</Label>
                <Input
                  type="number"
                  value={newFeeRange.fee}
                  onChange={(e) => setNewFeeRange({...newFeeRange, fee: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="30"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleAddFeeRange}
                  className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Existing Fee Ranges */}
          <div className="space-y-2">
            <h4 className="text-white font-semibold">Current Fee Ranges</h4>
            {deliveryFeeRanges.length === 0 ? (
              <div className="text-center py-8 bg-white/5 rounded-lg">
                <DollarSign className="w-8 h-8 mx-auto text-gray-600 mb-2" />
                <p className="text-gray-400 text-sm">No fee ranges configured</p>
                <p className="text-gray-500 text-xs">Add distance ranges above to enable location-based delivery fees</p>
              </div>
            ) : (
              deliveryFeeRanges.map((range) => (
                <div key={range.id} className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                  {editingFeeRange?.id === range.id ? (
                    <div className="flex-1 grid grid-cols-4 gap-2 items-center">
                      <Input
                        type="number"
                        step="0.5"
                        value={editingFeeRange.min_distance_km}
                        onChange={(e) => setEditingFeeRange({...editingFeeRange, min_distance_km: e.target.value})}
                        className="bg-white/10 border-white/20 text-white h-8"
                      />
                      <Input
                        type="number"
                        step="0.5"
                        value={editingFeeRange.max_distance_km}
                        onChange={(e) => setEditingFeeRange({...editingFeeRange, max_distance_km: e.target.value})}
                        className="bg-white/10 border-white/20 text-white h-8"
                      />
                      <Input
                        type="number"
                        value={editingFeeRange.fee}
                        onChange={(e) => setEditingFeeRange({...editingFeeRange, fee: e.target.value})}
                        className="bg-white/10 border-white/20 text-white h-8"
                      />
                      <div className="flex gap-1">
                        <Button size="sm" onClick={handleUpdateFeeRange} className="bg-green-500 hover:bg-green-600 h-8">
                          <Save className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingFeeRange(null)} className="border-white/20 h-8">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-4">
                        <div className="bg-[#FF4500]/20 px-3 py-1 rounded-full">
                          <span className="text-[#FF4500] font-bold">{range.min_distance_km} - {range.max_distance_km} km</span>
                        </div>
                        <span className="text-white font-bold text-lg">{formatCurrency(range.fee)}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingFeeRange({...range})}
                          className="border-white/20 text-white hover:bg-white/5 h-8"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteFeeRange(range.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
          
          {/* Help Text */}
          <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-blue-400 text-sm">
              <strong>How it works:</strong> When a customer places a delivery order, the distance from shop to delivery address is calculated. The delivery fee is then determined based on matching distance range.
            </p>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Staff Switcher - Quick switch between POS/Kitchen/Admin */}
      <StaffSwitcher />
      
      {/* Ticket Response Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Respond to Ticket</DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-sm text-gray-400">Subject</p>
                <p className="text-white font-medium">{selectedTicket.subject}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-sm text-gray-400">Description</p>
                <p className="text-gray-300">{selectedTicket.description}</p>
              </div>
              <div>
                <Label className="text-gray-400 text-sm mb-1 block">Your Response</Label>
                <Textarea
                  value={ticketResponse}
                  onChange={(e) => setTicketResponse(e.target.value)}
                  className="bg-white/5 border-white/10 text-white min-h-[100px]"
                  placeholder="Type your response..."
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={() => handleUpdateTicket(selectedTicket.id, 'in_progress', ticketResponse)}
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  disabled={!ticketResponse}
                >
                  Send Response
                </Button>
                <Button 
                  onClick={() => handleUpdateTicket(selectedTicket.id, 'resolved', ticketResponse || 'Issue resolved')}
                  className="flex-1 bg-green-500 hover:bg-green-600"
                >
                  Resolve & Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Loyalty Points Dialog */}
      <Dialog open={showAdjustPoints} onOpenChange={setShowAdjustPoints}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-[#FFD600]" />
              Adjust Loyalty Points
            </DialogTitle>
          </DialogHeader>
          {selectedCustomerForPoints && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#FFD600]/20 flex items-center justify-center">
                    <Star className="w-6 h-6 text-[#FFD600]" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">{selectedCustomerForPoints.name || 'Customer'}</h3>
                    <p className="text-gray-400">{selectedCustomerForPoints.phone}</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-gray-400">Current Balance:</span>
                  <span className="text-2xl font-bold text-[#FFD600]">{selectedCustomerForPoints.loyalty_points || 0} pts</span>
                </div>
              </div>

              {/* Action Type */}
              <div>
                <Label className="text-gray-400 text-sm mb-2 block">Action</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={pointsAdjustment.type === 'add' ? 'default' : 'outline'}
                    className={pointsAdjustment.type === 'add' 
                      ? 'bg-green-500 hover:bg-green-600 text-white' 
                      : 'border-white/20 text-white hover:bg-white/5'}
                    onClick={() => setPointsAdjustment(prev => ({ ...prev, type: 'add' }))}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Points
                  </Button>
                  <Button
                    type="button"
                    variant={pointsAdjustment.type === 'deduct' ? 'default' : 'outline'}
                    className={pointsAdjustment.type === 'deduct' 
                      ? 'bg-red-500 hover:bg-red-600 text-white' 
                      : 'border-white/20 text-white hover:bg-white/5'}
                    onClick={() => setPointsAdjustment(prev => ({ ...prev, type: 'deduct' }))}
                  >
                    <Minus className="w-4 h-4 mr-2" />
                    Deduct Points
                  </Button>
                </div>
              </div>

              {/* Points Amount */}
              <div>
                <Label className="text-gray-400 text-sm mb-1 block">Points Amount</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="Enter points amount"
                  value={pointsAdjustment.amount}
                  onChange={(e) => setPointsAdjustment(prev => ({ ...prev, amount: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Reason */}
              <div>
                <Label className="text-gray-400 text-sm mb-1 block">Reason (Optional)</Label>
                <Input
                  placeholder={pointsAdjustment.type === 'add' ? 'e.g., Bonus, Compensation, Promotion' : 'e.g., Correction, Refund adjustment'}
                  value={pointsAdjustment.reason}
                  onChange={(e) => setPointsAdjustment(prev => ({ ...prev, reason: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Preview */}
              {pointsAdjustment.amount && (
                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                  <p className="text-sm text-gray-400">New Balance After Adjustment:</p>
                  <p className="text-xl font-bold text-white">
                    {pointsAdjustment.type === 'add' 
                      ? (selectedCustomerForPoints.loyalty_points || 0) + parseInt(pointsAdjustment.amount || 0)
                      : Math.max(0, (selectedCustomerForPoints.loyalty_points || 0) - parseInt(pointsAdjustment.amount || 0))
                    } pts
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/5"
                  onClick={() => setShowAdjustPoints(false)}
                >
                  Cancel
                </Button>
                <Button
                  className={`flex-1 ${pointsAdjustment.type === 'add' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
                  onClick={handleAdjustPoints}
                  disabled={!pointsAdjustment.amount || parseInt(pointsAdjustment.amount) <= 0}
                >
                  {pointsAdjustment.type === 'add' ? 'Add' : 'Deduct'} {pointsAdjustment.amount || 0} Points
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Offer Create/Edit Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
            {editingOffer ? 'Edit Offer' : 'Create New Offer'}
          </DialogTitle>
          
          <div className="space-y-4 mt-4">
            {/* Basic Info */}
            <div className="space-y-3">
              <div>
                <Label className="text-white">Offer Name *</Label>
                <Input
                  value={offerForm.name}
                  onChange={(e) => setOfferForm({ ...offerForm, name: e.target.value })}
                  placeholder="e.g., Buy Any, Get Prawns at ₹9"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white">Description</Label>
                <Textarea
                  value={offerForm.description}
                  onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })}
                  placeholder="Optional description for customers"
                  className="bg-white/5 border-white/10 text-white mt-1"
                  rows={2}
                />
              </div>
            </div>

            {/* Offer Type */}
            <div>
              <Label className="text-white">Offer Type</Label>
              <Select
                value={offerForm.offer_type}
                onValueChange={(value) => setOfferForm({ ...offerForm, offer_type: value })}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="combo_deal" className="text-white">Combo Deal (Get product at special price)</SelectItem>
                  <SelectItem value="percentage" className="text-white">Percentage Discount</SelectItem>
                  <SelectItem value="flat_discount" className="text-white">Flat Discount (₹ off)</SelectItem>
                  <SelectItem value="bogo" className="text-white">Buy 1 Get 1 Free</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Condition */}
            <div className="bg-white/5 rounded-lg p-4 space-y-3">
              <Label className="text-orange-400 font-medium">Offer Condition (When does this apply?)</Label>
              <Select
                value={offerForm.condition.condition_type}
                onValueChange={(value) => setOfferForm({
                  ...offerForm,
                  condition: { ...offerForm.condition, condition_type: value }
                })}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="any_item" className="text-white">Any item in cart</SelectItem>
                  <SelectItem value="min_cart_value" className="text-white">Minimum cart value</SelectItem>
                  <SelectItem value="specific_product" className="text-white">Specific product(s) in cart</SelectItem>
                  <SelectItem value="specific_category" className="text-white">Specific category in cart</SelectItem>
                </SelectContent>
              </Select>

              {offerForm.condition.condition_type === 'min_cart_value' && (
                <div>
                  <Label className="text-gray-400 text-sm">Minimum Cart Value (₹)</Label>
                  <Input
                    type="number"
                    value={offerForm.condition.min_cart_value}
                    onChange={(e) => setOfferForm({
                      ...offerForm,
                      condition: { ...offerForm.condition, min_cart_value: e.target.value }
                    })}
                    placeholder="e.g., 200"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              )}

              {offerForm.condition.condition_type === 'specific_product' && (
                <div>
                  <Label className="text-gray-400 text-sm">Required Product</Label>
                  <Select
                    value={offerForm.condition.required_product_ids[0] || ''}
                    onValueChange={(value) => setOfferForm({
                      ...offerForm,
                      condition: { ...offerForm.condition, required_product_ids: [value] }
                    })}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-60">
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id} className="text-white">{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {offerForm.condition.condition_type === 'specific_category' && (
                <div>
                  <Label className="text-gray-400 text-sm">Required Category</Label>
                  <Select
                    value={offerForm.condition.required_category}
                    onValueChange={(value) => setOfferForm({
                      ...offerForm,
                      condition: { ...offerForm.condition, required_category: value }
                    })}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      {categories.map(c => (
                        <SelectItem key={c.id || c.name} value={c.name} className="text-white">{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Reward */}
            <div className="bg-white/5 rounded-lg p-4 space-y-3">
              <Label className="text-green-400 font-medium">Reward (What do they get?)</Label>
              
              {(offerForm.offer_type === 'combo_deal' || offerForm.offer_type === 'bogo') && (
                <>
                  <div>
                    <Label className="text-gray-400 text-sm">Reward Product</Label>
                    <Select
                      value={offerForm.reward.product_id}
                      onValueChange={(value) => {
                        const prod = products.find(p => p.id === value);
                        setOfferForm({
                          ...offerForm,
                          reward: { 
                            ...offerForm.reward, 
                            product_id: value,
                            product_name: prod?.name || ''
                          }
                        });
                      }}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-60">
                        {products.map(p => (
                          <SelectItem key={p.id} value={p.id} className="text-white">
                            {p.name} - ₹{p.price}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {offerForm.offer_type === 'combo_deal' && (
                    <div>
                      <Label className="text-gray-400 text-sm">Special Price (₹)</Label>
                      <Input
                        type="number"
                        value={offerForm.reward.special_price}
                        onChange={(e) => setOfferForm({
                          ...offerForm,
                          reward: { ...offerForm.reward, special_price: e.target.value }
                        })}
                        placeholder="e.g., 9"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                  )}
                </>
              )}

              {offerForm.offer_type === 'percentage' && (
                <>
                  <div>
                    <Label className="text-gray-400 text-sm">Discount Percentage (%)</Label>
                    <Input
                      type="number"
                      value={offerForm.reward.discount_percentage}
                      onChange={(e) => setOfferForm({
                        ...offerForm,
                        reward: { ...offerForm.reward, discount_percentage: e.target.value }
                      })}
                      placeholder="e.g., 20"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Max Discount (₹) - Optional</Label>
                    <Input
                      type="number"
                      value={offerForm.reward.max_discount}
                      onChange={(e) => setOfferForm({
                        ...offerForm,
                        reward: { ...offerForm.reward, max_discount: e.target.value }
                      })}
                      placeholder="e.g., 100"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                </>
              )}

              {offerForm.offer_type === 'flat_discount' && (
                <div>
                  <Label className="text-gray-400 text-sm">Discount Amount (₹)</Label>
                  <Input
                    type="number"
                    value={offerForm.reward.discount_amount}
                    onChange={(e) => setOfferForm({
                      ...offerForm,
                      reward: { ...offerForm.reward, discount_amount: e.target.value }
                    })}
                    placeholder="e.g., 50"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              )}
            </div>

            {/* Validity */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Start Date (Optional)</Label>
                <Input
                  type="date"
                  value={offerForm.start_date}
                  onChange={(e) => setOfferForm({ ...offerForm, start_date: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white">End Date (Optional)</Label>
                <Input
                  type="date"
                  value={offerForm.end_date}
                  onChange={(e) => setOfferForm({ ...offerForm, end_date: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            </div>

            {/* Usage Limit & Active */}
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <Label className="text-white">Usage Limit (Optional)</Label>
                <Input
                  type="number"
                  value={offerForm.usage_limit}
                  onChange={(e) => setOfferForm({ ...offerForm, usage_limit: e.target.value })}
                  placeholder="Leave empty for unlimited"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Switch
                  checked={offerForm.is_active}
                  onCheckedChange={(checked) => setOfferForm({ ...offerForm, is_active: checked })}
                />
                <Label className="text-white">Active</Label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-white/10">
              <Button
                variant="outline"
                className="flex-1 border-white/20 text-white"
                onClick={() => { setShowOfferDialog(false); resetOfferForm(); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-[#FF4500] hover:bg-[#FF4500]/80"
                onClick={saveOffer}
              >
                {editingOffer ? 'Update Offer' : 'Create Offer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
